import * as THREE from 'three'
import { OrbitControls } from 'three/examples/jsm/controls/OrbitControls.js'
import { Sky } from'three/addons/objects/Sky.js'
import { Timer } from 'three/addons/misc/Timer.js'
import { GLTFLoader } from 'three/addons/loaders/GLTFLoader.js'
import { DRACOLoader } from 'three/addons/loaders/DRACOLoader.js'
import { KTX2Loader } from 'three/addons/loaders/KTX2Loader.js'
import { clone as cloneSkeleton } from 'three/addons/utils/SkeletonUtils.js'
import GUI from 'lil-gui'
import { getProject, types } from '@theatre/core'
// Keyed by marker number (1-12, matching MARKER_NAMES's own 1-based
// numbering) - only markers with real authored content need an entry
// here; anything missing/absent just falls back to the plain
// placeholder (see openMarkerOverlay). A static import, not fetch() -
// Vite bundles this straight into the JS at build time, so there's no
// extra network request for it.
import markerContent from './markers.json'



/**
 * Base
 */
// Debug
const gui = new GUI()

// Hidden by request - every gui.addFolder/.add() call throughout this file
// still registers its control normally (nothing else had to change), the
// panel itself just doesn't render. Flip to true (or call gui.show()
// instead of hiding it here) to bring it back.
const SHOW_DEBUG_GUI = false
if (!SHOW_DEBUG_GUI)
    gui.hide()

// Paused by request, pending more work on the walking-figure model/
// animation - false skips loading Person.glb entirely (not just hiding
// the figures once created) and skips creating the students/kids-playing
// groups at all, so there's no leftover network/CPU cost while this is off.
const SHOW_PEOPLE = false

// Created first (before every other folder below) purely so it lands at
// the top of the panel - lil-gui folders keep whatever order they were
// added in, and this one's actual controls aren't added until much
// later (once the house model and floor have both loaded - see
// "Marker Position GUI" and the per-marker X/Z sliders further down).
const markerPositionFolder = gui.addFolder('Marker Position')

// Per-marker overrides for the zoomed-in camera's end-of-zoom position,
// look-at target, and field of view (see flyCameraToMarker, near the
// Camera section further down) - position/target X/Y/Z are in real
// scene/world coordinates, the same space as the "Camera" folder's own
// position X/Y/Z, so a value read off that folder while manually
// orbiting to a good close-up shot can be typed straight into these. A
// "preview in scene" checkbox per marker (added alongside these, further
// down) snaps the real camera to all of it live, so there's no need to
// actually click a marker just to see what its values look like. Left
// open (not .close()'d like every other folder) since it's meant to be
// actively used, not just tucked away.
const markerCameraFolder = gui.addFolder('Marker Camera')

// Canvas
const canvas = document.querySelector('canvas.webgl')

// Scene
const scene = new THREE.Scene()

/**
 * Loading
 * A single LoadingManager shared by every loader below (textures and both
 * GLBs) so its onProgress fires with a combined itemsLoaded/itemsTotal count
 * across all of them, instead of each loader tracking its own.
 */
const loadingScreen = document.querySelector('#loading-screen')
const loadingPercentageText = document.querySelector('#loading-percentage')

/**
 * Site header - logo + hamburger menu (right side)
 * Static markup/CSS handle the logo and the full-screen menu's look; this
 * wires up the toggle button - open/close the menu and its backdrop
 * together, swap the button's own "Menu"/"Close" label text (its icon and
 * color swap purely via CSS, keyed off the same menu-toggle--open class -
 * see style.css), and let the backdrop (or a link) close it too, rather
 * than only the button itself. No separate close button - the toggle
 * (raised above the full-screen menu in z-index, in style.css) stays
 * visible/usable the whole time and doubles as the close control.
 */
const siteHeader = document.querySelector('#site-header')
const hero = document.querySelector('#hero')
const heroTitle = document.querySelector('.hero-title')
// A separate element (always position:fixed near the logo, see
// .hero-marker-title in style.css) rather than swapping heroTitle's own
// text/position in place - crossfading two independent elements (this
// one sliding/fading in, heroTitle fading out via hero--marker-active)
// avoids the jump-cut jerk of changing one element's text AND position
// at the same instant.
const heroMarkerBlock = document.querySelector('#hero-marker-block')
const heroMarkerTitle = document.querySelector('#hero-marker-title')
const heroMarkerSubtitle = document.querySelector('#hero-marker-subtitle')
const menuToggle = document.querySelector('#menu-toggle')
const menuToggleLabel = document.querySelector('.menu-toggle-label')
const siteMenu = document.querySelector('#site-menu')
const menuScrollArea = document.querySelector('.menu-scroll-area')
const menuBackdrop = document.querySelector('#menu-backdrop')

const setMenuOpen = (open) =>
{
    // Drives the logo recoloring to white while the menu's dark overlay
    // is behind it (see .site-logo in style.css) - the logo is inlined
    // SVG with its text paths set to fill="currentColor" specifically so
    // this one class toggle can recolor it, no separate white-text SVG
    // asset needed.
    siteHeader.classList.toggle('site-header--menu-open', open)
    menuToggle.classList.toggle('menu-toggle--open', open)
    menuToggle.setAttribute('aria-expanded', open)
    menuToggle.setAttribute('aria-label', open ? 'Close menu' : 'Open menu')
    menuToggleLabel.textContent = open ? 'Close' : 'Menu'
    siteMenu.classList.toggle('site-menu--open', open)
    siteMenu.setAttribute('aria-hidden', !open)
    menuBackdrop.classList.toggle('menu-backdrop--open', open)
    // Closing (via the toggle, the backdrop, or picking a link - all
    // three call this with open=false) resets the list back to the top
    // rather than remembering where it was left scrolled, so it always
    // reopens fresh instead of picking up mid-list.
    if (!open)
        menuScrollArea.scrollTop = 0
    // The marker dial's collapsed peeking arc sits at a higher z-index
    // than this full-screen menu (see style.css) specifically so it
    // stays reachable over the plain 3D scene - which also means it
    // would otherwise show right through/on top of the menu overlay
    // too. Hidden for as long as the menu is open; restored on close,
    // unless the marker overlay (a separate reason to hide it) happens
    // to still be open at that point too.
    markerDial.classList.toggle('marker-dial--visible', !open && !markerOverlay.classList.contains('marker-overlay--open'))
}

menuToggle.addEventListener('click', () => setMenuOpen(!menuToggle.classList.contains('menu-toggle--open')))
menuBackdrop.addEventListener('click', () => setMenuOpen(false))
// Also closes on picking a link, rather than leaving the panel open behind
// wherever that link navigates to (or, for these placeholder "#" links,
// just sitting open with nothing to actually show for it).
siteMenu.querySelectorAll('a').forEach((link) => link.addEventListener('click', () => setMenuOpen(false)))

// Periodically replays the hamburger's own hover jiggle (menu-toggle--
// attention in style.css reuses the exact same keyframes/stagger as
// :hover there) on its own random timer, the same "draw a little
// attention to this" idea as the marker dial's periodic jiggle/shine -
// but deliberately NOT a fixed-period CSS animation like that one, so
// the two never end up pulsing in lockstep. Each cycle re-rolls its own
// random delay rather than reusing one interval, which is what keeps
// them independent indefinitely instead of just offset-but-still-
// periodic (which would still sync up occasionally at their lowest
// common multiple).
const MENU_TOGGLE_ATTENTION_MIN_DELAY = 6000 // ms
const MENU_TOGGLE_ATTENTION_MAX_DELAY = 14000 // ms
const MENU_TOGGLE_ATTENTION_DURATION = 1050 // bars' 0.8s + 0.16s stagger, and the label's own 0.95s menu-label-peek-jiggle (see style.css) - whichever runs longest, plus a little margin so neither gets cut off a frame early

const scheduleMenuToggleAttention = () =>
{
    const delay = THREE.MathUtils.randFloat(MENU_TOGGLE_ATTENTION_MIN_DELAY, MENU_TOGGLE_ATTENTION_MAX_DELAY)
    setTimeout(() =>
    {
        // Skipped quietly (no catch-up, just wait for the next roll)
        // while the button's either already morphed into the open
        // menu's close "X" or hidden outright behind the expanded
        // dial - a wiggle wouldn't even be visible/sensible there.
        if (!menuToggle.classList.contains('menu-toggle--open') && !siteHeader.classList.contains('site-header--dial-open'))
        {
            menuToggle.classList.add('menu-toggle--attention')
            setTimeout(() => menuToggle.classList.remove('menu-toggle--attention'), MENU_TOGGLE_ATTENTION_DURATION)
        }
        scheduleMenuToggleAttention()
    }, delay)
}
scheduleMenuToggleAttention()

/**
 * Marker overlay
 * Opens on clicking one of the 12 numbered building markers (see their
 * setup in the house model's load callback further down, and the
 * hover/click raycasting in tick()). Real per-marker page URLs go in
 * MARKER_PAGE_URLS below once available - null shows a placeholder
 * message instead of a blank iframe.
 */
const markerOverlay = document.querySelector('#marker-overlay')
const markerOverlayTint = document.querySelector('#marker-overlay-tint')
const markerOverlayClose = document.querySelector('#marker-overlay-close')
const markerOverlayPlaceholder = document.querySelector('#marker-overlay-placeholder')
const markerOverlayFrame = document.querySelector('#marker-overlay-frame')
const markerOverlayContent = document.querySelector('#marker-overlay-content')
const markerOverlayContentInner = document.querySelector('#marker-overlay-content-inner')
const markerOverlayScrollbar = document.querySelector('#marker-overlay-scrollbar')
const markerOverlayScrollbarHandle = document.querySelector('#marker-overlay-scrollbar-handle')

// Builds the full authored layout (hero image, intro, heritage grid,
// vision/mission, CTA) for one marker's entry in markers.json. Plain
// innerHTML, not per-element DOM building - safe here since this only
// ever renders our own authored JSON, never user input. heroImage
// empty/missing shows a dashed placeholder box instead of a broken
// <img> - swap in a real path once images exist, no markup changes
// needed.
// Renders one "chapter" - title/intro/heritage-pillars/closing/cta -
// shared by both the plain single-chapter markers (most of them) and
// the multi-chapter ones (see data.sections below, e.g. marker 10's
// Li'l Oasis/Middle School/Senior School) which just call this once
// per chapter and concatenate the results.
const renderMarkerSection = (section) =>
{
    const pillarsHtml = (section.heritage?.pillars ?? []).map((pillar) =>
    {
        const pillarImageHtml = pillar.image
            ? `<div class="marker-overlay-pillar-media marker-overlay-reveal marker-overlay-reveal--rise"><img src="${pillar.image}" alt="${pillar.title}"></div>`
            : `<div class="marker-overlay-pillar-media marker-overlay-pillar-media--placeholder">Image placeholder</div>`
        return `
        <div class="marker-overlay-pillar">
            ${pillarImageHtml}
            <div class="marker-overlay-pillar-text">
                <h3 class="marker-overlay-pillar-title" data-reveal="type">${pillar.title}</h3>
                <span class="marker-overlay-pillar-rule" aria-hidden="true"></span>
                <p class="marker-overlay-pillar-tagline marker-overlay-reveal">${pillar.tagline}</p>
                <p class="marker-overlay-pillar-description marker-overlay-reveal">${pillar.description}</p>
            </div>
        </div>
    `
    }).join('')

    const bodyParagraphs = (text) => text.split('\n\n').map((paragraph) => `<p class="marker-overlay-reveal">${paragraph}</p>`).join('')

    return `
        <h2 class="marker-overlay-title" data-reveal="type">${section.title}</h2>
        <p class="marker-overlay-intro marker-overlay-reveal">${section.intro}</p>
        ${section.heritage ? `
            <h3 class="marker-overlay-section-heading" data-reveal="type">${section.heritage.heading}</h3>
            <div class="marker-overlay-pillars">${pillarsHtml}</div>
        ` : ''}
        ${section.vision ? `
            <h3 class="marker-overlay-section-heading" data-reveal="type">${section.vision.heading}</h3>
            <div class="marker-overlay-body">${bodyParagraphs(section.vision.body)}</div>
        ` : ''}
        ${section.mission ? `
            <h3 class="marker-overlay-section-heading" data-reveal="type">${section.mission.heading}</h3>
            <div class="marker-overlay-body">${bodyParagraphs(section.mission.body)}</div>
        ` : ''}
        ${section.cta ? `<a class="marker-overlay-cta marker-overlay-reveal" href="${section.cta.url || '#'}"${section.cta.url ? ' target="_blank" rel="noopener noreferrer"' : ''}>${section.cta.label}</a>` : ''}
    `
}

const renderMarkerOverlayContent = (data) =>
{
    const heroImageHtml = data.heroImage
        ? `<img class="marker-overlay-hero-image marker-overlay-reveal marker-overlay-reveal--rise" src="${data.heroImage}" alt="${data.title}">`
        : `<div class="marker-overlay-hero-image marker-overlay-hero-image--placeholder">Image placeholder</div>`

    // Most markers are one chapter (title/intro/heritage/vision/mission/
    // cta at the top level) - data.sections is the exception, for a
    // marker covering several distinct stages/chapters back to back
    // (each with its own title, intro, pillars, closing and CTA) under
    // one shared hero image/LHS title.
    const sectionsHtml = data.sections
        ? data.sections.map((section) => renderMarkerSection(section)).join('<hr class="marker-overlay-section-divider">')
        : renderMarkerSection(data)

    markerOverlayContentInner.innerHTML = `
        ${heroImageHtml}
        ${sectionsHtml}
    `
    setupMarkerOverlayReveal()
}

// Scroll-triggered reveal for the overlay panel's own content (RHS
// only - the LHS hero title/subtitle are untouched): headings/titles
// type on letter by letter, paragraphs and images fade+rise in, each
// the first time it scrolls into the panel's visible area - including
// immediately for whatever's already in view the moment the panel
// opens, since observe() fires once right away with the current
// intersection state.
let markerOverlayRevealObserver = null
let markerOverlayTypeTimers = []
const markerOverlayTypeCharsByEl = new Map()

const clearMarkerOverlayTypeTimers = () =>
{
    markerOverlayTypeTimers.forEach(clearTimeout)
    markerOverlayTypeTimers = []
}

// Splits the heading's text into one hidden span per character up
// front (so the full word never flashes before animating), behind an
// aria-hidden wrapper with the real text as an aria-label on the
// heading itself - screen readers get the whole word, not 47 separate
// single-letter announcements.
const prepareMarkerOverlayTypeElement = (el) =>
{
    const text = el.textContent
    el.setAttribute('aria-label', text)
    el.textContent = ''
    const wrapper = document.createElement('span')
    wrapper.setAttribute('aria-hidden', 'true')
    const spans = [...text].map((char) =>
    {
        const span = document.createElement('span')
        span.className = 'marker-overlay-type-char'
        span.textContent = char
        wrapper.appendChild(span)
        return span
    })
    el.appendChild(wrapper)
    markerOverlayTypeCharsByEl.set(el, spans)
}

const startMarkerOverlayTypewriter = (el) =>
{
    const spans = markerOverlayTypeCharsByEl.get(el) ?? []
    spans.forEach((span, i) =>
        markerOverlayTypeTimers.push(setTimeout(() =>
            span.classList.add('marker-overlay-type-char--visible'), i * 55)))
}

const setupMarkerOverlayReveal = () =>
{
    // Fresh content just replaced the old DOM wholesale - drop the
    // previous observer/timers rather than letting them pile up across
    // every marker open.
    markerOverlayRevealObserver?.disconnect()
    clearMarkerOverlayTypeTimers()
    markerOverlayTypeCharsByEl.clear()

    const typeTargets = [...markerOverlayContentInner.querySelectorAll('[data-reveal="type"]')]
    typeTargets.forEach(prepareMarkerOverlayTypeElement)
    const fadeTargets = [...markerOverlayContentInner.querySelectorAll('.marker-overlay-reveal')]

    // Images lead, text follows - within one batch of entries becoming
    // visible together (e.g. hero image + title + intro, all already
    // in view the instant the panel opens), images (the --rise
    // variant) start revealing first, then every text element
    // (typewriter titles and plain fade paragraphs alike) waits for
    // TEXT_LEAD_MS before starting, so the banner photo is always
    // what the eye catches first rather than the two racing each other.
    const TEXT_LEAD_MS = 220
    markerOverlayRevealObserver = new IntersectionObserver((entries) =>
    {
        const visible = entries.filter((entry) => entry.isIntersecting)
        const images = visible.filter((entry) => entry.target.classList.contains('marker-overlay-reveal--rise'))
        const text = visible.filter((entry) => !entry.target.classList.contains('marker-overlay-reveal--rise'))

        images.forEach((entry, i) =>
            markerOverlayTypeTimers.push(setTimeout(() =>
                entry.target.classList.add('marker-overlay-reveal--visible'), i * 35)))

        text.forEach((entry, i) =>
        {
            markerOverlayTypeTimers.push(setTimeout(() =>
            {
                if (entry.target.dataset.reveal === 'type')
                    startMarkerOverlayTypewriter(entry.target)
                else
                    entry.target.classList.add('marker-overlay-reveal--visible')
            }, TEXT_LEAD_MS + i * 35))
        })

        visible.forEach((entry) => markerOverlayRevealObserver.unobserve(entry.target))
    }, { root: markerOverlayContent, threshold: 0.15 })

    typeTargets.forEach((el) => markerOverlayRevealObserver.observe(el))
    fadeTargets.forEach((el) => markerOverlayRevealObserver.observe(el))
}

// Custom scroll for .marker-overlay-content - replaces native
// overflow-scroll entirely (see index.html/style.css for why: native
// scroll here repaints through this panel's own backdrop-filter blur
// every event, competing with the WebGL render loop for the main
// thread, which is what was actually causing the reported lag - and
// separately, the ask was for no default scrollbar anyway). Wheel,
// dragging the handle, and touch-scrolling all set current/target 1:1
// with no easing - only the touch momentum coast (below) adds any
// cushioning, and only once the finger actually lifts.
const markerOverlayScroll = { current: 0, target: 0, max: 0, dragging: false, dragStartY: 0, dragStartScroll: 0, touchVelocity: 0, lastTouchY: 0, lastTouchTime: 0, momentumRaf: null, touchActive: false }

// The close button fades out for as long as ANY of these are true (see
// .marker-overlay-close--scrolling in style.css) - a touch drag, the
// momentum coast, or dragging the scrollbar handle itself - and back
// in once every one of them has settled.
const isMarkerOverlayScrollActive = () =>
    markerOverlayScroll.momentumRaf !== null || markerOverlayScroll.dragging || markerOverlayScroll.touchActive

const updateMarkerOverlayCloseVisibility = () =>
    markerOverlayClose.classList.toggle('marker-overlay-close--scrolling', isMarkerOverlayScrollActive())

const markerOverlayScrollVisibleRatio = () =>
{
    const contentHeight = markerOverlayContentInner.scrollHeight
    return contentHeight > 0 ? Math.min(1, markerOverlayContent.clientHeight / contentHeight) : 1
}

const updateMarkerOverlayScrollBounds = () =>
{
    markerOverlayScroll.max = Math.max(0, markerOverlayContentInner.scrollHeight - markerOverlayContent.clientHeight)
    markerOverlayScroll.target = THREE.MathUtils.clamp(markerOverlayScroll.target, 0, markerOverlayScroll.max)
    markerOverlayScroll.current = THREE.MathUtils.clamp(markerOverlayScroll.current, 0, markerOverlayScroll.max)
}

const applyMarkerOverlayScroll = () =>
{
    // Rounded to a whole pixel - current is eased via fractional lerp
    // math (e.g. -123.456px), and a sub-pixel translateY on this
    // GPU-composited layer (will-change: transform, see style.css)
    // forces the browser to rasterize the text off the device pixel
    // grid, which is what was actually reading as blurry - not a font/
    // smoothing issue, since it only ever showed up on scrollable
    // (transformed) content.
    // translateZ(0) pins this to one stable composited layer for the
    // whole scroll, rather than letting the browser re-evaluate layer
    // promotion/rasterization boundaries frame to frame - without it,
    // rounded-corner images and text inside were re-rasterizing at a
    // slightly different sub-pixel offset each frame, reading as
    // flickering edges during the scroll itself (same family of bug as
    // the Math.round() below, just the layer-boundary half of it).
    markerOverlayContentInner.style.transform = `translateY(${Math.round(-markerOverlayScroll.current)}px) translateZ(0)`
    const visibleRatio = markerOverlayScrollVisibleRatio()
    markerOverlayScrollbar.hidden = visibleRatio >= 1 // nothing to scroll - no handle needed
    if (markerOverlayScrollbar.hidden)
        return
    const trackHeight = markerOverlayScrollbar.clientHeight
    const handleHeight = Math.max(30, trackHeight * visibleRatio)
    const scrollRatio = markerOverlayScroll.max > 0 ? markerOverlayScroll.current / markerOverlayScroll.max : 0
    markerOverlayScrollbarHandle.style.height = `${handleHeight}px`
    markerOverlayScrollbarHandle.style.top = `${(trackHeight - handleHeight) * scrollRatio}px`
}

const stopMarkerOverlayMomentum = () =>
{
    if (markerOverlayScroll.momentumRaf !== null)
    {
        cancelAnimationFrame(markerOverlayScroll.momentumRaf)
        markerOverlayScroll.momentumRaf = null
    }
}

// Coasts on after the finger lifts, decaying via friction each frame,
// rather than stopping dead the instant touch ends - this (missing
// entirely before) was the actual "stiff" feeling reported: wheel
// input already eased via tickMarkerOverlayScroll above, but touch set
// current = target directly with nothing at all picking up afterwards.
const startMarkerOverlayMomentum = () =>
{
    let lastTime = performance.now()
    const step = () =>
    {
        const now = performance.now()
        const dt = now - lastTime
        lastTime = now
        markerOverlayScroll.target = THREE.MathUtils.clamp(
            markerOverlayScroll.target + markerOverlayScroll.touchVelocity * dt, 0, markerOverlayScroll.max)
        markerOverlayScroll.current = markerOverlayScroll.target
        applyMarkerOverlayScroll()
        // Exponential decay (not a linear one) - reads as a natural
        // coast-and-settle rather than a constant-rate slowdown.
        markerOverlayScroll.touchVelocity *= 0.94
        // Hitting either end kills the coast outright rather than
        // trying to bounce off it.
        if (markerOverlayScroll.target <= 0 || markerOverlayScroll.target >= markerOverlayScroll.max)
            markerOverlayScroll.touchVelocity = 0
        if (Math.abs(markerOverlayScroll.touchVelocity) < 0.01)
        {
            markerOverlayScroll.momentumRaf = null
            updateMarkerOverlayCloseVisibility()
            return
        }
        markerOverlayScroll.momentumRaf = requestAnimationFrame(step)
    }
    markerOverlayScroll.momentumRaf = requestAnimationFrame(step)
}

markerOverlayContent.addEventListener('wheel', (event) =>
{
    event.preventDefault()
    stopMarkerOverlayMomentum()
    updateMarkerOverlayScrollBounds()
    // No easing here by request - 1:1 with the wheel, same as
    // dragging. The touch momentum coast (startMarkerOverlayMomentum)
    // is the actual "cushion at the end" ask and is untouched - this
    // only removes the continuous lerp tickMarkerOverlayScroll used to
    // apply on every wheel tick.
    markerOverlayScroll.target = THREE.MathUtils.clamp(markerOverlayScroll.target + event.deltaY, 0, markerOverlayScroll.max)
    markerOverlayScroll.current = markerOverlayScroll.target
    applyMarkerOverlayScroll()
    updateMarkerOverlayCloseVisibility()
}, { passive: false })

// Touch - direct 1:1 tracking while a finger's actually down (same
// reasoning as the scrollbar handle drag below), but tracks recent
// velocity along the way so touchend can hand off to the momentum
// coast above.
markerOverlayContent.addEventListener('touchstart', (event) =>
{
    stopMarkerOverlayMomentum()
    updateMarkerOverlayScrollBounds()
    markerOverlayScroll.dragStartScroll = markerOverlayScroll.current
    markerOverlayScroll.dragStartY = event.touches[0].clientY
    markerOverlayScroll.lastTouchY = event.touches[0].clientY
    markerOverlayScroll.lastTouchTime = performance.now()
    markerOverlayScroll.touchVelocity = 0
    markerOverlayScroll.touchActive = true
    updateMarkerOverlayCloseVisibility()
}, { passive: true })

markerOverlayContent.addEventListener('touchmove', (event) =>
{
    const touchY = event.touches[0].clientY
    const deltaY = markerOverlayScroll.dragStartY - touchY
    markerOverlayScroll.target = THREE.MathUtils.clamp(markerOverlayScroll.dragStartScroll + deltaY, 0, markerOverlayScroll.max)
    markerOverlayScroll.current = markerOverlayScroll.target
    applyMarkerOverlayScroll()

    const now = performance.now()
    const dt = now - markerOverlayScroll.lastTouchTime
    if (dt > 0)
    {
        // px/ms, positive = finger moving up = content scrolling down.
        // Blended with the previous reading (not just the latest one)
        // so one jittery event right before lift-off doesn't dictate
        // the whole coast.
        const instantVelocity = (markerOverlayScroll.lastTouchY - touchY) / dt
        markerOverlayScroll.touchVelocity = markerOverlayScroll.touchVelocity * 0.7 + instantVelocity * 0.3
    }
    markerOverlayScroll.lastTouchY = touchY
    markerOverlayScroll.lastTouchTime = now
}, { passive: true })

const finishMarkerOverlayTouch = () =>
{
    markerOverlayScroll.touchActive = false
    // A slow/deliberate drag that was barely moving when the finger
    // lifted just stays put - only a genuine flick coasts.
    if (Math.abs(markerOverlayScroll.touchVelocity) > 0.02)
        startMarkerOverlayMomentum()
    updateMarkerOverlayCloseVisibility()
}
markerOverlayContent.addEventListener('touchend', finishMarkerOverlayTouch, { passive: true })
markerOverlayContent.addEventListener('touchcancel', finishMarkerOverlayTouch, { passive: true })

// Handle drag - same pointer-capture pattern as the marker dial's own
// drag-to-rotate.
markerOverlayScrollbar.addEventListener('pointerdown', (event) =>
{
    stopMarkerOverlayMomentum()
    updateMarkerOverlayScrollBounds()
    markerOverlayScroll.dragging = true
    markerOverlayScrollbar.classList.add('marker-overlay-scrollbar--dragging')
    markerOverlayScroll.dragStartY = event.clientY
    markerOverlayScroll.dragStartScroll = markerOverlayScroll.current
    markerOverlayScrollbar.setPointerCapture(event.pointerId)
    updateMarkerOverlayCloseVisibility()
})

markerOverlayScrollbar.addEventListener('pointermove', (event) =>
{
    if (!markerOverlayScroll.dragging)
        return
    const trackHeight = markerOverlayScrollbar.clientHeight
    const handleHeight = Math.max(30, trackHeight * markerOverlayScrollVisibleRatio())
    const draggableTrack = Math.max(1, trackHeight - handleHeight)
    const scrollDelta = ((event.clientY - markerOverlayScroll.dragStartY) / draggableTrack) * markerOverlayScroll.max
    markerOverlayScroll.target = THREE.MathUtils.clamp(markerOverlayScroll.dragStartScroll + scrollDelta, 0, markerOverlayScroll.max)
    markerOverlayScroll.current = markerOverlayScroll.target
    applyMarkerOverlayScroll()
})

const finishMarkerOverlayScrollbarDrag = () =>
{
    markerOverlayScroll.dragging = false
    markerOverlayScrollbar.classList.remove('marker-overlay-scrollbar--dragging')
    updateMarkerOverlayCloseVisibility()
}
markerOverlayScrollbar.addEventListener('pointerup', finishMarkerOverlayScrollbarDrag)
markerOverlayScrollbar.addEventListener('pointercancel', finishMarkerOverlayScrollbarDrag)

window.addEventListener('resize', () =>
{
    if (!markerOverlayContent.hidden)
    {
        updateMarkerOverlayScrollBounds()
        applyMarkerOverlayScroll()
    }
})

// One entry per marker (1-16) - fill in real URLs here once they exist.
const MARKER_PAGE_URLS = new Array(16).fill(null)

// Hides/shows every marker's line+label (see markerFadeRefs, populated
// in the house model's load callback further down) - toggled around the
// overlay opening/closing below so the OTHER 11 markers don't clutter a
// marker's own zoomed-in close-up shot. Toggling .visible rather than
// opacity/scale sidesteps tick()'s own reveal/hover animation entirely -
// that loop still runs underneath and keeps each ref's real opacity/scale
// exactly where it should be, so nothing needs restoring on the way back.
const setMarkersVisible = (visible) =>
{
    markerFadeRefs.forEach((ref) =>
    {
        ref.line.visible = visible
        ref.label.visible = visible
    })
}

// Matches style.css's own mobile breakpoint (@media (max-width: 760px))
// - checked live at click time rather than cached, so rotating a tablet
// or resizing a window between checks doesn't leave a stale answer.
const isMobileViewport = () => window.matchMedia('(max-width: 760px)').matches

// Wider than isMobileViewport - covers phones AND tablets, for the one
// spot that needs both lumped together (the default/post-intro camera
// framing below). Kept separate rather than widening isMobileViewport
// itself since that one's other callers (menu/overlay behaviour) are
// meant to stay phone-only.
const isMobileOrTabletViewport = () => window.matchMedia('(max-width: 1024px)').matches

// On mobile, the overlay only opens once the camera has actually finished
// flying in (see the click handler below and updateMarkerCameraAnimation's
// completion check) - watching the zoom animation play out first, THEN
// the page sliding in on top of it, reads better on a small screen where
// the two would otherwise compete for the same limited space. Desktop
// keeps the original behaviour (overlay slides in immediately, in
// parallel with the zoom) since there's a widescreen's worth of the 3D
// scene still visible alongside the 40%-wide panel. -1 means no marker
// zoom is currently waiting on an overlay open.
let pendingMobileMarkerOverlayIndex = -1

// Pending setTimeout ids for the staggered reveal below - tracked so a
// second open (a different marker) or an early close can cancel
// whichever steps haven't fired yet, rather than letting them land late
// on top of whatever state the overlay's since moved to.
let markerOverlaySequenceTimers = []

const clearMarkerOverlaySequenceTimers = () =>
{
    markerOverlaySequenceTimers.forEach(clearTimeout)
    markerOverlaySequenceTimers = []
}

const openMarkerOverlay = (index) =>
{
    // markers.json (authored content) takes priority over both the
    // iframe URL and the plain placeholder below - once a marker has a
    // real entry there, that's what shows.
    const content = markerContent[index + 1]
    if (content)
    {
        renderMarkerOverlayContent(content)
        markerOverlayContent.hidden = false
        markerOverlayPlaceholder.hidden = true
        markerOverlayFrame.hidden = true
        // Fresh scroll position every time this opens, whichever marker
        // it was scrolled to last time.
        stopMarkerOverlayMomentum()
        markerOverlayScroll.current = 0
        markerOverlayScroll.target = 0
        markerOverlayScroll.dragging = false
        markerOverlayScroll.touchActive = false
        updateMarkerOverlayScrollBounds()
        applyMarkerOverlayScroll()
        updateMarkerOverlayCloseVisibility()
    }
    else
    {
        markerOverlayContent.hidden = true
        const url = MARKER_PAGE_URLS[index]
        if (url)
        {
            markerOverlayFrame.src = url
            markerOverlayFrame.hidden = false
            markerOverlayPlaceholder.hidden = true
        }
        else
        {
            markerOverlayPlaceholder.textContent = `Marker ${index + 1} - page coming soon`
            markerOverlayPlaceholder.hidden = false
            markerOverlayFrame.hidden = true
        }
    }
    clearMarkerOverlaySequenceTimers()
    markerOverlay.classList.add('marker-overlay--open')
    markerOverlay.setAttribute('aria-hidden', 'false')
    setMarkersVisible(false)
    // setMarkersVisible alone doesn't stop the canvas click handler/
    // tick()'s own hover raycast from still hitting these markers -
    // three.js's Raycaster doesn't check .visible by default, so an
    // invisible marker is still perfectly "clickable". Without this, a
    // click on the exposed left-hand slice of the 3D scene (while the
    // overlay covers the right 47%) could still raycast-hit a different
    // marker and re-trigger flyCameraToMarker, visibly flying the
    // camera to a new shot behind the panel that's already open on the
    // old one - reusing the same flag the circle-nav dial suspends
    // mouse interaction with (see setMarkerDialExpanded) rather than a
    // separate one, since the canvas click handler/hover block already
    // check it.
    markerInteractionSuspended = true
    hoveredMarkerIndex = -1
    canvas.style.cursor = 'default'
    setMarkerDialExpanded(false)
    markerDial.classList.remove('marker-dial--visible')
    heroMarkerTitle.textContent = MARKER_NAMES[index] ?? `Marker ${index + 1}`
    // innerHTML, not textContent - markers.json's own heroSubtitle
    // strings author their line break the same way .hero-title's
    // default tagline does (a literal <br>), for a predictable 2-line
    // split rather than hoping the natural wrap lands there.
    heroMarkerSubtitle.innerHTML = markerContent[index + 1]?.heroSubtitle ?? ''
    // Staggered reveal, per request - the 40% panel slides in first
    // (above), then the 60% scene tint, then the hero title - rather
    // than all three starting in the same instant. Each delay is timed
    // to start while the previous step is still easing out, not after
    // it fully stops, so the sequence reads as one continuous motion
    // instead of three separate beats.
    markerOverlaySequenceTimers.push(setTimeout(() =>
        markerOverlayTint.classList.add('marker-overlay-tint--visible'), 250))
    markerOverlaySequenceTimers.push(setTimeout(() =>
    {
        // Crossfades the landing tagline out (hero--marker-active fades
        // heroTitle itself, see style.css) while this marker's own
        // title/subtitle fade/slide up into view on the separate
        // heroMarkerBlock - see closeMarkerOverlay for the reverse.
        hero.classList.add('hero--marker-active')
        heroMarkerBlock.classList.add('hero-marker-block--visible')
    }, 500))
}

const closeMarkerOverlay = () =>
{
    clearMarkerOverlaySequenceTimers()
    // Staggered close, mirroring openMarkerOverlay's reveal in reverse -
    // hero title first, then the 60% tint, then the 40% panel itself
    // last. Reverses openMarkerOverlay's crossfade - tagline fades back
    // in, marker title/subtitle fade back out (their text just gets
    // overwritten next time it opens, no need to clear it now).
    hero.classList.remove('hero--marker-active')
    heroMarkerBlock.classList.remove('hero-marker-block--visible')
    markerOverlaySequenceTimers.push(setTimeout(() =>
        markerOverlayTint.classList.remove('marker-overlay-tint--visible'), 250))
    markerOverlaySequenceTimers.push(setTimeout(() =>
    {
        markerOverlay.classList.remove('marker-overlay--open')
        // Browsers block aria-hidden on a container that still contains
        // the focused element (e.g. this close button, right after its
        // own click handler runs) - blur it first so the container is
        // safe to hide.
        if (markerOverlay.contains(document.activeElement))
            document.activeElement.blur()
        markerOverlay.setAttribute('aria-hidden', 'true')
        // Stops whatever the iframe loaded (video/audio included) - held
        // off until the panel is actually about to slide away, rather
        // than immediately, so its content doesn't go blank while the
        // panel is still sitting there fully open.
        markerOverlayFrame.src = ''
        setMarkersVisible(true)
        markerInteractionSuspended = false
        markerDial.classList.add('marker-dial--visible')
    }, 500))
    // Flies the camera back out to wherever it was before the first
    // marker was clicked (see flyCameraToMarker/flyCameraAwayFromMarker,
    // defined near the Camera section) - a no-op if no marker is
    // actually zoomed in (e.g. Escape pressed twice). Starts right away,
    // in parallel with the staggered UI close above rather than waiting
    // on it - it's a separate, noticeably longer 3D animation.
    flyCameraAwayFromMarker()
}

markerOverlayClose.addEventListener('click', closeMarkerOverlay)
// Clicking the dark backdrop area itself (not the close button, not the
// iframe/placeholder content) closes it too, same pattern as the
// hamburger menu's own backdrop.
markerOverlay.addEventListener('click', (event) =>
{
    if (event.target === markerOverlay)
        closeMarkerOverlay()
})
window.addEventListener('keydown', (event) =>
{
    if (event.key === 'Escape' && markerOverlay.classList.contains('marker-overlay--open'))
        closeMarkerOverlay()
})

/**
 * Marker dial - circular navigator
 * Styled after jenamdvacetpet.cz's own activated timeline dial: a ring
 * of small plain dots (not buttons) that drags to rotate, with
 * whichever one ends up nearest the top named in the ring's own center
 * alongside a "View" button - clicking that is what actually feeds
 * into flyCameraToMarker/openMarkerOverlay (the same flow the 3D
 * badges themselves use). Collapsed to a small peeking arc at rest
 * (see .marker-dial's `top` in style.css); tapping it re-centers and
 * expands the ring. The 12 dots themselves are built inside the house
 * model's load callback further down (where MARKER_COUNT is actually
 * in scope), pushed into markerDialItems below - everything here that
 * doesn't need that count (refs, state, the rotation math, the drag
 * handlers) is set up now.
 */
const markerDial = document.querySelector('#marker-dial')
const markerDialRing = document.querySelector('#marker-dial-ring')
const markerDialDragHint = document.querySelector('#marker-dial-drag-hint')
const markerDialBackdrop = document.querySelector('#marker-dial-backdrop')
const markerDialClose = document.querySelector('#marker-dial-close')
const markerDialCenterName = document.querySelector('#marker-dial-center-name')
const markerDialCenterButton = document.querySelector('#marker-dial-center-button')

// Placeholder names - swap in the real ones whenever they exist, same
// spirit as MARKER_PAGE_URLS above (one entry per marker, 1-16). Marker
// 1's is real (matches its markers.json content's own theme), used both
// as the LHS hero title (openMarkerOverlay) and the dial's center name.
const MARKER_NAMES = Array.from({ length: 16 }, (_, i) => `Marker ${i + 1}`)
MARKER_NAMES[0] = 'Philosophy & Vision'
MARKER_NAMES[1] = 'Kids Play Area'
MARKER_NAMES[2] = 'Café GDPS'
MARKER_NAMES[3] = 'Basketball Court'
MARKER_NAMES[4] = 'Outdoor Games'
MARKER_NAMES[9] = `Li'l Oasis to Senior School`
MARKER_NAMES[11] = 'A Growing Campus'
MARKER_NAMES[5] = 'The Banyan Tree'
MARKER_NAMES[6] = 'Farm & Garden'
MARKER_NAMES[7] = 'Wood Design & Making'
MARKER_NAMES[8] = 'Automotive Lab'
MARKER_NAMES[10] = 'Sprint Avenue'
MARKER_NAMES[12] = 'RC Track & Lab'
MARKER_NAMES[13] = 'Robotics Lab'
MARKER_NAMES[14] = 'Tune & Tone'
MARKER_NAMES[15] = 'Metalwork & CNC'

const MARKER_DIAL_ITEM_ANGLE = 360 / 16 // matches MARKER_COUNT - see the build loop further down
const markerDialItems = [] // { itemEl, slotAngle }, one per marker, pushed once the ring is built
// markerDialRotation is the authoritative target - drag/wheel/click/
// snap all write to it directly and instantly, same as before. What's
// actually rendered (and what drives the active-index/center-name
// sync) is markerDialDisplayRotation, a few degrees behind it at any
// given moment - a small spring (see ensureMarkerDialSpringRunning
// further down) continuously eases the display value toward whatever
// the target currently is, with a touch of overshoot on settle. That
// one mechanism is what gives both a live drag and a wheel/snap/click
// their damped, slightly elastic feel, rather than the ring either
// jumping instantly or needing a separate tween per gesture.
let markerDialRotation = 0 // degrees - 0 means marker 1's slotAngle (0) sits at the top/12-o'clock position
let markerDialDisplayRotation = 0
let markerDialSpringVelocity = 0
let markerDialSpringRaf = null
let markerDialActiveIndex = -1 // whichever marker is currently nearest the top - what the center name/button refer to
let markerDialExpanded = false
// While true, the markers stay fully visible and the camera keeps
// auto-rotating - only mouse-driven interaction with either (canvas
// click-to-open, hover-highlight, OrbitControls drag/zoom) is inert.
// Set alongside setOrbitControlsSuspended() in setMarkerDialExpanded.
let markerInteractionSuspended = false
let markerDialDrag = null // { startRotation, startPointerAngle, centerX, centerY } while a drag is in progress, else null
// Once true (the user's first real drag), the "Drag to explore" hint
// never shows again for the rest of the session - it's done its job.
let markerDialHasDragged = false

// Recomputes every item's on-screen rotation (slotAngle + the live
// DISPLAY rotation - see markerDialDisplayRotation above, not the raw
// target), flags whichever is angularly closest to the top as "active"
// (just a dot's own ::after in style.css - see .marker-dial-item--
// active), and keeps the center name in sync with that same index so
// it always names whatever's actually sitting at the top. Reading the
// display value (not the target) here means the center name itself
// only updates once the spring's actually carried the ring there,
// rather than flipping the instant a drag/wheel/click sets a new
// target several slots away.
const applyMarkerDialRotation = () =>
{
    let bestIndex = -1
    let bestDistance = Infinity
    markerDialItems.forEach(({ itemEl, slotAngle }, i) =>
    {
        const totalAngle = slotAngle + markerDialDisplayRotation
        itemEl.style.transform = `rotate(${totalAngle}deg)`
        const normalized = ((totalAngle % 360) + 360) % 360
        const distanceFromTop = Math.min(normalized, 360 - normalized)
        itemEl.classList.toggle('marker-dial-item--active', distanceFromTop < MARKER_DIAL_ITEM_ANGLE / 2)
        if (distanceFromTop < bestDistance)
        {
            bestDistance = distanceFromTop
            bestIndex = i
        }
    })
    markerDialActiveIndex = bestIndex
    markerDialCenterName.textContent = bestIndex !== -1 ? (MARKER_NAMES[bestIndex] ?? `Marker ${bestIndex + 1}`) : ''
}

// A light, slightly underdamped spring - stiffness/damping tuned for a
// subtle "catches up with a little give" feel rather than a pronounced
// wobble. Runs continuously (restarting itself via rAF) for as long as
// the display value hasn't settled on the target, regardless of which
// gesture (drag/wheel/click-to-select/snap) last moved that target;
// all of them just write markerDialRotation and call this, rather than
// each owning its own tween.
const MARKER_DIAL_SPRING_STIFFNESS = 230
const MARKER_DIAL_SPRING_DAMPING = 20

const ensureMarkerDialSpringRunning = () =>
{
    if (markerDialSpringRaf !== null)
        return
    let lastTime = performance.now()
    const step = () =>
    {
        const now = performance.now()
        // Seconds, clamped so a dropped/backgrounded frame can't fling
        // the spring with one huge catch-up step.
        const dt = Math.min((now - lastTime) / 1000, 1 / 30)
        lastTime = now

        const displacement = markerDialDisplayRotation - markerDialRotation
        const acceleration = -MARKER_DIAL_SPRING_STIFFNESS * displacement - MARKER_DIAL_SPRING_DAMPING * markerDialSpringVelocity
        markerDialSpringVelocity += acceleration * dt
        markerDialDisplayRotation += markerDialSpringVelocity * dt
        applyMarkerDialRotation()

        if (Math.abs(displacement) < 0.01 && Math.abs(markerDialSpringVelocity) < 0.01)
        {
            markerDialDisplayRotation = markerDialRotation
            markerDialSpringVelocity = 0
            applyMarkerDialRotation()
            markerDialSpringRaf = null
            return
        }
        markerDialSpringRaf = requestAnimationFrame(step)
    }
    markerDialSpringRaf = requestAnimationFrame(step)
}

const setMarkerDialExpanded = (expanded) =>
{
    markerDialExpanded = expanded
    markerDial.classList.toggle('marker-dial--expanded', expanded)
    // Same reasoning as closeMarkerOverlay's blur - the center "View"
    // button (or the close button) can still be focused right as this
    // collapses the dial back to aria-hidden.
    if (!expanded && markerDial.contains(document.activeElement))
        document.activeElement.blur()
    markerDial.setAttribute('aria-hidden', !expanded)
    markerDialBackdrop.classList.toggle('marker-dial-backdrop--visible', expanded)
    markerDialClose.classList.toggle('marker-dial-close--visible', expanded)
    // The backdrop visually covers the scene, but OrbitControls/the
    // marker badges' own hover+click raycasting were still live
    // underneath it, which is what was actually fighting the ring's
    // own drag-to-rotate gesture (and sometimes eating the first
    // pointer event outright) - same suspend-while-covered treatment
    // the marker overlay already gets. The dial can only ever be
    // expanded while the marker overlay is closed (see setMenuOpen/
    // openMarkerOverlay, which hide the dial's peek entirely
    // otherwise), so this never fights that overlay's own suspend
    // calls on the way back.
    // Camera auto-rotate and the markers themselves both stay visibly
    // running - only the MOUSE-driven interactions (orbit-drag/zoom,
    // marker hover/click) stop responding for as long as the dial's
    // open, which is what was actually fighting the ring's own drag
    // gesture. Deliberately NOT setOrbitControlsSuspended() here - that
    // flips theatreCameraState.enabled, which makes tick() skip
    // controls.update() altogether (see the guard around it there) and
    // so stops the turntable autoRotate too, which isn't wanted. Setting
    // controls.enabled directly only gates OrbitControls' own pointer/
    // wheel listeners; controls.update()/autoRotate keep running every
    // frame exactly as before. markerInteractionSuspended is checked by
    // the canvas click handler and the hover raycast in tick() below.
    //
    // Collapsing (expanded === false) only turns controls back ON when
    // nothing else currently needs them suspended - the dial's own
    // "View" button calls flyCameraToMarker() (which suspends orbit via
    // setOrbitControlsSuspended, since a marker is now zoomed in/its
    // overlay about to open) immediately BEFORE collapsing the dial via
    // this same function; unconditionally forcing controls.enabled back
    // to true here was clobbering that suspension the instant the dial
    // closed, letting the exposed scene behind the now-open marker
    // overlay still be orbit-dragged. markerCameraState.zoomedIn is set
    // synchronously at the top of flyCameraToMarker (before this ever
    // runs), so checking it here is reliable regardless of that call
    // order.
    if (expanded)
        controls.enabled = false
    else if (!markerCameraState.zoomedIn)
        controls.enabled = true
    markerInteractionSuspended = expanded
    if (expanded)
    {
        hoveredMarkerIndex = -1
        canvas.style.cursor = 'default'
    }
    // Reuses the same white-logo mechanism as the hamburger menu's own
    // full-screen overlay (site-header--menu-open) - the logo would
    // otherwise stay black against the dial's own darkened backdrop.
    // Also hides the hamburger button itself for as long as the dial is
    // open - it sits in the same corner as the dial's own close button,
    // and the backdrop already blocks clicking it anyway (see its
    // z-index in style.css), so a dim, unusable ghost button there was
    // just visual clutter.
    siteHeader.classList.toggle('site-header--dial-open', expanded)
    // Shown every time the dial opens, until the user has actually
    // dragged it once (see the ring's own pointerdown handler below).
    markerDialDragHint.classList.toggle('marker-dial-drag-hint--visible', expanded && !markerDialHasDragged)
}

// The hint text itself is pointer-events:none (a pure label, see
// style.css) - the click target while collapsed is the dial container
// as a whole, guarded so it only acts as an "expand" tap before the
// ring itself becomes interactive (once expanded, the ring's own drag
// and the center button take over, and outside clicks go to the
// backdrop).
markerDial.addEventListener('click', () =>
{
    if (!markerDialExpanded)
        setMarkerDialExpanded(true)
})
markerDialBackdrop.addEventListener('click', () => setMarkerDialExpanded(false))
markerDialClose.addEventListener('click', () => setMarkerDialExpanded(false))

// "View" - jumps straight to whichever marker is currently active
// (nearest the top), same flyCameraToMarker/openMarkerOverlay pairing
// the 3D badges themselves trigger on click.
markerDialCenterButton.addEventListener('click', (event) =>
{
    // Without this, the click bubbles up to #marker-dial's own "tap
    // while collapsed to expand" listener just below - by the time it
    // gets there, setMarkerDialExpanded(false) here has already flipped
    // markerDialExpanded to false, so that listener sees what looks
    // like a fresh collapsed-state tap and immediately re-expands the
    // dial right back open (the "tint stays" bug - the backdrop it
    // brings back with it never actually goes away).
    event.stopPropagation()
    if (markerDialActiveIndex === -1)
        return
    flyCameraToMarker(markerDialActiveIndex)
    if (isMobileViewport())
        pendingMobileMarkerOverlayIndex = markerDialActiveIndex
    else
        openMarkerOverlay(markerDialActiveIndex)
    setMarkerDialExpanded(false)
})

// Angle (degrees, 0 = straight up, increasing clockwise) from a pivot
// point to a pointer position - same convention as each item's own
// slotAngle, so a drag delta can be added directly to markerDialRotation.
const markerDialAngleFromCenter = (clientX, clientY, centerX, centerY) =>
    Math.atan2(clientX - centerX, -(clientY - centerY)) * (180 / Math.PI)

markerDialRing.addEventListener('pointerdown', (event) =>
{
    // Only the ring's own background starts a rotate-drag - a pointer
    // that landed on the center name/button, or on one of the numbered
    // items themselves (see their own click handler further down,
    // which rotates straight to whichever one was tapped), is left
    // alone so those elements' own click handlers fire cleanly instead
    // of also starting a drag underneath them.
    if (!markerDialExpanded || event.target.closest('.marker-dial-center') || event.target.closest('.marker-dial-item'))
        return

    if (!markerDialHasDragged)
    {
        markerDialHasDragged = true
        markerDialDragHint.classList.remove('marker-dial-drag-hint--visible')
    }

    const rect = markerDialRing.getBoundingClientRect()
    markerDialDrag = {
        centerX: rect.left + rect.width / 2,
        centerY: rect.top + rect.height / 2,
        startRotation: markerDialRotation,
        startPointerAngle: markerDialAngleFromCenter(event.clientX, event.clientY, rect.left + rect.width / 2, rect.top + rect.height / 2)
    }
    markerDial.classList.add('marker-dial--dragging')
    markerDialRing.setPointerCapture(event.pointerId)
})

markerDialRing.addEventListener('pointermove', (event) =>
{
    if (!markerDialDrag)
        return
    const pointerAngle = markerDialAngleFromCenter(event.clientX, event.clientY, markerDialDrag.centerX, markerDialDrag.centerY)
    markerDialRotation = markerDialDrag.startRotation + (pointerAngle - markerDialDrag.startPointerAngle)
    ensureMarkerDialSpringRunning()
})

// Snaps to the nearest 30-degree slot rather than leaving it at whatever
// odd angle a drag/scroll ended on, so a marker always lands cleanly at
// the top (and in the center name) instead of sitting slightly
// off-center. Shared by a drag's own pointerup and the wheel listener's
// debounced "scrolling settled" callback below - the actual visible
// settle (including its slight elastic overshoot) comes from the
// spring above picking up this new target, not from anything in here.
const snapMarkerDialRotation = () =>
{
    markerDialRotation = Math.round(markerDialRotation / MARKER_DIAL_ITEM_ANGLE) * MARKER_DIAL_ITEM_ANGLE
    ensureMarkerDialSpringRunning()
}

// "Jump straight to this one", used by each numbered item's own click
// handler below - sets the target to whichever delta brings the
// clicked index's own slotAngle to the top (the shorter of the two
// directions around the circle, never spinning the long way around
// for a slot that's actually right next door the other way) and lets
// the same spring that handles drag/wheel/snap carry it there.
const selectMarkerDialIndex = (index) =>
{
    const item = markerDialItems[index]
    if (!item)
        return
    const totalAngle = item.slotAngle + markerDialRotation
    const normalized = ((totalAngle % 360) + 360) % 360
    const delta = normalized <= 180 ? -normalized : 360 - normalized
    markerDialRotation += delta
    ensureMarkerDialSpringRunning()
}

const finishMarkerDialDrag = () =>
{
    if (!markerDialDrag)
        return
    markerDialDrag = null
    markerDial.classList.remove('marker-dial--dragging')
    snapMarkerDialRotation()
}
markerDialRing.addEventListener('pointerup', finishMarkerDialDrag)
markerDialRing.addEventListener('pointercancel', finishMarkerDialDrag)

// Mouse-wheel/trackpad rotation - an alternative to click-drag, so a
// desktop mouse (no touch, and a plain click only opens the active
// marker) can still spin the ring. deltaMode distinguishes pixel-based
// wheels (0, most mice/trackpads) from the rarer line- or page-based
// ones (1/2 - some older/legacy devices), normalizing all three to a
// comparable pixel-ish scale before applying the same sensitivity.
// Rotation is applied live per event (like a drag's pointermove), then
// snapped to the nearest slot only once scrolling has actually stopped
// (debounced) rather than after every single wheel tick, so a fast
// trackpad swipe settles once at the end instead of stutter-snapping
// mid-gesture.
const MARKER_DIAL_WHEEL_SENSITIVITY = 0.25 // degrees of rotation per normalized delta pixel
const MARKER_DIAL_WHEEL_SNAP_DELAY = 150 // ms of no further wheel events before snapping
let markerDialWheelSnapTimeout = null

markerDialRing.addEventListener('wheel', (event) =>
{
    if (!markerDialExpanded || event.target.closest('.marker-dial-center'))
        return
    event.preventDefault()

    if (!markerDialHasDragged)
    {
        markerDialHasDragged = true
        markerDialDragHint.classList.remove('marker-dial-drag-hint--visible')
    }

    const deltaPixels = event.deltaMode === 1 ? event.deltaY * 16 : event.deltaMode === 2 ? event.deltaY * window.innerHeight : event.deltaY
    markerDialRotation -= deltaPixels * MARKER_DIAL_WHEEL_SENSITIVITY
    ensureMarkerDialSpringRunning()

    clearTimeout(markerDialWheelSnapTimeout)
    markerDialWheelSnapTimeout = setTimeout(snapMarkerDialRotation, MARKER_DIAL_WHEEL_SNAP_DELAY)
}, { passive: false })

// Swipe-left-to-close - on mobile, a left swipe while any of the three
// full-screen overlays (marker overlay, hamburger menu, expanded marker
// dial) is open closes just that overlay. Without this, that same
// gesture falls through to the browser's own edge-swipe-back navigation
// (iOS Safari/Android Chrome both treat a left-edge swipe as "go back"),
// which leaves the overlay open and navigates the whole page away
// instead - this intercepts it first so only the overlay reacts. Global
// (on window, not per-overlay) since only one of the three is ever open
// at a time and the close logic is identical either way; bails out
// immediately if the touch started on the marker dial's ring, which
// already owns left/right drag for its own rotate gesture and shouldn't
// also be read as a close request.
const SWIPE_CLOSE_DISTANCE = 70 // px of net leftward travel to count as a deliberate swipe
const SWIPE_DIRECTION_ARM_DISTANCE = 10 // px before committing to "this is a horizontal gesture" (filters out an almost-still touch's jitter)
let swipeCloseTracking = null // { startX, startY, horizontal } while a single-finger touch is active over an open overlay, else null

const anyOverlayIsOpenForSwipeClose = () =>
    markerOverlay.classList.contains('marker-overlay--open') ||
    siteMenu.classList.contains('site-menu--open') ||
    markerDialExpanded

window.addEventListener('touchstart', (event) =>
{
    if (event.touches.length !== 1 || !anyOverlayIsOpenForSwipeClose() || event.target.closest('#marker-dial-ring'))
    {
        swipeCloseTracking = null
        return
    }
    swipeCloseTracking = { startX: event.touches[0].clientX, startY: event.touches[0].clientY, horizontal: false }
}, { passive: true })

window.addEventListener('touchmove', (event) =>
{
    if (!swipeCloseTracking)
        return
    const deltaX = event.touches[0].clientX - swipeCloseTracking.startX
    const deltaY = event.touches[0].clientY - swipeCloseTracking.startY
    if (!swipeCloseTracking.horizontal)
    {
        if (Math.max(Math.abs(deltaX), Math.abs(deltaY)) < SWIPE_DIRECTION_ARM_DISTANCE)
            return
        // Committed once: a predominantly-vertical start (scrolling the
        // marker overlay's own content, or the menu's link list) never
        // flips back into "horizontal" even if it drifts sideways later.
        swipeCloseTracking.horizontal = Math.abs(deltaX) > Math.abs(deltaY)
        if (!swipeCloseTracking.horizontal)
        {
            swipeCloseTracking = null
            return
        }
    }
    // Only preventDefault once a leftward horizontal drag is confirmed -
    // this is what actually suppresses the browser's own edge-swipe-back
    // navigation; needs the listener itself to be non-passive to work.
    if (deltaX < 0)
        event.preventDefault()
}, { passive: false })

const finishSwipeCloseTouch = (event) =>
{
    if (!swipeCloseTracking)
        return
    const lastTouch = event.changedTouches[0]
    const deltaX = lastTouch.clientX - swipeCloseTracking.startX
    const deltaY = lastTouch.clientY - swipeCloseTracking.startY
    swipeCloseTracking = null
    if (!(deltaX <= -SWIPE_CLOSE_DISTANCE && Math.abs(deltaX) > Math.abs(deltaY)))
        return

    // Priority matches how these overlays actually nest in z-index/focus
    // - the marker overlay and the menu are mutually exclusive by
    // construction, and the dial can only be expanded while both of
    // those are closed, but checking in this order is cheap and correct
    // either way.
    if (markerOverlay.classList.contains('marker-overlay--open'))
        closeMarkerOverlay()
    else if (siteMenu.classList.contains('site-menu--open'))
        setMenuOpen(false)
    else if (markerDialExpanded)
        setMarkerDialExpanded(false)
}
window.addEventListener('touchend', finishSwipeCloseTouch, { passive: true })
window.addEventListener('touchcancel', () => { swipeCloseTracking = null }, { passive: true })

/**
 * Loading idol preview
 * A small dedicated 3D viewport on the loading screen - a GD idol/bust
 * model, spun continuously via its own render loop while the main scene's
 * assets load in the background. Deliberately its own scene/camera/
 * renderer/GLTFLoader (not the shared loadingManager above) - this preview
 * loading (or, until the model file actually exists, failing to load) has
 * no effect on the main loading bar's percentage, and needs its own camera
 * framing/lighting anyway, independent of the main scene's.
 *
 * Model: static/models/GedeeNadiu_Statue.glb
 */
const loadingIdolWrap = document.querySelector('#loading-idol-wrap')
const loadingIdolCanvas = document.querySelector('#loading-idol')
const loadingIdolScene = new THREE.Scene()
const loadingIdolCamera = new THREE.PerspectiveCamera(35, 1, 0.1, 100)
loadingIdolCamera.position.set(0, 1.2, 4)
loadingIdolCamera.lookAt(0, 1, 0)

// Internal render resolution - matches --idol-size's own max value in
// style.css (min(336px, 42vw)); setSize's 3rd arg (false) keeps the
// canvas's actual on-screen size under CSS's control (including its
// viewport cap on small screens) rather than this call overwriting it
// with a fixed inline style.
// Neither renderer had any webglcontextlost/webglcontextrestored
// handling at all - a lost context (GPU driver crash/reset, common on
// some mobile devices under memory/GPU pressure) just left the canvas
// permanently black with no recovery path, which is also what trips a
// browser's own "this page keeps crashing the GPU, block new contexts"
// protection on repeated losses (reported as "A WebGL context could not
// be created... Web page caused context loss and was blocked" on a
// Pixel 10 XL). preventDefault() is required for a context to even be
// eligible to restore at all; reloading is the pragmatic recovery here
// rather than hand-reconstructing every material/geometry/instanced
// mesh in this scene. Guarded to once per session (not per renderer) -
// without that, a device that genuinely can't sustain a WebGL context
// at all would reload, lose it again immediately, and loop forever,
// which is worse than just leaving the dead canvas on screen.
const handleWebglContextLost = (label) => (event) =>
{
    event.preventDefault()
    console.error(`[webgl] ${label} context lost`)
    try
    {
        if (sessionStorage.getItem('webglContextLostReloaded'))
            return
        sessionStorage.setItem('webglContextLostReloaded', '1')
    }
    catch (error) { /* private browsing or similar - fall through and reload anyway */ }
    window.location.reload()
}

const LOADING_IDOL_RESOLUTION = 403 // was 336, +20%
// antialias dropped - this preview sits alongside the main scene's own
// WebGLRenderer for the whole loading duration (two real GL contexts
// alive at once), and antialiasing a 403x403 canvas buys almost nothing
// visually while adding GPU cost right in the window where that dual-
// context overlap is already the heaviest load on the device.
const loadingIdolRenderer = new THREE.WebGLRenderer({ canvas: loadingIdolCanvas, alpha: true, antialias: false })
loadingIdolRenderer.setSize(LOADING_IDOL_RESOLUTION, LOADING_IDOL_RESOLUTION, false)
loadingIdolRenderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
loadingIdolCanvas.addEventListener('webglcontextlost', handleWebglContextLost('loading-idol'), false)

loadingIdolScene.add(new THREE.AmbientLight('#ffffff', 2.0))
const loadingIdolKeyLight = new THREE.DirectionalLight('#fff4e0', 2.6)
loadingIdolKeyLight.position.set(2, 3, 4)
loadingIdolScene.add(loadingIdolKeyLight)

// A second light from a different angle - a single light on a smooth
// metal surface only ever produces one clean highlight; a rim/fill light
// from elsewhere gives it a second one, which is what actually starts to
// read as "catching the light" rather than a single static shine spot.
const loadingIdolRimLight = new THREE.DirectionalLight('#ffe9b0', 1.6)
loadingIdolRimLight.position.set(-3, 1.5, -2)
loadingIdolScene.add(loadingIdolRimLight)

// Procedural sparkle/glitter normal map - no environment map in this tiny
// preview scene for a pure metal material to reflect (which is what
// usually sells "shiny metal"), so this fakes it instead: a flat tangent-
// space normal (128,128,255) perturbed by small per-pixel random noise,
// tiled in a fine repeat. That noise breaks what would otherwise be one
// single smooth specular highlight into many tiny twinkling glints as the
// statue rotates under the two lights above - reading as "glittering"
// rather than a single dull, flat gold color.
const createGoldGlitterNormalTexture = () =>
{
    const size = 256
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')
    const imageData = ctx.createImageData(size, size)
    for (let i = 0; i < imageData.data.length; i += 4)
    {
        imageData.data[i] = 128 + (Math.random() - 0.5) * 70     // normal.x
        imageData.data[i + 1] = 128 + (Math.random() - 0.5) * 70 // normal.y
        imageData.data[i + 2] = 255                               // normal.z (mostly outward-facing)
        imageData.data[i + 3] = 255
    }
    ctx.putImageData(imageData, 0, 0)

    const texture = new THREE.CanvasTexture(canvas)
    texture.wrapS = THREE.RepeatWrapping
    texture.wrapT = THREE.RepeatWrapping
    // Tiled small - fine sparkle grain across the statue's surface rather
    // than a few large, obviously-repeated blobs
    texture.repeat.set(24, 24)
    return texture
}

// Rich metallic gold - high metalness + low roughness for a proper shine
// (not the statue's own original flat, non-metallic orange material),
// with the glitter normal map above layered on top at a modest scale so
// it reads as texture/sparkle rather than actual bumpy geometry.
const goldIdolMaterial = new THREE.MeshStandardMaterial({
    color: '#ffdd33', // was '#ffd700' - pushed further toward yellow, less orange
    metalness: 0.75, // was 0.85 - metals go pure black wherever light doesn't directly hit (no env map here to fill that in); lower metalness keeps some diffuse color showing through instead
    roughness: 0.22,
    // A slight warm self-glow, NOT lighting-driven - keeps the darkest
    // parts of the statue (its own shadow side) reading as a dim gold
    // rather than fading to flat black.
    emissive: '#4a3300',
    emissiveIntensity: 0.18,
    normalMap: createGoldGlitterNormalTexture(),
    normalScale: new THREE.Vector2(0.35, 0.35)
})

// Silver/chrome for the Circle specifically - its own distinct material
// (not sharing goldIdolMaterial with the statue), a bit shinier/cooler,
// reusing the same glitter normal map for a consistent sparkle style.
const silverCircleMaterial = new THREE.MeshStandardMaterial({
    color: '#cfd4d9',
    metalness: 0.85,
    roughness: 0.16,
    emissive: '#2a2e33',
    emissiveIntensity: 0.15,
    normalMap: createGoldGlitterNormalTexture(),
    normalScale: new THREE.Vector2(0.35, 0.35)
})

let loadingIdolModel = null
let loadingIdolActive = true
// The GLB's own "Circle" object (a ring around the statue) - counter-
// rotated in the render loop below so it visibly spins the opposite way
// from the statue itself rather than just rotating along with it as an
// ordinary child would. Its OWN authored orientation (confirmed directly
// against the GLB: a fixed -90 degree tilt about X, standing it up rather
// than lying flat) is captured once at load time and kept as a fixed base
// - the counter-spin is layered on top of that as a pure rotation about
// the world/parent Y axis (see the quaternion composition in the render
// loop), not applied by writing straight to .rotation.y, which would
// recompute all 3 Euler axes together and fight the existing tilt instead
// of spinning cleanly around vertical.
let loadingIdolCircle = null
let loadingIdolCircleBaseQuaternion = null

// Shared by both GLTFLoader instances in this file (this one-off idol
// loader, and the main scene's gltfLoader further down) - every
// static/models/*.glb was re-exported through gltf-transform's draco
// command, so GLTFLoader needs this wired in just to decode them, not
// only for the size win. Draco compresses mesh geometry only, not
// embedded textures - GedeeNadiu_Statue.glb (pure geometry, no
// textures) shrank ~95% from it; Building_surface.glb/Floor.glb
// (mostly embedded texture bytes) only shrank modestly, which is
// expected, not a sign compression didn't apply.
const dracoLoader = new DRACOLoader()
dracoLoader.setDecoderPath('./draco/')

const loadingIdolGltfLoader = new GLTFLoader()
loadingIdolGltfLoader.setDRACOLoader(dracoLoader)
loadingIdolGltfLoader.load(
    './models/GedeeNadiu_Statue.glb',
    (gltf) =>
    {
        loadingIdolModel = gltf.scene

        // Center + scale to a consistent on-screen size regardless of the
        // model's own raw dimensions/origin - same reasoning as the house
        // model's own real-world scaling further down this file.
        const box = new THREE.Box3().setFromObject(loadingIdolModel)
        const size = box.getSize(new THREE.Vector3())
        const center = box.getCenter(new THREE.Vector3())
        const targetHeight = 2
        const scale = size.y > 0 ? targetHeight / size.y : 1
        loadingIdolModel.scale.setScalar(scale)
        loadingIdolModel.position.set(-center.x * scale, -center.y * scale, -center.z * scale)
        // Starts already facing the camera (see IDOL_FRONT_ROTATION_OFFSET
        // above), not at the raw, unrotated orientation the GLB itself
        // happens to load with.
        loadingIdolModel.rotation.y = IDOL_FRONT_ROTATION_OFFSET

        // Swap the statue's own original material (a flat, non-metallic
        // orange - see the GLB forensics from earlier in this project) for
        // the gold/glitter one set up above - except "Circle", which gets
        // its own silver material instead, so the ring reads as a
        // distinct chrome accent rather than just another gold surface.
        loadingIdolModel.traverse((child) =>
        {
            if (child.isMesh)
                child.material = child.name === 'Circle' ? silverCircleMaterial : goldIdolMaterial
        })

        loadingIdolScene.add(loadingIdolModel)
        loadingIdolCircle = loadingIdolModel.getObjectByName('Circle')
        if (loadingIdolCircle)
            loadingIdolCircle.visible = false
        if (loadingIdolCircle)
            loadingIdolCircleBaseQuaternion = loadingIdolCircle.quaternion.clone()

        // Reframe the camera against the model's ACTUAL bounding sphere
        // (not a fixed guessed distance) now that it's loaded, aimed at
        // its true center - (0,0,0), since the recentring above already
        // moved the model's own bounding box center there, not the (0,1,0)
        // the camera was originally pointed at (that mismatch is what was
        // pushing the model low/off-center in frame and clipping its base
        // - the lookAt target and the model's real center didn't agree).
        // Sized off the bounding SPHERE specifically (not just height) so
        // whatever the widest point is - shoulders, base, at any rotation
        // angle - stays inside frame throughout the full spin, not just
        // face-on.
        const scaledBox = new THREE.Box3().setFromObject(loadingIdolModel)
        const boundingSphere = scaledBox.getBoundingSphere(new THREE.Sphere())
        const fovRadians = THREE.MathUtils.degToRad(loadingIdolCamera.fov)
        const fitDistance = (boundingSphere.radius / Math.sin(fovRadians / 2)) * 1.15 // 15% margin so it doesn't touch the frame edges

        loadingIdolCamera.position.set(0, 0, fitDistance)
        loadingIdolCamera.lookAt(0, 0, 0)
    },
    undefined,
    (error) =>
    {
        console.error('[loading idol] failed to load ./models/GedeeNadiu_Statue.glb - loading screen will show without it', error)
    }
)

const loadingIdolTimer = new Timer()

// Parsing the main house GLB (large, synchronous) can briefly block the
// main thread - JS is single-threaded, so no amount of independence in
// this render loop can keep frames actually rendering DURING that block.
// What it can fix is what happens right after: without a cap, the elapsed
// delta across that stall is huge, so the very next frame spins the idol
// forward in one big instant jump - which is what actually reads as a
// "jerk" once things resume, not the block itself. Clamping delta turns
// that into a smaller, still-smooth step instead, at the cost of very
// slightly "losing" some spin time during a bad stall - an easy trade for
// no visible jump.
const LOADING_IDOL_MAX_DELTA = 1 / 30

// Reused scratch objects for the Circle's counter-rotation (see the
// render loop below) - avoids allocating a new Quaternion/Vector3 every
// frame.
const loadingIdolWorldUpAxis = new THREE.Vector3(0, 1, 0)
const loadingIdolCircleCounterRotation = new THREE.Quaternion()

// How long the idol itself stays visible/rotating after load, overlapping
// the intro camera animation once that starts (see loadingManager.onLoad
// further down) - a MINIMUM, not an exact fade time; see the wrap-based
// trigger below for why.
const IDOL_HOLD_MS = 500
const IDOL_FADE_MS = 600 // must match .loading-idol-wrap's opacity transition duration in style.css

// Fixed, independent pace (not derived from IDOL_HOLD_MS, which has since
// been tuned well below what a full turn actually takes) - was (2*PI)/2
// (~3.14 rad/s, a full turn in 2s), slowed down further here. Since the
// hold is already shorter than one full turn either way, the wrap-based
// trigger below just waits for the current turn to finish regardless -
// slowing this down further simply makes that wait (and so the actual
// on-screen overlap) longer too, which is expected/intended.
const IDOL_ROTATION_SPEED = 2.6 // radians/second - a full turn in ~2.4s (was 2.0/~3.1s)

// The wrap-based "always front-facing at fade" logic below only actually
// works if rotation.y = 0 IS front-facing to begin with - it wasn't: this
// statue's own raw orientation (however the scan/export happened to set
// its rotation.y = 0) doesn't face the camera at all, so snapping to "0"
// was still snapping to a side pose. This offset corrects that - applied
// once as the model's starting rotation (see the load callback) and used
// as the "zero point" for the wrap math below instead of literal 0.
// Confirmed by eye at -90 degrees (a quarter turn).
const IDOL_FRONT_ROTATION_OFFSET = -Math.PI / 2

// LOADING_IDOL_MAX_DELTA above deliberately clamps (i.e. discards) time
// during a bad main-thread stall, so the model's ACTUAL accumulated
// rotation can fall behind where a plain real-time
// setTimeout(..., IDOL_HOLD_MS) would assume it is - which would show up
// as "back is facing front" if the fade were triggered directly off that
// timer. Fixed by triggering the fade off the rotation itself wrapping
// past a multiple of 360 degrees (always front-facing, by definition)
// instead - loadingIdolReadyToFade (set true by a plain timeout once
// IDOL_HOLD_MS has genuinely passed) only gates WHEN that wrap is allowed
// to count, so a bad stall can very slightly push the actual fade past the
// nominal cap (waiting for that next completed turn) rather than ever
// showing an off-angle idol - a rare, small overshoot traded for never
// looking wrong.
let loadingIdolReadyToFade = false
let loadingIdolFadeTriggered = false
// Always-increasing turn-progress accumulator, tracked separately from the
// model's actual rendered rotation.y (see renderLoadingIdol below) - the
// wrap/"just completed a full turn" detection needs a value that only
// ever goes up, but the model itself needs to visually spin clockwise
// (i.e. rotation.y decreasing) rather than counter-clockwise. Decoupling
// the two lets the direction be flipped without breaking the wrap math.
let loadingIdolProgress = 0

const triggerLoadingIdolFade = () =>
{
    loadingIdolFadeTriggered = true
    loadingIdolWrap.classList.add('loading-idol-wrap--hidden')

    // Stop and free the idol preview's own render loop/GL context only
    // once its own fade has actually finished, not before - stopping it
    // any earlier would freeze the rotation mid-fade instead of it reading
    // as spinning smoothly right up until it's gone.
    window.setTimeout(() =>
    {
        loadingIdolActive = false
        loadingIdolRenderer.dispose()
    }, IDOL_FADE_MS)
}

const renderLoadingIdol = () =>
{
    if (!loadingIdolActive)
        return

    loadingIdolTimer.update()
    const delta = Math.min(loadingIdolTimer.getDelta(), LOADING_IDOL_MAX_DELTA)

    if (loadingIdolModel)
    {
        // Progress only ever increases (needed for the wrap math below to
        // work at all) - the model's actual rotation.y is then derived
        // from it as IDOL_FRONT_ROTATION_OFFSET MINUS progress, which is
        // what makes it spin clockwise rather than counter-clockwise; "a
        // full turn" is still measured directly off progress itself, so
        // that detection is completely unaffected by the sign flip.
        const fullTurns = Math.floor(loadingIdolProgress / (Math.PI * 2))
        loadingIdolProgress += delta * IDOL_ROTATION_SPEED
        loadingIdolModel.rotation.y = IDOL_FRONT_ROTATION_OFFSET - loadingIdolProgress
        const justWrapped = Math.floor(loadingIdolProgress / (Math.PI * 2)) > fullTurns

        if (justWrapped && loadingIdolReadyToFade && !loadingIdolFadeTriggered)
        {
            // Snap exactly to front-on (the offset, not 0) and reset
            // progress back to 0 - rather than leaving this frame's small
            // overshoot past the exact wrap point in place, and rather
            // than letting the raw value climb unbounded turn after turn.
            loadingIdolProgress = 0
            loadingIdolModel.rotation.y = IDOL_FRONT_ROTATION_OFFSET
            triggerLoadingIdolFade()
        }

        // Counter-rotate the Circle - it's a child of loadingIdolModel, so
        // it already inherits the parent's own spin. Cancelling that and
        // applying an equal spin the other way needs a rotation of -2x the
        // parent's current value, about the world/parent Y axis specifically
        // - composed with (not overwriting) the circle's own fixed tilt via
        // quaternion multiplication, so the tilt survives untouched.
        // Computed fresh off the parent's current rotation every frame (not
        // accumulated separately), so it can't drift out of sync with it,
        // including right through the front-facing snap above.
        if (loadingIdolCircle && loadingIdolCircleBaseQuaternion)
        {
            loadingIdolCircleCounterRotation.setFromAxisAngle(loadingIdolWorldUpAxis, -2 * loadingIdolModel.rotation.y)
            loadingIdolCircle.quaternion.copy(loadingIdolCircleCounterRotation).multiply(loadingIdolCircleBaseQuaternion)
        }
    }

    loadingIdolRenderer.render(loadingIdolScene, loadingIdolCamera)
    window.requestAnimationFrame(renderLoadingIdol)
}
renderLoadingIdol()

const loadingManager = new THREE.LoadingManager()

loadingManager.onProgress = (url, itemsLoaded, itemsTotal) =>
{
    const percent = Math.round((itemsLoaded / itemsTotal) * 100)
    loadingPercentageText.textContent = `${percent}%`
}

loadingManager.onLoad = () =>
{
    loadingPercentageText.textContent = '100%'

    // Snap the camera/fog/cloud state for the intro's opening shot right
    // now, while it's still hidden behind the (still opaque) loading
    // screen - an aerial view over the building, "inside" a bank of
    // cloud. Nothing here is visible yet, so it can happen instantly with
    // no easing. See prepareIntroAerialView() near the bottom of this file
    // - defined later, but not called until this async callback fires, by
    // which point the rest of the script (camera, controls, fog, clouds)
    // has long since finished running.
    prepareIntroAerialView()

    // Hold on the 100% state for a beat so it's actually visible, rather
    // than jumping straight from some lower percentage to gone. This
    // governs the opaque background + bar + percentage only - the idol
    // (a separate element, see below) stays on screen well past this on
    // its own longer timing.
    const HOLD_MS = 300
    const FADE_MS = 600 // must match .loading-screen's opacity transition duration in style.css
    window.setTimeout(() =>
    {
        loadingScreen.classList.add('loading-screen--hidden')
        // Site header/hero text reveal has moved to finishIntroTravel()
        // further down this file - they shouldn't appear until the whole
        // intro camera sequence has actually finished, not just once the
        // loading screen itself has faded (which is when the intro travel
        // is only just starting).
    }, HOLD_MS)

    // Start the actual eased camera travel exactly once the opaque
    // background has fully faded out - otherwise the aerial/cloudy opening
    // shot set up above would already be mid-travel by the time the user
    // can actually see it, instead of reading as a clean first frame. The
    // idol is still visible at this point (see its own timing below) and
    // keeps rotating on top of it - a deliberate overlap, not a bug.
    window.setTimeout(beginIntroTravel, HOLD_MS + FADE_MS)

    // The percentage text has done its job the moment loading hits 100% -
    // rather than staying around for the idol's whole extended overlap
    // below, it fades out on its own, much shorter timing, leaving just
    // the statue (+ its Circle) alone rotating over the intro for the
    // rest of the overlap.
    const PERCENTAGE_HOLD_MS = 200 // brief pause so "100%" is actually seen for an instant before fading
    const PERCENTAGE_FADE_MS = 400 // must match .loading-percentage's own opacity transition duration in style.css
    window.setTimeout(() =>
    {
        loadingPercentageText.classList.add('loading-percentage--hidden')
    }, PERCENTAGE_HOLD_MS)

    // Fully hidden (not just invisible) once the fade has actually
    // finished, same reasoning as .loading-screen--hidden's own
    // visibility:hidden - nothing left behind to (even invisibly) intercept
    // interaction.
    window.setTimeout(() =>
    {
        loadingPercentageText.style.visibility = 'hidden'
    }, PERCENTAGE_HOLD_MS + PERCENTAGE_FADE_MS)

    // The idol itself gets a much longer, separate hold (IDOL_HOLD_MS,
    // declared up near its render loop) - sized to comfortably cover the
    // intro's opening "cloud" moment (the camera starts deep in dense fog/
    // cloud puffs and doesn't meaningfully clear until a few seconds in,
    // even though the puffs themselves animate across the full
    // INTRO_TRAVEL_DURATION). Starts overlapping the intro camera travel
    // above (which begins well before this, at HOLD_MS + FADE_MS)
    // immediately, not after this hold - the hold only controls how long
    // the idol itself stays on top of it. This just marks the hold as
    // satisfied - renderLoadingIdol() (see above) is what actually decides
    // the exact moment to fade, waiting for the model to be front-facing
    // again rather than cutting off wherever real elapsed time happens to
    // land it.
    window.setTimeout(() =>
    {
        loadingIdolReadyToFade = true
    }, IDOL_HOLD_MS)
}

loadingManager.onError = (url) =>
{
    console.error('Error loading asset:', url)
}

/**
 * House
 */

/**
 * Textures
 */
const textureLoader = new THREE.TextureLoader(loadingManager)

// Flat white 1x1 fallback texture - used to give GLB meshes that ship
// without their own color map an explicit texture instead of an untextured
// default (see the house model's traverse below).
const whiteTexture = new THREE.DataTexture(new Uint8Array([255, 255, 255, 255]), 1, 1, THREE.RGBAFormat)
whiteTexture.colorSpace = THREE.SRGBColorSpace
whiteTexture.needsUpdate = true

// Grey to pair with the fallback above - GLTFLoader gives every material-
// less primitive in the whole model one single shared default material,
// so recoloring it here affects all of them at once (not just one mesh).
// Checked what the OTHER "real" cube materials in this GLB actually use
// (Material.013/.014/.015/.017) - they're all this exact same flat grey
// (baseColorFactor [0.8, 0.8, 0.8]), so this makes every currently-white
// materialless prop (including most of "Cube.223", which turned out to be
// one object with ~220 sub-pieces, only one of which had a real material)
// match the rest of the built environment instead of standing out white.
const DEFAULT_MATERIAL_COLOR = 0.8

//Plane (floor material's color map)
// Floor.glb's own material/UVs are kept as-is (see its traverse below) -
// only the base color texture itself is swapped for this external file.
// flipY is set to false to match glTF's UV convention (V=0 at the top),
// which is what the mesh's own UVs were authored against - loading it with
// THREE.TextureLoader's default flipY (true, the regular WebGL/image
// convention) is what caused the mirrored/misaligned look before.
const planeColorTexture = textureLoader.load('./plane/Base%20Color.png')
planeColorTexture.colorSpace = THREE.SRGBColorSpace
planeColorTexture.flipY = false
// Same mipmap-corruption risk already hit (and fixed the same way) for
// the cloud/bird/badge canvas textures further down - there it only
// ever showed up on Safari, but a corrupted-colored-static report on a
// Pixel 10 XL that persisted even after downscaling this texture from
// 4096 to 2048 (so not a memory-size issue) points at the same kind of
// driver-level mipmap generation bug, just on a different GPU/driver
// this time. Costs the anisotropic filtering just below its actual
// effect (it needs mipmaps to do anything) - some aliasing/shimmer on
// this ground plane at distance is the trade-off, same as the
// logarithmicDepthBuffer one, for not rendering as corrupted garbage.
planeColorTexture.generateMipmaps = false
planeColorTexture.minFilter = THREE.LinearFilter
planeColorTexture.magFilter = THREE.LinearFilter

// Alpha map - applied as-is (no inversion), so it's transparent exactly
// where its own pixel values say transparent (black = transparent, white =
// opaque - the standard alphaMap convention, taken at face value this time).
const planeAlphaTexture = textureLoader.load('./plane/alpha.png')
planeAlphaTexture.flipY = false
planeAlphaTexture.generateMipmaps = false
planeAlphaTexture.minFilter = THREE.LinearFilter
planeAlphaTexture.magFilter = THREE.LinearFilter

// Tree pack textures - extracted copies of the same images already
// embedded in Building_surface.glb, placed by hand in static/trees/textures/
// so they can be wired up explicitly here (matching the plane textures'
// pattern above) instead of relying on GLTFLoader's own automatic handling
// of the embedded copies. baseColor is the only sRGB-encoded map - normal
// and metallicRoughness are non-color data and must stay linear, which is
// also GLTFLoader's own convention for these same texture types.
const loadTreeTexture = (filename, isColor) =>
{
    const texture = textureLoader.load(`./trees/textures/${filename}`)
    // Matches the floor's own textures above - these replace maps used by
    // glTF mesh UVs (V=0 at the top), not THREE.TextureLoader's default
    // (V=0 at the bottom) image convention.
    texture.flipY = false
    if (isColor)
        texture.colorSpace = THREE.SRGBColorSpace
    return texture
}

// Material name (normalized - see stripMaterialNameSuffix below, since
// the GLB has duplicate-suffixed variants like "Tree_Branches_01.001") to
// its texture set. metallicRoughness is one glTF-packed texture read for
// both roughnessMap and metalnessMap; Rocks' same file doubles as its
// occlusion map too (per the GLB's own material - occlusionTexture and
// metallicRoughnessTexture both point at the same image there).
const treeTextureSets = {
    Tree_Trunk_01: {
        map: loadTreeTexture('Tree_Trunk_01_baseColor.png', true),
        normalMap: loadTreeTexture('Tree_Trunk_01_normal.png', false),
        metallicRoughness: loadTreeTexture('Tree_Trunk_01_metallicRoughness.png', false)
    },
    Tree_Trunk_02: {
        map: loadTreeTexture('Tree_Trunk_02_baseColor.png', true),
        normalMap: loadTreeTexture('Tree_Trunk_02_normal.png', false),
        metallicRoughness: loadTreeTexture('Tree_Trunk_02_metallicRoughness.png', false)
    },
    Tree_Branches_01: {
        map: loadTreeTexture('Tree_Branches_01_baseColor.png', true),
        normalMap: loadTreeTexture('Tree_Branches_01_normal.png', false),
        metallicRoughness: loadTreeTexture('Tree_Branches_01_metallicRoughness.png', false)
    },
    Tree_Branches_02: {
        map: loadTreeTexture('Tree_Branches_02_baseColor.png', true),
        normalMap: loadTreeTexture('Tree_Branches_02_normal.png', false)
        // No metallicRoughness file for this one - the GLB's own material
        // never had a metallicRoughnessTexture either, just a flat
        // roughnessFactor, so there's nothing to extract/replace.
    },
    Background_Tree_Atlas: {
        map: loadTreeTexture('Background_Tree_Atlas_baseColor.png', true),
        normalMap: loadTreeTexture('Background_Tree_Atlas_normal.png', false),
        metallicRoughness: loadTreeTexture('Background_Tree_Atlas_metallicRoughness.png', false)
    },
    Rocks: {
        map: loadTreeTexture('Rocks_baseColor.png', true),
        normalMap: loadTreeTexture('Rocks_normal.png', false),
        metallicRoughness: loadTreeTexture('Rocks_metallicRoughness.png', false),
        aoMap: loadTreeTexture('Rocks_metallicRoughness.png', false)
    }
}

// Strips Blender's duplicate-material suffix (".001", ".002", ...) so
// e.g. "Tree_Branches_01.001" matches the "Tree_Branches_01" texture set
// above instead of finding nothing.
const stripMaterialNameSuffix = (name) => name.replace(/\.\d+$/, '')

//House container
const house = new THREE.Group()
scene.add(house)

// Filled in once the house model below has loaded and its real-world size
// is known - used by the intro camera animation (further down this file)
// to frame its opening aerial shot against the building's actual
// footprint instead of a guessed distance.
const buildingCenter = new THREE.Vector3()
let introBuildingFootprint = 60 // sane fallback; overwritten before loadingManager.onLoad can possibly fire

// Building markers pop up out of the ground together once the intro
// camera sequence finishes (see finishIntroTravel() further down) - each
// starts fully transparent/scaled to nothing, sitting at the floor, at
// creation (see the marker setup in the house model's load callback
// below) and is animated up from there by tick(), rather than being
// visible right away underneath the intro.
let markersRevealTriggered = false
let markersRevealStartTime = 0
const markerFadeRefs = [] // { line, label, labelTargetScale, localFloorY, topY, revealDelay, revealDone }
// { sprite, text, normalTexture, hoverTexture } per marker - module scope
// (populated inside the house model's load callback further down, same
// reasoning as markerFadeRefs above) so both regenerateMarkerLabels
// (style GUI changes) and tick()'s hover swap can reach it.
const markerLabels = []
// Badge color while a marker is hovered - matches .hero-cta's own
// background color in style.css, so the badge reads as "this is
// clickable" using the same visual language as the site's one other
// button.
const MARKER_HOVER_BADGE_COLOR = '#000000'
const MARKER_HOVER_FONT_COLOR = '#ffffff'
let previousHoveredMarkerIndex = -1
// Per-marker overrides for the zoomed-in camera's end position, look-at
// target, and field of view - populated once the markers themselves
// exist (see the house model's load callback further down) and read
// directly by flyCameraToMarker/applyMarkerOverrideToCamera, defined at
// module scope like this so both sides can see them despite that
// population happening inside a nested callback. Position and target are
// world-space Vector3s; fov is wrapped in its own { fov } object per
// marker (rather than a bare number in the array) since lil-gui's
// .add(object, property) needs an actual object property to bind to.
const markerCameraOverrides = []
const markerTargetOverrides = []
const markerFovOverrides = []
const MARKER_REVEAL_DURATION = 1.1 // seconds, per marker, once its own randomized delay has elapsed
const MARKER_REVEAL_RANDOM_SPREAD = 1.2 // seconds - each marker's own revealDelay is random within this range, not a fixed per-marker sequence, so the 12 pop up in no particular order rather than a predictable 1-2-3 cascade

// Hover/click on the marker badges - raycasts against markerFadeRefs[].
// label (the sprite, not the line - a much more forgiving click target).
// Hover (mouse-only - see the pointermove listener) is read fresh every
// frame in tick() purely for the cursor/scale-bump feedback; the actual
// click below does its own separate raycast at the exact click position
// (event.clientX/clientY, populated correctly for a tap too) instead of
// trusting whatever hoveredMarkerIndex last settled on - touch devices
// never fire pointermove before a tap, so that would stay at -1 forever
// and a marker would never register as clicked on mobile. "click" itself
// (rather than pointerdown/up) is deliberate: browsers already suppress
// it after a drag/pan/pinch gesture on their own, and it's the one event
// guaranteed to fire consistently for both a mouse click and a touch tap
// everywhere, so there's no need to reimplement that drag-vs-tap
// distinction by hand.
const markerRaycaster = new THREE.Raycaster()
const markerPointerNDC = new THREE.Vector2(-Infinity, -Infinity)
let hoveredMarkerIndex = -1

window.addEventListener('pointermove', (event) =>
{
    markerPointerNDC.x = (event.clientX / window.innerWidth) * 2 - 1
    markerPointerNDC.y = -(event.clientY / window.innerHeight) * 2 + 1
})

canvas.addEventListener('click', (event) =>
{
    // The circle-nav dial's own backdrop already sits visually on top
    // while it's expanded, but the canvas underneath still receives
    // clicks through anything that isn't blocking pointer-events - this
    // guard matches the hover raycast's own markerInteractionSuspended
    // check above so a marker can't be opened while the dial's doing its
    // own drag-to-rotate gesture.
    if (markerInteractionSuspended)
        return
    const rect = canvas.getBoundingClientRect()
    const clickNDC = new THREE.Vector2(
        ((event.clientX - rect.left) / rect.width) * 2 - 1,
        -((event.clientY - rect.top) / rect.height) * 2 + 1
    )
    markerRaycaster.setFromCamera(clickNDC, camera)
    const revealedLabels = markerFadeRefs.filter((ref) => ref.revealDone).map((ref) => ref.label)
    const hit = markerRaycaster.intersectObjects(revealedLabels, false)[0]
    if (!hit)
        return
    const index = markerFadeRefs.findIndex((ref) => ref.label === hit.object)
    if (index !== -1)
    {
        flyCameraToMarker(index)
        if (isMobileViewport())
            pendingMobileMarkerOverlayIndex = index
        else
            openMarkerOverlay(index)
    }
})

// Standard easeOutBack (easings.net) - a single gentle overshoot past 1
// that settles back, not the repeated oscillating bounce of
// easeOutElastic (that made the rise itself too fast/hidden to actually
// read as "coming up out of the ground" - most of its motion happened in
// one late snap). This one rises smoothly and visibly for the "little
// elastic" pop on each marker's reveal below.
const easeOutBack = (t) =>
{
    if (t <= 0)
        return 0
    if (t >= 1)
        return 1
    const c1 = 1.70158
    const c3 = c1 + 1
    return 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2)
}

// Populated once the house model has loaded (see the Vehicles section
// inside its load callback below) - declared here, at module scope, so
// updateVehicles() (called from tick() further down) can see it too.
const vehicles = []

// Points where two road-path curves physically cross each other, found
// once via findRoadJunctions() (defined further down, called once the
// road curves are built) - also populated inside the Vehicles section,
// also needed by updateVehicles() at module scope.
const vehicleJunctions = []

// Same pattern as vehicles/vehicleJunctions above - populated inside
// the same load callback (see "People" further down, right after
// Vehicles), read every frame by updateStudents()/updateKidsPlaying()
// (both defined near updateVehicles further down, both called from
// tick()).
const students = []
const kidsPlaying = []

// Filled in once personGltfPromise resolves (see createAnimatedPersonMesh,
// near createPersonMesh further down) - personTemplate stays null if the
// model failed to load, which is what tells createPersonOrFallbackMesh to
// use the old procedural figure instead.
let personTemplate = null
let personAnimationClip = null
let personNaturalHeight = 1

//GLB loader (shared by the floor and the house models)
const gltfLoader = new GLTFLoader(loadingManager)
gltfLoader.setDRACOLoader(dracoLoader) // see dracoLoader's own definition, near the loading-idol's GLTFLoader further up

// Building_surface.glb/Floor.glb's embedded textures were re-encoded to
// KTX2/Basis Universal (ETC1S) on top of the Draco geometry compression
// above - this is what actually decodes those, not just a size optimization.
// detectSupport() needs a real WebGLRenderer to query which GPU texture
// formats are actually supported, but the main scene's own `renderer`
// isn't created until much later in this file (long after this loader
// is used) - loadingIdolRenderer (set up earlier, for the loading-screen
// preview) already has a real WebGL context by this point and works
// identically for this purely capability-detection purpose.
const ktx2Loader = new KTX2Loader()
ktx2Loader.setTranscoderPath('./basis/')
ktx2Loader.detectSupport(loadingIdolRenderer)
gltfLoader.setKTX2Loader(ktx2Loader)

//Floor model (GLB)
// Loaded via loadAsync (rather than the load() callback style below) and not
// scaled/added here - it needs the building's footprint (known only once the
// house model below has loaded and been scaled) to size itself against, so
// that work happens in the house model's callback instead.
const floorGltfPromise = gltfLoader.loadAsync('./models/Floor.glb')
    .catch((error) =>
    {
        console.error('Error loading floor model:', error)
        return null
    })

//Kids play area + banyan tree model (GLB) - same deal as the floor above:
// exported from the same shared Blender scene/origin as Building_surface.glb,
// so it's aligned the same way (scaleFactor + the house's own Y lift) once
// the house model below has loaded, rather than here.
const kpaGltfPromise = gltfLoader.loadAsync('./models/baniyan_Tree&KPA.glb')
    .catch((error) =>
    {
        console.error('Error loading kids play area / banyan tree model:', error)
        return null
    })

// Person model (GLB) - Khronos's "RiggedFigure" sample asset (CC-BY 4.0,
// (c) 2017 Cesium - https://github.com/KhronosGroup/glTF-Sample-Assets),
// a plain untextured low-poly mannequin with one in-place walk-cycle
// animation. Used for every walking student and playing kid below, in
// place of the old procedurally-built capsule figures. One load, then
// cloneSkeleton() (NOT Object3D.clone() - a SkinnedMesh's skeleton/bone
// references wouldn't retarget correctly through a plain clone) per
// instance, each with its own AnimationMixer so they don't all step in
// lockstep.
const personGltfPromise = (SHOW_PEOPLE ? gltfLoader.loadAsync('./models/Person.glb') : Promise.resolve(null))
    .catch((error) =>
    {
        console.error('Error loading person model:', error)
        return null
    })
    .then((personGltf) =>
    {
        // Attached here (right next to the load itself) rather than down
        // where students/kids are actually created, so personTemplate is
        // already populated by the time those later .then() callbacks
        // run - same-promise .then() callbacks fire in attachment order,
        // and this one is attached first.
        if (!personGltf)
            return null // createPersonOrFallbackMesh falls back to the procedural figure

        personTemplate = personGltf.scene

        // Despite the root node being named "Z_UP" (and the mesh's own
        // bind-pose POSITION data spanning its height along local Z, not
        // Y), the model renders standing correctly with no corrective
        // rotation - its skeleton's own joint/bind matrices already
        // reorient the skinned result into this scene's Y-up convention.
        // Confirmed directly: an isolated test (src/person-test.html,
        // temporary - safe to delete) measuring the loaded bounding box
        // showed height already fully spanning Y with no rotation
        // applied, and a clearly wrong (partly below-ground, squashed)
        // box once a -90°-X "fix" was added - so this comment is here
        // specifically to stop a future "but Z_UP..." re-fix.
        personAnimationClip = personGltf.animations[0]

        // Measured from the template's own rest pose (before any
        // AnimationMixer has touched it) - same idea as houseModel's own
        // naturalHeight further down, just for this model instead.
        const box = new THREE.Box3().setFromObject(personTemplate)
        personNaturalHeight = box.max.y - box.min.y || 1

        return personGltf
    })

/**
 * Building markers
 * 12 numbered pins ("1".."12") ringed around the building, each a thin line
 * up to a small badge - same resting pin style the single "Building" label
 * used to have, just numbered instead of named, and with no hover glow
 * ("flow") effect anymore. The actual marker meshes are created once the
 * house model has loaded and its real-world bounds are known (see the
 * house model's load callback below) - this section only sets up the
 * shared label-texture piece.
 */

// Numbered pill badge - light rounded background, dark text, matching the
// reference's white number badges - reused once per pin below, passing
// "1".."12" as the text. badgeColor/fontSize/bold are shared across all 12
// (see the "Marker Position" GUI folder) rather than per-marker - re-called
// for every marker whenever any of the three changes.
const createLabelTexture = (text, { badgeColor = '#ffffff', fontColor = '#1b2733', fontSize = 48, bold = true } = {}) =>
{
    const paddingX = 32
    const height = 96
    const canvas = document.createElement('canvas')
    const ctx = canvas.getContext('2d')
    const font = `${bold ? 'bold ' : ''}${fontSize}px sans-serif`
    ctx.font = font
    const textWidth = ctx.measureText(text).width
    canvas.width = textWidth + paddingX * 2
    canvas.height = height

    // Resizing the canvas above resets the 2D context state, so the font
    // has to be re-applied before it's used to actually draw.
    ctx.font = font
    ctx.textBaseline = 'middle'
    ctx.textAlign = 'center'

    const radius = height / 2
    ctx.fillStyle = badgeColor
    ctx.beginPath()
    ctx.moveTo(radius, 0)
    ctx.lineTo(canvas.width - radius, 0)
    ctx.arc(canvas.width - radius, radius, radius, -Math.PI / 2, Math.PI / 2)
    ctx.lineTo(radius, height)
    ctx.arc(radius, radius, radius, Math.PI / 2, -Math.PI / 2)
    ctx.closePath()
    ctx.fill()

    ctx.fillStyle = fontColor
    ctx.fillText(text, canvas.width / 2, height / 2 + 2)

    const texture = new THREE.CanvasTexture(canvas)
    // Same Safari mipmap-corruption risk as the cloud/bird textures -
    // this badge is just a flat sprite at a fixed small size, so it
    // never needed mipmapping to begin with.
    texture.generateMipmaps = false
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    return { texture, aspect: canvas.width / canvas.height }
}

//House model (GLB)
gltfLoader.load(
    './models/Building_surface.glb',
    (gltf) =>
    {
        const houseModel = gltf.scene

        // Scale to a real-world height instead of a guessed number: treat
        // this as a modest 3-storey building (~3m per floor) and derive the
        // scale from the model's own bounding box, whatever its raw size is.
        const floorsHeight = 3
        const metersPerFloor = 3
        const targetHeight = floorsHeight * metersPerFloor

        // Position is left exactly as authored in the GLB, aside from a
        // small manual lift on Y - only scale is derived, to reach the
        // real-world target height above.
        houseModel.scale.set(1, 1, 1)

        // All vegetation is grouped under one "All trees" node - detached
        // here (and reattached right after) so it can't affect these two
        // measurements. Box3.setFromObject has no "exclude this subtree"
        // option and ignores .visible entirely, so temporarily removing it
        // from the scene graph is the only way to measure "just the
        // building".
        const treesNode = houseModel.getObjectByName('All trees')
        const treesParent = treesNode ? treesNode.parent : null
        if (treesNode)
            treesParent.remove(treesNode)

        const naturalBox = new THREE.Box3().setFromObject(houseModel)
        const naturalHeight = naturalBox.max.y - naturalBox.min.y
        const scaleFactor = naturalHeight > 0 ? targetHeight / naturalHeight : 1
        houseModel.scale.setScalar(scaleFactor)
        houseModel.position.y = 0.5

        const scaledBox = new THREE.Box3().setFromObject(houseModel)

        if (treesNode)
            treesParent.add(treesNode)

        // Used to range this GUI folder's sliders and the Floor folder's
        // below, so their bounds land somewhere useful for this building's
        // actual size instead of an arbitrary fixed number.
        const buildingWidth = scaledBox.max.x - scaledBox.min.x
        const buildingDepth = scaledBox.max.z - scaledBox.min.z
        const buildingFootprint = Math.max(buildingWidth, buildingDepth)

        // Hand the building's real center/footprint to the module-level
        // copies the intro camera animation reads (see near the bottom of
        // this file) - it can't compute these itself since it only runs
        // later, once loadingManager.onLoad fires.
        buildingCenter.copy(scaledBox.getCenter(new THREE.Vector3()))
        introBuildingFootprint = buildingFootprint

        // Building GUI controls - position only (scale is derived above
        // from the real-world target height, not something to hand-tune).
        const buildingFolder = gui.addFolder('Building').close()
        buildingFolder.add(houseModel.position, 'x').min(-buildingFootprint).max(buildingFootprint).step(0.1).name('position X')
        buildingFolder.add(houseModel.position, 'y').min(-buildingFootprint * 0.1).max(buildingFootprint * 0.1).step(0.01).name('position Y')
        buildingFolder.add(houseModel.position, 'z').min(-buildingFootprint).max(buildingFootprint).step(0.1).name('position Z')

        // Meshes smaller than this (bolts, railing posts, and the like -
        // the GLB has dozens of them) skip the shadow-casting pass. Their
        // shadow contribution is imperceptible at this size, but each one
        // still costs a full extra draw call in the shadow depth pass
        // otherwise - with ~430 meshes in this model that adds up.
        const SMALL_MESH_SHADOW_THRESHOLD = 0.1 // meters, measured after real-world scaling
        const meshWorldSize = new THREE.Vector3() // reused scratch vector, avoids allocating one per mesh below
        const meshWorldBox = new THREE.Box3() // reused scratch box, same reason

        // Tracks the highest actual roof surface seen during this same
        // traverse (reusing its per-mesh Box3 rather than measuring twice),
        // for the Building marker's anchor point below - this GLB turns out
        // to be a whole multi-building complex, so the overall bounding
        // box's centre (the obvious first guess) can land in open space
        // between buildings instead of on a roof. Trees are excluded since
        // they're often taller than the buildings themselves and would
        // otherwise win.
        let tallestRoofY = -Infinity
        let tallestRoofCenter = null

        // Collected below, used by the "Vegetation tint" GUI folder
        // further down - covers trunk/bark too, not just foliage, since
        // it's meant as a general color/brightness nudge for the whole
        // tree pack against the ground's own palette.
        const treeTintMaterials = new Set()

        // One cached shadow-only depth material per unique foliage
        // material (keyed by material.uuid), used just below - avoids
        // creating a duplicate for every single leaf-card mesh that
        // happens to share the same source material.
        const foliageShadowDepthMaterials = new Map()

        // "Cube*" backdrop buildings (moved up here, ahead of the main
        // traverse below, so that traverse can also use it - see where it
        // skips these further down) - matched against the mesh's own name
        // OR any ancestor's (GLTFLoader can restructure a node with
        // multiple primitives into a wrapping Group carrying the original
        // name, with individually-named or unnamed Mesh children
        // underneath - matching only child.name would miss that case).
        // The pattern itself is "Cube" + digits with NO separator, not
        // "Cube" + a word boundary - confirmed live that GLTFLoader strips
        // the dot out of names like "Cube.003" while parsing (it uses dots
        // as path separators in its own animation-track property binding
        // syntax), leaving "Cube003" at runtime - a `\b` word-boundary
        // check right after "Cube" never matches there, since digits
        // count as word characters too.
        const isCityBlockObject = (object) =>
        {
            let current = object
            while (current && current !== houseModel)
            {
                if (/^Cube\d*$/.test(current.name || ''))
                    return true
                current = current.parent
            }
            return false
        }

        houseModel.traverse((child) =>
        {
            if (child.isMesh)
            {
                meshWorldBox.setFromObject(child)
                meshWorldBox.getSize(meshWorldSize)
                const isTinyDecoration = meshWorldSize.length() < SMALL_MESH_SHADOW_THRESHOLD

                if (meshWorldBox.max.y > tallestRoofY && !/tree|leaves/i.test(child.name))
                {
                    tallestRoofY = meshWorldBox.max.y
                    tallestRoofCenter = meshWorldBox.getCenter(new THREE.Vector3())
                }

                // Building casts a shadow onto the floor, and receives shadow
                // from its own other meshes too (self-shadowing) - except
                // tiny decorative meshes, which only skip casting (still
                // receive, in case something bigger shadows them).
                child.castShadow = !isTinyDecoration
                child.receiveShadow = true

                // Some meshes in the GLB have a material but no color map of
                // their own (left unassigned in the export) - give those an
                // explicit flat white texture instead of leaving them on
                // whatever untextured default the loader/material falls back to.
                // "Cube*" backdrop buildings are skipped here - they keep
                // their own real GLB material untouched (see isCityBlockObject
                // above and where it's used again further down), whatever
                // that turns out to be, instead of this fallback assuming
                // every mapless material is an unassigned/default one.
                const materials = Array.isArray(child.material) ? child.material : [child.material]
                for (const material of materials)
                {
                    if (material && !material.map && !isCityBlockObject(child))
                    {
                        material.map = whiteTexture
                        material.color.setScalar(DEFAULT_MATERIAL_COLOR)
                        material.needsUpdate = true
                    }

                    // Swap in the hand-placed external tree textures
                    // (static/trees/textures/) for whichever of these
                    // materials the GLB actually uses, in place of
                    // GLTFLoader's own embedded copies.
                    if (material)
                    {
                        const textureSet = treeTextureSets[stripMaterialNameSuffix(material.name)]
                        if (textureSet)
                        {
                            material.map = textureSet.map
                            if (textureSet.normalMap)
                                material.normalMap = textureSet.normalMap
                            if (textureSet.metallicRoughness)
                            {
                                material.roughnessMap = textureSet.metallicRoughness
                                material.metalnessMap = textureSet.metallicRoughness
                            }
                            if (textureSet.aoMap)
                                material.aoMap = textureSet.aoMap
                            material.needsUpdate = true
                        }
                    }

                    // Forces both faces of every plane/card to render,
                    // regardless of view direction. The GLB already marks
                    // every material "doubleSided": true (which GLTFLoader
                    // auto-converts to THREE.DoubleSide on load) and
                    // nothing else in this file touches .side, so this
                    // should be a no-op - but it's a one-line, zero-risk
                    // way to rule the possibility out completely rather
                    // than trust that automatic conversion blindly.
                    if (material)
                        material.side = THREE.DoubleSide

                    if (material && /^leaves$|^tree_trunk|^tree_branches|^background_tree_atlas/i.test(material.name))
                        treeTintMaterials.add(material)

                    // Shadow casting uses a separate depth pass that
                    // doesn't look at a material's alpha at all by
                    // default - so a leaf-card mesh (a flat quad with a
                    // transparent PNG shaped like foliage) casts a shadow
                    // shaped like its whole rectangular quad, not the leaf
                    // cutout, which is what was showing up as blocky
                    // rectangular tree shadows on the ground. Fixed with a
                    // dedicated shadow-only depth material that DOES use
                    // alphaTest, assigned via customDepthMaterial - the
                    // visible material itself is completely untouched
                    // (still the native alpha-BLEND look, exactly as
                    // deliberately kept above), since the depth pass and
                    // the color pass are two entirely separate materials.
                    // Excludes tree_trunk on purpose - trunks are solid
                    // opaque geometry, not alpha-carded, so they already
                    // cast a correct shadow with no fix needed.
                    if (material && /^leaves$|^tree_branches|^background_tree_atlas/i.test(material.name))
                    {
                        let foliageDepthMaterial = foliageShadowDepthMaterials.get(material.uuid)
                        if (!foliageDepthMaterial)
                        {
                            foliageDepthMaterial = new THREE.MeshDepthMaterial({
                                depthPacking: THREE.RGBADepthPacking,
                                map: material.map,
                                alphaTest: 0.5
                            })
                            foliageShadowDepthMaterials.set(material.uuid, foliageDepthMaterial)
                        }
                        child.customDepthMaterial = foliageDepthMaterial
                    }

                    // Anisotropic filtering was only ever applied to the
                    // floor's own externally-loaded textures - anything
                    // GLTFLoader loads automatically from inside this GLB
                    // (which is everything on the tree pack: bark, leaf/
                    // branch cards, atlas cards) never got it. At a steep
                    // viewing angle - looking down from a high camera, the
                    // exact case reported - a texture without anisotropic
                    // filtering gets forced to a very blurry mip level,
                    // which is what read as trees "disappearing" into a
                    // colorless smear from above while looking fine from a
                    // level angle. maxAnisotropy is declared later in this
                    // file (needs the renderer to exist to query the GPU),
                    // but this callback only runs once the GLB finishes
                    // loading - long after that line has already run.
                    if (material)
                    {
                        for (const key of ['map', 'normalMap', 'roughnessMap', 'metalnessMap', 'aoMap', 'emissiveMap'])
                        {
                            if (material[key])
                                material[key].anisotropy = maxAnisotropy
                        }
                    }

                    // Foliage ("leaves", "Tree_Branches_*") is deliberately
                    // left on its native alphaMode BLEND behaviour
                    // (transparent + depthWrite false, exactly as
                    // GLTFLoader set it up) - an alpha-test cutout was
                    // tried here for a while to fix camera-angle-dependent
                    // disappearing, but it also meant flat/hard edges and a
                    // bare-branch look on some trees no matter how the
                    // cutoff was tuned. Reverted by explicit preference:
                    // the soft blended look back, accepting that
                    // occasional orbit-angle flicker/disappearing can come
                    // with it.
                    //
                    // "Background_Tree_Atlas*" is the one exception - these
                    // are the distant backdrop cards, not close-up foliage,
                    // so hard edges are far less noticeable on them, while
                    // depthWrite:false blended geometry crossing through
                    // solid objects (cars, the compound wall) is exactly
                    // what was reading as a garbled/overlapping texture
                    // mess rather than trees - depth-less blended geometry
                    // has no reliable way to sort against opaque geometry
                    // it physically intersects. alphaTest writes depth
                    // normally, which fixes that at the (much less visible,
                    // here) cost of a harder silhouette edge.
                    if (material && /^background_tree_atlas/i.test(material.name))
                    {
                        material.transparent = false
                        material.depthWrite = true
                        material.alphaTest = 0.5
                        material.needsUpdate = true
                    }
                }
            }
        })

        // Vegetation tint - each tree material's own .color multiplies
        // against its baseColorTexture (standard glTF/PBR behaviour), so
        // this tints/brightens the tree pack's existing textures live
        // rather than replacing them - a direct lever for nudging the
        // tree pack's color/brightness against the ground's own palette,
        // since the two came from different sources (hand-picked floor
        // texture vs. a downloaded asset pack) with no rendering-pipeline
        // bug behind any mismatch (sRGB decoding and tone mapping are
        // already confirmed correct and applied uniformly). Defaults to
        // white/1.0 - a true no-op, identical to not having this control
        // at all, until actually dragged.
        const vegetationTint = { color: '#ffffff', brightness: 1 }
        const applyVegetationTint = () =>
        {
            const tint = new THREE.Color(vegetationTint.color).multiplyScalar(vegetationTint.brightness)
            for (const material of treeTintMaterials)
            {
                material.color.copy(tint)
                material.needsUpdate = true
            }
        }
        const vegetationFolder = gui.addFolder('Vegetation tint').close()
        vegetationFolder.addColor(vegetationTint, 'color').name('tint').onChange(applyVegetationTint)
        vegetationFolder.add(vegetationTint, 'brightness').min(0.2).max(2).step(0.01).name('brightness').onChange(applyVegetationTint)

        // City-block backdrop buildings ("Cube*" nodes) - forced back to
        // one uniform warm tan/brown color, by request (preferred the
        // look of the previous backup over each one's own real GLB
        // material) - EXCEPT "Cube.003", which keeps its own dedicated
        // color (see just below), since that one was specifically asked
        // for separately from this general revert. "Cube.223" used to be
        // a second exception here too, but was reverted back into this
        // same brown sweep by request.
        const cityBlockMaterial = new THREE.MeshStandardMaterial({
            color: '#9c6b45',
            roughness: 0.85,
            metalness: 0,
            side: THREE.DoubleSide
        })
        let cityBlockMeshCount = 0
        houseModel.traverse((cityChild) =>
        {
            if (cityChild.isMesh && isCityBlockObject(cityChild))
            {
                cityChild.material = cityBlockMaterial
                cityBlockMeshCount++
            }
        })
        console.log(`[city blocks] recolored ${cityBlockMeshCount} backdrop building mesh(es) to a uniform tan/brown`)

        // "Cube.003" - kept on its own real color (a green, "Material.002"
        // in the GLB) instead of the brown sweep above, applied directly
        // here (after that sweep) so it isn't dependent on excluding it
        // from the sweep's own name-matching.
        const cube003 = houseModel.getObjectByName('Cube003')
        if (cube003 && cube003.isMesh)
        {
            cube003.material = new THREE.MeshStandardMaterial({
                color: '#46e799',
                roughness: 0.5,
                metalness: 0,
                side: THREE.DoubleSide
            })
        }
        else
        {
            console.warn('[Cube.003] node not found under houseModel - dedicated material not applied')
        }

        // "Cube.009" and "Cube.010" - both share the same real GLB
        // material ("Material.007"): a semi-transparent cyan/blue
        // (alphaMode BLEND, alpha 0.5), confirmed directly at runtime -
        // reads as glass or water, not a plain building block, so kept
        // off the brown sweep too, on one shared dedicated material
        // (same source material, so no need for two separate instances).
        // Both are also very thin, flat shapes (their own local Y scale
        // is ~0.018) sitting almost exactly flush against whatever
        // surface is directly beneath each one - z-fighting against that
        // surface, same root cause and same polygonOffset fix as
        // "BézierCurve" elsewhere in this file.
        const cube009010Material = new THREE.MeshStandardMaterial({
            color: '#2ddee7',
            opacity: 0.5,
            transparent: true,
            roughness: 0.5,
            metalness: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1
        })
        for (const name of ['Cube009', 'Cube010'])
        {
            const node = houseModel.getObjectByName(name)
            if (node && node.isMesh)
                node.material = cube009010Material
            else
                console.warn(`[${name}] node not found under houseModel - dedicated material not applied`)
        }

        // "Cube.208" and "Cube.004" - neither has any material of its own
        // in the GLB (both fall back to GLTFLoader's shared default
        // material otherwise), kept grey by request rather than swept
        // into the brown - same shared dedicated grey as elsewhere in
        // this file (Cube.223, NurbsPath, BézierCurve).
        const cube208004Material = new THREE.MeshStandardMaterial({
            color: '#e7e7e7',
            roughness: 0.5,
            metalness: 0,
            side: THREE.DoubleSide
        })
        for (const name of ['Cube208', 'Cube004'])
        {
            const node = houseModel.getObjectByName(name)
            if (node && node.isMesh)
                node.material = cube208004Material
            else
                console.warn(`[${name}] node not found under houseModel - dedicated material not applied`)
        }

        // "Circle", "Circle.001", "Circle.002", "Circle.003" - none of the
        // four has any material of its own either, same as Cube.208/.004
        // above - grey by request. ("Circle.003" is the one case with a
        // second, LINES-mode primitive alongside its triangles - traverse
        // + isMesh below only touches the triangle part, same as it would
        // for any other mixed-primitive node in this file.) polygonOffset
        // added for the same z-fighting reason as "BézierCurve"/
        // "Cube.009"/"Cube.010" elsewhere in this file - these are the
        // same kind of thin, flush-against-another-surface shape.
        const circlesMaterial = new THREE.MeshStandardMaterial({
            color: '#e7e7e7',
            roughness: 0.5,
            metalness: 0,
            side: THREE.DoubleSide,
            polygonOffset: true,
            polygonOffsetFactor: -1,
            polygonOffsetUnits: -1
        })
        for (const name of ['Circle', 'Circle001', 'Circle002', 'Circle003'])
        {
            const node = houseModel.getObjectByName(name)
            if (!node)
            {
                console.warn(`[${name}] node not found under houseModel - dedicated material not applied`)
                continue
            }
            node.traverse((child) =>
            {
                if (child.isMesh)
                    child.material = circlesMaterial
            })
        }

        // "Plane.019" has no material of its own either - green by
        // request, same green already used for "Cube.003" above rather
        // than a new arbitrary shade.
        const plane019 = houseModel.getObjectByName('Plane019')
        if (plane019 && plane019.isMesh)
        {
            plane019.material = new THREE.MeshStandardMaterial({
                color: '#46e799',
                roughness: 0.5,
                metalness: 0,
                side: THREE.DoubleSide
            })
        }
        else
        {
            console.warn('[Plane.019] node not found under houseModel - dedicated material not applied')
        }

        // The 198 duplicate-twin nodes that used to be hidden here (a scan
        // of every mesh-bearing node's world position found 160 pairs/
        // groups of true duplicate geometry, same leftover-export pattern
        // as the "Car_*.001" vehicle duplicates below) have since been
        // deleted directly from Building_surface.glb instead, along with
        // their now-orphaned meshes/materials/accessors (~435KB smaller) -
        // nothing left to hide here any more.

        // "Plane.013" gets its own dedicated material here too, directly -
        // its own GLB material ("Material.004") is a near-black, semi-
        // transparent flat color (alphaMode BLEND, no map) that reads as a
        // soft ground shadow/decal, but the "no map" fallback above was
        // overwriting its color to the generic flat grey default (it's
        // not a "Cube*" city-block name, so that exclusion never covered
        // it) - leaving opacity/transparency alone, but turning what
        // should be a dark blob into a pale grey one.
        const plane013 = houseModel.getObjectByName('Plane013')
        if (plane013 && plane013.isMesh)
        {
            plane013.material = new THREE.MeshStandardMaterial({
                color: '#343434',
                opacity: 0.7192224264144897,
                transparent: true,
                roughness: 0.5,
                metalness: 0,
                side: THREE.DoubleSide
            })
        }
        else
        {
            console.warn('[Plane.013] node not found under houseModel - dedicated material not applied')
        }

        // "NurbsPath" has no material of its own in the GLB at all (unlike
        // the three above, there's no original color being overwritten
        // here) - given its own dedicated material directly anyway, by
        // request, so it's not left depending on GLTFLoader's shared
        // default material instance (see the same reasoning on "Cube.223"
        // above - that shared instance is also used by many unrelated
        // meshes elsewhere in the model). polygonOffset for the same
        // z-fighting reason as "BézierCurve"/"Cube.009"/"Cube.010"/the
        // Circles elsewhere in this file.
        const nurbsPath = houseModel.getObjectByName('NurbsPath')
        if (nurbsPath && nurbsPath.isMesh)
        {
            nurbsPath.material = new THREE.MeshStandardMaterial({
                color: '#e7e7e7',
                roughness: 0.5,
                metalness: 0,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: -1,
                polygonOffsetUnits: -1
            })
        }
        else
        {
            console.warn('[NurbsPath] node not found under houseModel - dedicated material not applied')
        }

        // "BézierCurve" - same situation and same treatment as "NurbsPath"
        // just above (no material of its own in the GLB at all), plus
        // polygonOffset to fix z-fighting/flicker against whatever other
        // surface it sits flush against (the Solidify modifier shown in
        // Blender's outliner for this one gives it real thickness, but
        // apparently not enough to clear the surface underneath at this
        // scene's scale) - a small negative offset pulls it very slightly
        // toward the camera, just enough to break the depth tie without
        // being visible as a separate "floating above the surface" gap.
        const bezierCurve = houseModel.getObjectByName('BézierCurve')
        if (bezierCurve && bezierCurve.isMesh)
        {
            bezierCurve.material = new THREE.MeshStandardMaterial({
                color: '#e7e7e7',
                roughness: 0.5,
                metalness: 0,
                side: THREE.DoubleSide,
                polygonOffset: true,
                polygonOffsetFactor: -1,
                polygonOffsetUnits: -1
            })
        }
        else
        {
            console.warn('[BézierCurve] node not found under houseModel - dedicated material not applied')
        }

        // Disable frustum culling for every mesh under "All trees" (the
        // one parent node the whole vegetation field is grouped under),
        // rather than continuing to pattern-match specific node/material
        // names one at a time - that approach kept missing categories
        // (first "leaves", then had to add "Tree_Branches_*"/
        // "Background_Tree_Atlas*", and even then "Tree_Trunk_*" was still
        // uncovered and could disappear the same way). Confirmed via
        // testing that this is a real, systemic culling bug affecting
        // trees broadly at distance/angle - not fog, not occlusion by
        // another object - so the robust fix is to just stop culling the
        // whole vegetation subtree rather than keep chasing which specific
        // mesh category is affected next.
        if (treesNode)
        {
            treesNode.traverse((child) =>
            {
                if (child.isMesh)
                    child.frustumCulled = false
            })
        }

        // 12 numbered markers ringed around the building - centred on the
        // tallest roof surface found during the traverse above (falls back
        // to the overall bounding box centre/top on the off chance nothing
        // qualified), at a height/radius derived from the building's own
        // real-world height rather than a fixed number (buildingFootprint
        // isn't usable for this - it measures the whole multi-building/
        // city/road GLB, not just this building). Added to "house" (never
        // itself moved or scaled), not to houseModel, so they don't inherit
        // the Building GUI position sliders above - they stay put at the
        // model's original position.
        const MARKER_COUNT = 16
        const markerLineHeight = targetHeight * 0.55
        const markerCenter = tallestRoofCenter
            ? new THREE.Vector3(tallestRoofCenter.x, tallestRoofY, tallestRoofCenter.z)
            : new THREE.Vector3((scaledBox.min.x + scaledBox.max.x) / 2, scaledBox.max.y, (scaledBox.min.z + scaledBox.max.z) / 2)

        // Manually placed via the "Marker Position" GUI sliders and handed
        // back here as the new starting layout (was a ring formula
        // before) - each pair is that marker's own absolute world X/Z,
        // matching exactly what those sliders are bound to
        // (buildingMarker.position.x/z directly, not an offset from
        // markerCenter).
        const initialMarkerPositions = [
            { x: 0.357195, z: -13.4163 },
            { x: -23.0428, z: 23.53363 },
            { x: 4.107195, z: 24.73363 },
            { x: 4.757195, z: -36.2663 },
            { x: 12.15719, z: -29.6163 },
            { x: -11.0428, z: 37.03363 },
            { x: -27.0928, z: 7.807348 },
            { x: 5.657195, z: 8.13363 },
            { x: 0.757195, z: 12.63363 },
            { x: -7.5928, z: 0.78363 },
            { x: -11.3428, z: -71.8663 },
            { x: 13.10719, z: 35.53363 },
            // Markers 13-16 - all four now hand-placed via the "Marker
            // Position" GUI sliders (matching 1-12's own approach).
            // Marker 15 landed right back at its own original
            // placeholder spot (0, 8), kept here as-is.
            { x: 10.85719, z: 25.18363 },
            { x: 4.507195, z: 31.08363 },
            { x: 0, z: 8 },
            { x: -6.94280, z: 48.38363 }
        ]

        // Shared across all 12 badges (not per-marker) - changing any of
        // these regenerates every label's texture via
        // regenerateMarkerLabels below.
        const labelStyleState = {
            badgeColor: '#ffffff',
            fontColor: '#000000',
            fontSize: 48,
            bold: true
        }

        const labelHeight = targetHeight * 0.3
        const buildingMarkers = []
        for (let i = 0; i < MARKER_COUNT; i++)
        {
            const buildingMarker = new THREE.Group()
            buildingMarker.position.set(
                initialMarkerPositions[i].x,
                markerCenter.y,
                initialMarkerPositions[i].z
            )
            house.add(buildingMarker)

            // Resting state - a thin line from the roof up to the label.
            // Both points here are just placeholders - once the floor
            // loads (see floorGltfPromise.then below, which fills in this
            // marker's own markerFadeRefs[i].localFloorY) and the intro
            // finishes, tick()'s pop-in animation redraws this every
            // frame, growing it from the floor up to markerLineHeight.
            // Opacity starts at 0 too (its resting value is 0.85).
            const markerLine = new THREE.Line(
                new THREE.BufferGeometry().setFromPoints([new THREE.Vector3(0, 0, 0), new THREE.Vector3(0, markerLineHeight, 0)]),
                new THREE.LineBasicMaterial({ color: '#fff4e0', transparent: true, opacity: 0 })
            )
            buildingMarker.add(markerLine)

            // Numbered badge, floating at the top of the line. depthTest is
            // off so it always reads on top of the building geometry
            // behind it, the way a UI-ish label pin should. Sized off
            // targetHeight (the fixed 3-storey real-world height), same
            // reasoning as the manual X/Z placements above. Starts at
            // opacity 0 and scaled to nothing - eased up to its real
            // (labelHeight-based) scale and opacity 1 by the same tick()
            // block as the line above, for a combined fade+pop-in.
            const labelText = String(i + 1)
            const { texture: labelTexture, aspect: labelAspect } = createLabelTexture(labelText, labelStyleState)
            // Second, pre-baked texture with the badge swapped to
            // MARKER_HOVER_BADGE_COLOR - swapped in/out by tick()'s hover
            // block (see previousHoveredMarkerIndex) rather than redrawing
            // the canvas every time hover state changes.
            const { texture: labelHoverTexture } = createLabelTexture(labelText, { ...labelStyleState, badgeColor: MARKER_HOVER_BADGE_COLOR, fontColor: MARKER_HOVER_FONT_COLOR })
            const markerLabel = new THREE.Sprite(new THREE.SpriteMaterial({ map: labelTexture, transparent: true, depthTest: false, opacity: 0 }))
            const labelTargetScale = new THREE.Vector3(labelHeight * labelAspect, labelHeight, 1)
            markerLabel.scale.set(0, 0, 1)
            markerLabel.position.set(0, markerLineHeight, 0)
            markerLabel.renderOrder = 10
            buildingMarker.add(markerLabel)

            buildingMarkers.push(buildingMarker)
            markerLabels.push({ sprite: markerLabel, text: labelText, normalTexture: labelTexture, hoverTexture: labelHoverTexture })
            // localFloorY (the line's bottom point, in this marker's own
            // local space) isn't known until the floor loads - filled in
            // there (see floorGltfPromise.then below). topY is just
            // markerLineHeight, named to match, for the growth animation
            // in tick() to read against. revealDelay is rolled once here
            // (not derived from i) so the 12 pop up in a random order
            // rather than a predictable 1-2-3 sequence.
            markerFadeRefs.push({
                line: markerLine,
                label: markerLabel,
                labelTargetScale,
                localFloorY: 0,
                topY: markerLineHeight,
                revealDelay: Math.random() * MARKER_REVEAL_RANDOM_SPREAD,
                revealDone: false
            })
        }

        // Fills the module-level markerCameraOverrides/markerTargetOverrides/
        // markerFovOverrides declared above - markers 1-6 use the exact
        // position/target/FOV dialed in live via the "preview in scene"
        // checkbox + orbiting/sliders (see the Marker Camera GUI folder)
        // and reported back; marker 7's camera position (only) was
        // tuned in an earlier pass and kept as-is. Everything not yet
        // tuned falls back to its original generic default.
        const tunedMarkerCameraPositions = {
            0: { x: -13.9721, y: 5.84308, z: -27.796 },
            1: { x: -19.0275, y: 2.84722, z: 35.11581 },
            2: { x: -14.3178, y: 2.41083, z: 23.40209 },
            3: { x: 9.954929, y: 4.921221, z: -48.079 },
            4: { x: 17.58192, y: 2.321369, z: -47.4368 },
            5: { x: -8.71916, y: 1.375074, z: 27.75436 },
            6: { x: -15.8428, y: 15.5, z: 20.58363 }
        }
        const tunedMarkerTargetPositions = {
            0: { x: -13.192, y: 5.345008, z: -2.82868 },
            1: { x: -13.8409, y: 4.023333, z: 23.43527 },
            2: { x: 6.538451, y: 3.230389, z: 35.58114 },
            3: { x: -8.39566, y: 1.900409, z: -38.9494 },
            4: { x: 4.542413, y: 2.023618, z: -36.2194 },
            5: { x: -17.7548, y: 0.676865, z: 36.60014 }
        }
        const tunedMarkerFovs = {
            0: 85,
            1: 75,
            2: 75,
            3: 75,
            4: 75,
            5: 75
        }
        buildingMarkers.forEach((buildingMarker, i) =>
        {
            const tunedPosition = tunedMarkerCameraPositions[i]
            markerCameraOverrides.push(tunedPosition
                ? new THREE.Vector3(tunedPosition.x, tunedPosition.y, tunedPosition.z)
                : new THREE.Vector3(
                    buildingMarker.position.x,
                    markerCenter.y + MARKER_ZOOM_MIN_HEIGHT_ABOVE,
                    buildingMarker.position.z + MARKER_ZOOM_DISTANCE
                ))

            // Target defaults to the marker's own resting badge position
            // (same height as its label at rest, markerLineHeight above
            // the group) - previously computed live off the label's
            // world position every time a marker was clicked; now a
            // tunable default like the camera position above, so "where
            // the shot looks" can be dialed in too, not just "where the
            // camera sits".
            const tunedTarget = tunedMarkerTargetPositions[i]
            markerTargetOverrides.push(tunedTarget
                ? new THREE.Vector3(tunedTarget.x, tunedTarget.y, tunedTarget.z)
                : new THREE.Vector3(
                    buildingMarker.position.x,
                    markerCenter.y + markerLineHeight,
                    buildingMarker.position.z
                ))

            // FOV defaults to the scene camera's own current fov (its
            // normal, non-zoomed value) - a { fov } wrapper object, not a
            // bare number, since lil-gui's .add(object, property) needs
            // an actual object property to bind its slider to.
            markerFovOverrides.push({ fov: tunedMarkerFovs[i] ?? camera.fov })
        })

        // Builds the circular dial's 12 position dots (see the
        // markerDial* refs/state/drag-handling near the marker overlay
        // wiring, much further up - this is just where MARKER_COUNT is
        // actually in scope). Each is a numbered badge (its ::after in
        // style.css, the number itself read back out via data-number/
        // attr() there) that's also directly clickable - its own click
        // handler (selectMarkerDialIndex, defined with the rest of the
        // dial's rotation logic further up) spins the ring straight to
        // it, the same as dragging it to the top would, without
        // actually opening it - the center "View" button is still what
        // navigates there.
        for (let i = 0; i < MARKER_COUNT; i++)
        {
            const dialItem = document.createElement('div')
            dialItem.className = 'marker-dial-item'
            dialItem.dataset.number = i + 1
            dialItem.addEventListener('click', () => selectMarkerDialIndex(i))
            markerDialRing.appendChild(dialItem)
            markerDialItems.push({ itemEl: dialItem, slotAngle: i * MARKER_DIAL_ITEM_ANGLE })
        }
        applyMarkerDialRotation()

        // Re-draws every badge's texture (both the normal AND hover
        // versions) from the current labelStyleState - called once per
        // GUI change below, not per frame. Re-applies whichever of the
        // two the sprite should currently be showing (hoverTexture if
        // this marker happens to be hovered right now, normalTexture
        // otherwise) rather than always resetting to the normal one,
        // so tweaking a style slider mid-hover doesn't visibly flicker
        // back to the un-hovered color for a frame.
        const regenerateMarkerLabels = () =>
        {
            markerLabels.forEach((entry, i) =>
            {
                entry.normalTexture.dispose()
                entry.hoverTexture.dispose()
                const { texture, aspect } = createLabelTexture(entry.text, labelStyleState)
                const { texture: hoverTexture } = createLabelTexture(entry.text, { ...labelStyleState, badgeColor: MARKER_HOVER_BADGE_COLOR, fontColor: MARKER_HOVER_FONT_COLOR })
                entry.normalTexture = texture
                entry.hoverTexture = hoverTexture
                entry.sprite.material.map = i === hoveredMarkerIndex ? hoverTexture : texture
                entry.sprite.material.needsUpdate = true
                entry.sprite.scale.set(labelHeight * aspect, labelHeight, 1)
            })
        }

        // Marker Position GUI - populating the folder created right at the
        // top of the file (so it lands first in the panel) with its
        // shared badge style controls. The per-marker X/Z position
        // sliders are added later, once the floor has loaded (see
        // floorGltfPromise.then below), so their range can span its
        // actual full extent rather than a small guessed number.
        markerPositionFolder.addColor(labelStyleState, 'badgeColor').name('badge color').onChange(regenerateMarkerLabels)
        markerPositionFolder.addColor(labelStyleState, 'fontColor').name('font color').onChange(regenerateMarkerLabels)
        markerPositionFolder.add(labelStyleState, 'fontSize').min(16).max(96).step(1).name('font size').onChange(regenerateMarkerLabels)
        markerPositionFolder.add(labelStyleState, 'bold').name('bold').onChange(regenerateMarkerLabels)

        // Instancing pass - this GLB has dozens of meshes that are
        // identical in shape (railing posts, bolts, etc.) but each still
        // costs its own draw call as a separate THREE.Mesh. GLTFLoader
        // doesn't share geometry across them (each gets its own
        // BufferGeometry, even where the vertex data is byte-identical),
        // so grouping has to compare actual vertex data rather than object
        // references - material references ARE shared though (GLTFLoader
        // caches one default material for every material-less primitive,
        // which is what most of these turned out to be), so that part can
        // just compare .uuid.
        houseModel.updateMatrixWorld(true)
        const houseModelWorldInverse = new THREE.Matrix4().copy(houseModel.matrixWorld).invert()

        // Cheap hash over a sample of vertex positions, used only to bucket
        // candidates before the exact byte-for-byte check below - a hash
        // collision here just means two different shapes get compared and
        // correctly rejected, never incorrectly merged.
        const geometryHash = (geometry) =>
        {
            const position = geometry.attributes.position
            if (!position)
                return null

            let hash = position.count
            const stride = Math.max(1, Math.floor(position.count / 24))
            for (let i = 0; i < position.count; i += stride)
            {
                hash = (hash * 31 + Math.round(position.getX(i) * 1000)) | 0
                hash = (hash * 31 + Math.round(position.getY(i) * 1000)) | 0
                hash = (hash * 31 + Math.round(position.getZ(i) * 1000)) | 0
            }
            return hash
        }

        const positionsMatch = (geometryA, geometryB) =>
        {
            const arrayA = geometryA.attributes.position.array
            const arrayB = geometryB.attributes.position.array
            if (arrayA.length !== arrayB.length)
                return false

            for (let i = 0; i < arrayA.length; i++)
            {
                if (arrayA[i] !== arrayB[i])
                    return false
            }
            return true
        }

        // Bucket every eligible mesh by material + a fast geometry hash.
        // Excludes multi-material meshes (InstancedMesh needs exactly one),
        // skinned meshes, and morph targets - none of that survives being
        // flattened into a shared instance buffer correctly.
        const instancingBuckets = new Map()

        houseModel.traverse((child) =>
        {
            if (!child.isMesh || child.isSkinnedMesh || Array.isArray(child.material))
                return

            // Transparent materials (foliage: "leaves", "Tree_Branches_*",
            // "Background_Tree_Atlas*", all alphaMode BLEND) can't go into
            // an InstancedMesh: all its instances draw in one call with no
            // back-to-front sort between them, and since BLEND also means
            // depthWrite: false, two overlapping instanced groups have
            // nothing to correctly resolve which one's in front - whichever
            // drew later just wins outright. Which one that is depends on
            // the transparent-queue sort order, which changes with camera
            // position - that's what read as trees disappearing/appearing
            // based on where you looked from. Left as individual meshes,
            // the renderer sorts and depth-tests them correctly.
            if (child.material.transparent)
                return

            const attributes = child.geometry.attributes
            if (!attributes.position || attributes.skinWeight || child.geometry.morphAttributes.position)
                return

            const hash = geometryHash(child.geometry)
            if (hash === null)
                return

            const key = `${child.material.uuid}:${hash}:${attributes.position.count}`
            if (!instancingBuckets.has(key))
                instancingBuckets.set(key, [])
            instancingBuckets.get(key).push(child)
        })

        const MIN_INSTANCE_GROUP_SIZE = 4 // fewer than this isn't worth the InstancedMesh overhead
        let instancedMeshCount = 0
        let instancedGroupCount = 0

        for (const candidates of instancingBuckets.values())
        {
            // The hash only bucketed candidates - split by an exact vertex
            // check in case two different shapes happened to hash the same;
            // this is what actually guarantees a safe merge.
            const verifiedGroups = []
            for (const candidate of candidates)
            {
                const matchingGroup = verifiedGroups.find((group) => positionsMatch(candidate.geometry, group[0].geometry))
                if (matchingGroup)
                    matchingGroup.push(candidate)
                else
                    verifiedGroups.push([candidate])
            }

            for (const group of verifiedGroups)
            {
                if (group.length < MIN_INSTANCE_GROUP_SIZE)
                    continue

                const { geometry, material } = group[0]
                const instancedMesh = new THREE.InstancedMesh(geometry, material, group.length)
                instancedMesh.castShadow = group[0].castShadow
                instancedMesh.receiveShadow = group[0].receiveShadow
                instancedMesh.frustumCulled = group[0].frustumCulled

                const instanceMatrix = new THREE.Matrix4()
                group.forEach((mesh, i) =>
                {
                    instanceMatrix.multiplyMatrices(houseModelWorldInverse, mesh.matrixWorld)
                    instancedMesh.setMatrixAt(i, instanceMatrix)
                    mesh.parent.remove(mesh)
                })
                instancedMesh.instanceMatrix.needsUpdate = true
                if (typeof instancedMesh.computeBoundingSphere === 'function')
                    instancedMesh.computeBoundingSphere()

                houseModel.add(instancedMesh)
                instancedGroupCount++
                instancedMeshCount += group.length
            }
        }

        if (instancedMeshCount > 0)
        {
            console.log(`[instancing] merged ${instancedMeshCount} meshes into ${instancedGroupCount} InstancedMesh draw call(s), building_surface.glb`)
        }

        /**
         * Vehicles
         * Building_surface.glb carries a "Vehicles_f" collection (Blender-side
         * only - collections don't survive as glTF nodes, see the long
         * back-and-forth getting this working) with 3 road-path curves
         * (RoadPath01/02/03, exported as simple line-segment meshes - a
         * loose-edges-only mesh, no faces) and 5 static parked car meshes
         * (Car_1_1, Car_3_1, Car_3_2, Pickup_3, Truck_2). The path meshes are
         * read here for their point data only (never rendered themselves);
         * the car meshes are used as templates, cloned into several moving
         * instances instead of animating the lone originals in place.
         */
        const ROAD_PATH_NAMES = ['RoadPath01', 'RoadPath02', 'RoadPath03']
        // "Gas_Truck" (the tanker) removed from this list by request - no
        // longer cloned into moving traffic. Its own static original is
        // hidden separately below, same as the others, since it's no
        // longer part of carTemplates for that loop to reach.
        const CAR_TEMPLATE_NAMES = ['Car_1_1', 'Car_3_1', 'Car_3_2', 'Pickup_3', 'Truck_2']
        const VEHICLE_COUNT = 27 // total moving cars spread across all paths/varieties - was 9, increased 200%
        const VEHICLE_SPEED_RANGE = [0.2, 0.4] // meters/second (real-world scale), randomized per car - was [1.5, 3], read as way too fast

        const roadCurves = []
        for (const pathName of ROAD_PATH_NAMES)
        {
            const pathObject = houseModel.getObjectByName(pathName)
            const positionAttribute = pathObject && pathObject.geometry && pathObject.geometry.attributes.position
            if (!positionAttribute)
                continue

            // Confirmed directly against the GLB's own index buffer (pairs
            // like 0-1, 1-2, 2-3, ...) that these vertices are already laid
            // out in path order - no separate reordering needed, just read
            // them off in order. pathObject sits at the same level as
            // houseModel's other root children with no extra parent
            // transform beyond its own position (translation only, no
            // rotation/scale - also confirmed directly against the GLB), so
            // adding that position to each local vertex is enough to land in
            // houseModel's own local space - the same space the car clones
            // below live in.
            const points = []
            for (let i = 0; i < positionAttribute.count; i++)
            {
                points.push(new THREE.Vector3(
                    positionAttribute.getX(i),
                    positionAttribute.getY(i),
                    positionAttribute.getZ(i)
                ).add(pathObject.position))
            }

            const curve = new THREE.CatmullRomCurve3(points, false, 'catmullrom')
            // Kept around (not part of THREE.Curve itself) purely so
            // findRoadJunctions() below can search the road's own actual
            // vertices directly, rather than re-sampling the smoothed
            // curve it builds from them.
            curve.rawPoints = points
            roadCurves.push(curve)
            pathObject.visible = false // a driving guide only, never meant to render
        }

        // Find where these roads actually cross each other, once, up front
        // - see findRoadJunctions() and resolveIntersectionYielding() (the
        // latter defined alongside updateVehicles() further down, using
        // this array every frame).
        vehicleJunctions.push(...findRoadJunctions(roadCurves))
        console.log(`[vehicles] found ${vehicleJunctions.length} road junction(s) requiring yield-to-cross behavior`)

        if (roadCurves.length > 0)
        {
            const carTemplates = CAR_TEMPLATE_NAMES
                .map((name) => houseModel.getObjectByName(name))
                .filter(Boolean)

            // The static originals (Car_1_1, Truck_2, etc.) used to be
            // left visible as "parked" vehicles, but they read as sunk
            // into the ground at this scene's scale/positioning - hidden
            // here instead (clone() further down copies the geometry
            // before this takes effect, so the moving traffic is
            // unaffected, only these source originals disappear). The
            // GLB's second, exact-duplicate set of these same 6 vehicles
            // (".001" suffix, at the identical position) has since been
            // deleted from the file directly, same as the other duplicate
            // geometry found later in this callback - nothing left to
            // hide for those.
            for (const template of carTemplates)
                template.visible = false
            const gasTruck = houseModel.getObjectByName('Gas_Truck')
            if (gasTruck)
                gasTruck.visible = false

            // Weight which path a new car gets by that path's own actual
            // length (not an even 1-in-3 chance per path) - so the longer
            // track ends up with proportionally more traffic on it (more
            // total cars there naturally means more variety/frequency of
            // cars vs. trucks showing up on it too), rather than every road
            // averaging out to roughly the same car count regardless of size.
            const roadCurveLengths = roadCurves.map((curve) => curve.getLength())
            const totalRoadLength = roadCurveLengths.reduce((sum, length) => sum + length, 0)
            const pickWeightedRoadCurve = () =>
            {
                let roll = Math.random() * totalRoadLength
                for (let i = 0; i < roadCurves.length; i++)
                {
                    roll -= roadCurveLengths[i]
                    if (roll <= 0)
                        return roadCurves[i]
                }
                return roadCurves[roadCurves.length - 1]
            }

            for (let i = 0; i < VEHICLE_COUNT && carTemplates.length > 0; i++)
            {
                const template = carTemplates[Math.floor(Math.random() * carTemplates.length)]
                const car = template.clone()
                car.visible = true
                houseModel.add(car)

                vehicles.push({
                    object: car,
                    curve: pickWeightedRoadCurve(),
                    // Preserves the model's own authored corrective rotation
                    // (these car meshes are modeled lying on their side in
                    // source data, hence e.g. Car_1_1's own baked-in 90-degree
                    // X rotation) - travel direction is layered on top of
                    // this in updateVehicles(), not in place of it.
                    baseQuaternion: template.quaternion.clone(),
                    t: Math.random(), // random starting point along the path
                    direction: Math.random() < 0.5 ? 1 : -1, // to-and-fro: some start forward, some backward
                    speed: THREE.MathUtils.lerp(VEHICLE_SPEED_RANGE[0], VEHICLE_SPEED_RANGE[1], Math.random()),
                    // Temporary multiplier on top of the base speed above -
                    // eased toward speedBoostTarget above 1 when this
                    // vehicle is being tailgated (see
                    // resolveVehicleOvertaking), so IT accelerates to open
                    // the gap back up instead of the follower always being
                    // the only one that reacts; eases back down to 1 once
                    // nothing's close behind it any more.
                    speedBoost: 1,
                    speedBoostTarget: 1
                })
            }

            /**
             * Students - walking back and forth along the same road
             * curves the cars drive on, just offset further out toward
             * the shoulder (PEDESTRIAN_LANE_OFFSET, bigger than the
             * cars' own VEHICLE_LANE_OFFSET) so they read as walking
             * alongside traffic rather than in it. Same to-and-fro
             * bounce as vehicles (see updateStudents, next to
             * updateVehicles further down) - deliberately skips the
             * cars' own overtaking/junction-yielding logic, which
             * solves problems (tailgating, blind intersections) that
             * don't meaningfully apply to a handful of slow pedestrians.
             * See createPersonMesh (also further down) for how the
             * figures themselves are built - there's no actual human
             * character model anywhere in this project, only the cars
             * are pre-modeled (baked into Building_surface.glb's own
             * "Vehicles_f" collection), so these are procedural.
             */
            const STUDENT_COUNT = 10
            const STUDENT_SPEED_RANGE = [0.05, 0.09] // this scene's own speed units (see VEHICLE_SPEED_RANGE) - well under even the slowest car
            // Divided by scaleFactor (see its own definition above) rather
            // than just "1" - these person meshes are built as real
            // literal geometry (unlike curveLength/speed above, which are
            // just path-sampling parameters), and get added as children
            // of houseModel, so houseModel's own scale multiplies
            // whatever height is built here by scaleFactor at render
            // time. Building at 1m directly made every student ~20m+
            // tall once rendered - towering over the 9m-tall building
            // itself - this is what actually gets the real ~1m figure.
            const STUDENT_HEIGHT = 1 / scaleFactor // roughly a school-age student, in real-world meters

            // Waits on personGltfPromise so personTemplate is already
            // resolved (or definitively failed) before any figure gets
            // built - see createPersonOrFallbackMesh. Skipped entirely
            // while SHOW_PEOPLE is off (see its own definition up top).
            if (SHOW_PEOPLE)
            personGltfPromise.then(() =>
            {
                for (let i = 0; i < STUDENT_COUNT; i++)
                {
                    const person = createPersonOrFallbackMesh(STUDENT_HEIGHT)
                    houseModel.add(person)
                    students.push({
                        object: person,
                        curve: pickWeightedRoadCurve(),
                        side: Math.random() < 0.5 ? -1 : 1, // which shoulder of the road - independent of travel direction
                        t: Math.random(),
                        direction: Math.random() < 0.5 ? 1 : -1,
                        speed: THREE.MathUtils.lerp(STUDENT_SPEED_RANGE[0], STUDENT_SPEED_RANGE[1], Math.random())
                    })
                }
            })
        }

        /**
         * Kids playing - a handful of smaller figures scattered near the
         * "Kids Play Area" marker. KIDS_PLAY_AREA_POSITION duplicates
         * initialMarkerPositions[1] (marker index 1, "Kids Play Area" -
         * see MARKER_NAMES) as a plain constant rather than importing
         * that array, since only this one fixed point is needed and
         * that array is local to a different load callback entirely;
         * house (this marker position's own space) has an identity
         * transform all the way up to the scene, so it's usable here
         * as-is with no conversion. Each kid just loops around its own
         * starting spot in a loose, never-quite-circular wander (two
         * sine waves at slightly different frequencies/offsets) rather
         * than following any real path - reads as "playing nearby", not
         * "walking somewhere", which is the whole distinction from the
         * students above.
         */
        const KIDS_PLAY_AREA_POSITION = new THREE.Vector3(-23.0428, 0, 23.53363) // matches initialMarkerPositions[1]
        const KIDS_COUNT = 5
        const KIDS_SCATTER_RADIUS = 4 // meters from the play area center that each kid's own "home" spot can land
        const KIDS_HEIGHT = 0.75

        if (SHOW_PEOPLE)
        personGltfPromise.then(() =>
        {
            for (let i = 0; i < KIDS_COUNT; i++)
            {
                const kid = createPersonOrFallbackMesh(KIDS_HEIGHT)
                const angle = Math.random() * Math.PI * 2
                const radius = Math.random() * KIDS_SCATTER_RADIUS
                kid.userData.homeX = KIDS_PLAY_AREA_POSITION.x + Math.cos(angle) * radius
                kid.userData.homeZ = KIDS_PLAY_AREA_POSITION.z + Math.sin(angle) * radius
                kid.userData.wanderRadius = 0.6 + Math.random() * 0.8
                kid.userData.wanderSpeed = 0.6 + Math.random() * 0.6
                kid.userData.wanderOffset = Math.random() * Math.PI * 2
                kid.position.set(kid.userData.homeX, KIDS_PLAY_AREA_POSITION.y, kid.userData.homeZ)
                scene.add(kid)
                kidsPlaying.push(kid)
            }
        })

        // Kids play area + banyan tree - same scaleFactor/Y-lift as the
        // house model itself (see kpaGltfPromise's own comment above), so
        // it lines up with the building without needing its own bounding-
        // box measurement like the floor does below.
        kpaGltfPromise.then((kpaGltf) =>
        {
            if (kpaGltf)
            {
                const kpaModel = kpaGltf.scene
                kpaModel.scale.setScalar(scaleFactor)
                kpaModel.position.y = 0.5

                kpaModel.traverse((child) =>
                {
                    if (child.isMesh)
                    {
                        child.castShadow = true
                        child.receiveShadow = true
                    }
                })

                scene.add(kpaModel)
            }
        })

        // Wait for the floor to load so it can be sized against the
        // building's footprint (now known) and its actual top-surface
        // height measured, before resting the house on it and adding both
        // to the sceneple
        floorGltfPromise.then((floorGltf) =>
        {
            if (floorGltf)
            {
                const floorModel = floorGltf.scene

                // Both GLBs' group scale is set to the exact same figure -
                // scaleFactor, the one real-world scale derived from the
                // house's own height above - instead of each being computed
                // separately. This sets it directly (not a multiply on top
                // of some other starting value), and does NOT touch the
                // "Plane" node's own authored local scale/position, which is
                // what actually shapes and places the floor to match
                // Building_surface.glb, since both were exported from the
                // same shared Blender scene/origin.
                floorModel.scale.setScalar(scaleFactor)
                floorModel.position.y = 0
                const floorScale = scaleFactor

                floorModel.traverse((child) =>
                {
                    if (child.isMesh)
                    {
                        // Swap in the external base color + alpha textures,
                        // but keep Floor.glb's own material/UVs otherwise
                        // untouched - that's what keeps this aligned with
                        // the source. transparent must be enabled for the
                        // alphaMap to actually cut anything out.
                        const materials = Array.isArray(child.material) ? child.material : [child.material]
                        for (const material of materials)
                        {
                            if (material)
                            {
                                material.map = planeColorTexture
                                material.alphaMap = planeAlphaTexture
                                material.transparent = true
                                material.needsUpdate = true
                            }
                        }

                        // Floor receives the building's shadow, doesn't cast one
                        child.castShadow = false
                        child.receiveShadow = true
                    }
                })

                scene.add(floorModel)

                // Sizing the shadow frustum to the floor's FULL footprint
                // (the previous fix, for a boundary-line bug) spreads the
                // shadow map's fixed pixel resolution across the whole site
                // instead of concentrating it where the actual shadow-caster
                // (the building) is - the building's own shadow becomes a
                // tiny, heavily-blurred smudge across that much larger area
                // and effectively disappears. So the frustum only covers
                // the building's own footprint (with a margin for how far
                // the shadow falls) rather than the entire floor.
                // Tightened from 3x - a smaller frustum concentrates the
                // (now 4096) shadow map's resolution more on the building
                // itself instead of spreading it over extra unused margin.
                // Still enough room for the shadow to fall away from the
                // building without clipping at this sun elevation (55°).
                const shadowMargin = 2 // building footprint x this = frustum size
                updateShadowCameraFrustum(buildingFootprint * shadowMargin)

                // Floor GUI controls - position and a uniform scale slider.
                // Ranged relative to the building's footprint and the pinned
                // scale above, rather than fixed numbers, so the slider
                // bounds land somewhere useful for this building's size.
                const floorFolder = gui.addFolder('Floor').close()
                floorFolder.add(floorModel.position, 'x').min(-buildingFootprint).max(buildingFootprint).step(0.1).name('position X')
                floorFolder.add(floorModel.position, 'y').min(-buildingFootprint * 0.1).max(buildingFootprint * 0.1).step(0.01).name('position Y')
                floorFolder.add(floorModel.position, 'z').min(-buildingFootprint).max(buildingFootprint).step(0.1).name('position Z')

                const floorScaleControl = { scale: floorScale }
                floorFolder.add(floorScaleControl, 'scale')
                    .min(floorScale * 0.25)
                    .max(floorScale * 3)
                    .step(floorScale / 100)
                    .name('scale')
                    .onChange((value) => floorModel.scale.setScalar(value))

                // Per-marker X/Z position sliders, added here (not back in
                // the marker setup above) specifically so their range can
                // span the floor's own actual extent - the alpha-mapped
                // plane the site's paths/parking/track are cut into (see
                // planeAlphaTexture) - letting each of the 12 markers be
                // placed anywhere across the visible scene, not just in a
                // small ring near the building.
                const floorBounds = new THREE.Box3().setFromObject(floorModel)
                console.log(`[markers] floor bounds - X: ${floorBounds.min.x.toFixed(2)} to ${floorBounds.max.x.toFixed(2)}, Z: ${floorBounds.min.z.toFixed(2)} to ${floorBounds.max.z.toFixed(2)}`)
                // Markers sit at roof height (markerCenter.y) - left as-is,
                // not moved - but their line's bottom was only reaching
                // local y=0 (the group's own height), leaving it floating
                // above the ground wherever a marker got moved away from
                // the building via the X/Z sliders below. The line's
                // bottom point needs to reach the floor's actual top
                // surface (converted into this group's local space) while
                // the badge at the top stays exactly where it was -
                // recorded on markerFadeRefs here rather than drawn
                // directly, since the pop-in animation in tick() is what
                // actually grows the line from this point up to
                // markerLineHeight (see markersRevealTriggered there).
                buildingMarkers.forEach((buildingMarker, i) =>
                {
                    markerFadeRefs[i].localFloorY = floorBounds.max.y - buildingMarker.position.y
                })
                buildingMarkers.forEach((buildingMarker, i) =>
                {
                    markerPositionFolder.add(buildingMarker.position, 'x')
                        .min(floorBounds.min.x)
                        .max(floorBounds.max.x)
                        .step(0.05)
                        .name(`marker ${i + 1} position X`)
                    markerPositionFolder.add(buildingMarker.position, 'z')
                        .min(floorBounds.min.z)
                        .max(floorBounds.max.z)
                        .step(0.05)
                        .name(`marker ${i + 1} position Z`)
                })

                // Per-marker zoomed-in camera controls - one collapsed
                // subfolder per marker (rather than everything flattened
                // directly under Marker Camera) so adjusting one marker's
                // values isn't a scroll-and-squint exercise among the
                // other 11. Camera/target X/Z share the floor's own
                // extent (same reasoning as the marker position sliders
                // just above: full freedom across the visible scene), Y
                // gets its own fixed, generous range since "how high
                // above the ground" has nothing to do with the floor's
                // own (near-zero) vertical extent. The "preview in scene"
                // checkbox (see setMarkerCameraPreview, near the Camera
                // section) snaps the real scene camera to all of these
                // live values - position, look-at target, AND fov - so a
                // slider drag is actually visible instead of only
                // affecting the next real marker click.
                const MARKER_CAMERA_MIN_Y = -5
                const MARKER_CAMERA_MAX_Y = 80
                markerCameraOverrides.forEach((override, i) =>
                {
                    const markerCameraSubfolder = markerCameraFolder.addFolder(`Marker ${i + 1}`).close()

                    const previewState = { preview: false }
                    markerCameraPreviewStates[i] = previewState
                    markerCameraPreviewControllers[i] = markerCameraSubfolder.add(previewState, 'preview')
                        .name('preview in scene')
                        .onChange((active) => setMarkerCameraPreview(i, active))

                    // Every slider below applies live to the actual scene
                    // camera (via applyMarkerOverrideToCamera) the instant
                    // it's dragged, but only while THIS marker is the one
                    // being previewed - editing marker 3's sliders while
                    // marker 7 is previewed just queues up marker 3's
                    // values for whenever it's previewed instead.
                    const applyIfPreviewing = () =>
                    {
                        if (markerCameraPreviewIndex === i)
                            applyMarkerOverrideToCamera(i)
                    }

                    const cameraXController = markerCameraSubfolder.add(override, 'x')
                        .min(floorBounds.min.x)
                        .max(floorBounds.max.x)
                        .step(0.05)
                        .name('camera X')
                        .onChange(applyIfPreviewing)
                    const cameraYController = markerCameraSubfolder.add(override, 'y')
                        .min(MARKER_CAMERA_MIN_Y)
                        .max(MARKER_CAMERA_MAX_Y)
                        .step(0.05)
                        .name('camera Y')
                        .onChange(applyIfPreviewing)
                    const cameraZController = markerCameraSubfolder.add(override, 'z')
                        .min(floorBounds.min.z)
                        .max(floorBounds.max.z)
                        .step(0.05)
                        .name('camera Z')
                        .onChange(applyIfPreviewing)

                    // Look-at target - where the shot points, as opposed
                    // to where the camera itself sits above. Defaults to
                    // the marker's own resting badge position (see
                    // markerTargetOverrides above) but is just as
                    // freely tunable as the camera position.
                    const targetOverride = markerTargetOverrides[i]
                    const targetXController = markerCameraSubfolder.add(targetOverride, 'x')
                        .min(floorBounds.min.x)
                        .max(floorBounds.max.x)
                        .step(0.05)
                        .name('target X')
                        .onChange(applyIfPreviewing)
                    const targetYController = markerCameraSubfolder.add(targetOverride, 'y')
                        .min(MARKER_CAMERA_MIN_Y)
                        .max(MARKER_CAMERA_MAX_Y)
                        .step(0.05)
                        .name('target Y')
                        .onChange(applyIfPreviewing)
                    const targetZController = markerCameraSubfolder.add(targetOverride, 'z')
                        .min(floorBounds.min.z)
                        .max(floorBounds.max.z)
                        .step(0.05)
                        .name('target Z')
                        .onChange(applyIfPreviewing)

                    // Refreshed by syncMarkerCameraPreviewFromScene when
                    // orbiting the mouse around during preview updates
                    // these same override values out from under the GUI.
                    markerCameraValueControllers[i] = [
                        cameraXController, cameraYController, cameraZController,
                        targetXController, targetYController, targetZController
                    ]

                    // Field of view - a tighter fov reads as a longer
                    // lens/more "zoomed" shot even at the same distance;
                    // a wider one fits more of the surroundings in. Not
                    // included in markerCameraValueControllers above -
                    // orbiting can't change fov, so there's nothing for
                    // it to sync back from.
                    markerCameraSubfolder.add(markerFovOverrides[i], 'fov')
                        .min(15)
                        .max(90)
                        .step(0.5)
                        .name('FOV')
                        .onChange(applyIfPreviewing)
                })
            }

            // Camera/controls are no longer auto-framed against this
            // building's bounds - they're pinned to the values dialed in via
            // the Camera GUI panel (screenshot-captured) at their declaration
            // below instead, and left alone here.

            house.add(houseModel)
        })
    },
    undefined,
    (error) =>
    {
        console.error('Error loading house model:', error)
    }
)

/**
 * Lights
 */
// Ambient light - soft skylight fill (blue-ish, like open daytime sky).
// Color set to the client-specified hex; the trailing "FF" is an alpha
// channel from the source swatch and is dropped since light colors in
// three.js have no alpha (fully opaque either way).
const ambientLight = new THREE.AmbientLight('#beebfe', 1.7)
scene.add(ambientLight)

// Match the loading screen's background to the scene's ambient color, so
// the page doesn't cut from one flat color to a visibly different one once
// loading finishes and the 3D scene fades in behind it. Text color is
// picked for contrast against it (luminance-based) rather than assumed,
// since ambient color is tunable via the Lighting GUI folder below.
loadingScreen.style.backgroundColor = `#${ambientLight.color.getHexString()}`

const ambientLuminance = ambientLight.color.r * 0.299 + ambientLight.color.g * 0.587 + ambientLight.color.b * 0.114
const loadingForeground = ambientLuminance > 0.6 ? '#1b2733' : '#fff4e0'
loadingPercentageText.style.color = loadingForeground

// Directional light - the sun, ~11am: bright and near-white/warm.
// Position is set by updateSky() below, to match the sun's position in the Sky shader.
const directionalLight = new THREE.DirectionalLight('#fff4e0', 2.17)
scene.add(directionalLight)

//Door light
const doorLight = new THREE.PointLight('#ff7d46', 5)
doorLight.position.set(0, 2.2, 2.5)
house.add(doorLight)

// Theatre.js project/sheet - created here (before camera/controls below,
// which register the actual "Camera" object on it) so both the camera
// setup and anything else added later share the one sheet. @theatre/studio
// - the floating editor UI - is dev-only, dynamically imported behind the
// DEV check so it never ends up in the production bundle; @theatre/core
// (imported at the top) is the always-shipped playback engine.
// Passing an explicit (empty) state here is what @theatre/core itself falls
// back to internally when no state is given - the only difference is that
// providing it ourselves stops @theatre/core from throwing its "state is
// empty" error a second later, since studio is intentionally never loaded.
const theatreProject = getProject('GD_Trial', {
    state: {
        sheetsById: {},
        definitionVersion: '0.4.0',
        revisionHistory: []
    }
})
const theatreSheet = theatreProject.sheet('Scene')

const doorLightTheatre = theatreSheet.object('Door Light', {
    position: types.compound({
        x: types.number(doorLight.position.x, { range: [-10, 10] }),
        y: types.number(doorLight.position.y, { range: [-10, 10] }),
        z: types.number(doorLight.position.z, { range: [-10, 10] })
    }),
    intensity: types.number(doorLight.intensity, { range: [0, 20] }),
    color: types.rgba({ r: 1, g: 0.49, b: 0.27, a: 1 })
})

doorLightTheatre.onValuesChange((values) =>
{
    doorLight.position.set(values.position.x, values.position.y, values.position.z)
    doorLight.intensity = values.intensity
    doorLight.color.setRGB(values.color.r, values.color.g, values.color.b)
})

// Hidden by request - the floating Theatre Studio panel itself, not the
// underlying Theatre.js sheets/objects (Camera, Intro Fog, Door Light -
// those keep working/playing back regardless, studio.initialize() only
// ever controlled whether the editing UI shows up). Flip
// SHOW_THEATRE_STUDIO to true to bring the panel back.
const SHOW_THEATRE_STUDIO = false
if (import.meta.env.DEV && SHOW_THEATRE_STUDIO)
{
    import('@theatre/studio').then(({ default: studio }) => studio.initialize())
}

// Lighting balance - exposed so the ambient/sun mix and shadow strength can
// be tuned live against a reference render instead of guessed blind
const lightingFolder = gui.addFolder('Lighting').close()
lightingFolder.addColor({ color: ambientLight.color.getHexString() }, 'color')
    .name('ambient color').onChange((hex) => ambientLight.color.set(hex))
lightingFolder.add(ambientLight, 'intensity').min(0).max(3).step(0.01).name('ambient intensity')
lightingFolder.add(directionalLight, 'intensity').min(0).max(6).step(0.01).name('sun intensity')

/**
 * Sizes
 */
const sizes = {
    width: window.innerWidth,
    height: window.innerHeight
}

window.addEventListener('resize', () =>
{
    // Update sizes
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight

    // Update camera
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()

    // Update renderer
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
})

/**
 * Camera
 */
// Base camera
// Position/fov pinned to the values dialed in via the Camera GUI panel
// (screenshot-captured) instead of the small-model-safe (4, 2, 5) default.
const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 0.1, 1000)
// Mobile/tablet get their own resting position (dialed in live via the
// Camera GUI on an actual phone) - target/fov are unchanged from desktop's,
// only where the camera itself sits differs. This is also what the intro
// sequence flies TO (see introState.endPosition below), since it just
// copies whatever camera.position already is at setup time.
if (isMobileOrTabletViewport())
    camera.position.set(-57.51, 29.872, 27.326)
else
    camera.position.set(-69.0372, 23.28460, 14.00660) // re-pinned again to a newer Camera GUI screenshot (was (-61.2743, 29.82583, 45.14890)); target/fov below still unchanged
scene.add(camera)

// Controls
// Lowest the camera is allowed to orbit down to (see the clamp in tick()
// below) - keeps user interaction from reaching low, underneath-looking
// angles that break the scene's framing.
const CAMERA_MIN_Y = 1.64
const controls = new OrbitControls(camera, canvas)
controls.enableDamping = true
// Tight zoom range around the default ~82-unit starting distance - keeps
// the view close to the pinned default framing rather than letting the
// user zoom far in or out.
controls.minDistance = 60
controls.maxDistance = 100
// Target pinned the same way as the camera position above
const CAMERA_BASE_TARGET = new THREE.Vector3(0.8, -4.29999, -19.5)
// How far the orbit target is allowed to drift from that pinned point
// while panning (see the clamp in tick() below) - a small amount of give
// rather than a hard lock, so panning still feels responsive.
const CAMERA_TARGET_FREEDOM = 0.5
controls.target.copy(CAMERA_BASE_TARGET)
controls.update()

// Turntable effect - a slow, constant-speed orbit around the target.
// autoRotate advances the camera's azimuth by a fixed angle every frame (via
// controls.update() in tick() below), so it's already smooth and loops
// indefinitely with no easing - it's a constant rotation, not an eased one.
controls.autoRotate = true
controls.autoRotateSpeed = 0.5 // low = slow; the OrbitControls default (2) is a full turn every ~30s at 60fps

// Camera GUI controls - bound directly to the live camera/controls objects
// (not a copy), so dragging a slider moves the actual camera immediately.
// .listen() keeps each slider's displayed value in sync with the object in
// case it's ever repositioned from elsewhere in code.
const cameraFolder = gui.addFolder('Camera').close()
cameraFolder.add(camera.position, 'x').min(-200).max(200).step(0.1).name('position X').listen()
cameraFolder.add(camera.position, 'y').min(-200).max(200).step(0.1).name('position Y').listen()
cameraFolder.add(camera.position, 'z').min(-200).max(200).step(0.1).name('position Z').listen()
cameraFolder.add(camera, 'fov').min(10).max(120).step(1).name('field of view').listen()
    .onChange(() => camera.updateProjectionMatrix())
cameraFolder.add(camera, 'near').min(0.01).max(100).step(0.01).name('camera near').listen()
    .onChange(() => camera.updateProjectionMatrix())
cameraFolder.add(camera, 'far').min(10).max(5000).step(10).name('camera far').listen()
    .onChange(() => camera.updateProjectionMatrix())
cameraFolder.add(controls.target, 'x').min(-200).max(200).step(0.1).name('target X').listen()
    .onChange(() => controls.update())
cameraFolder.add(controls.target, 'y').min(-200).max(200).step(0.1).name('target Y').listen()
    .onChange(() => controls.update())
cameraFolder.add(controls.target, 'z').min(-200).max(200).step(0.1).name('target Z').listen()
    .onChange(() => controls.update())
cameraFolder.add(controls, 'autoRotate').name('turntable rotation')
cameraFolder.add(controls, 'autoRotateSpeed').min(0).max(5).step(0.1).name('rotation speed').listen()

// Theatre.js camera binding - a "Camera" object with position + a look-at
// target, meant to be hand-sequenced/keyframed in Studio (see the README-
// style note further down, near introPlayFolder). OrbitControls and
// Theatre both want to own camera.position/rotation every frame, so
// they're mutually exclusive: the "Theatre camera" toggle flips between
// them. Off (default) - OrbitControls (+ turntable autoRotate) behaves
// exactly as before, Theatre's values are computed but not applied. On -
// orbit/autoRotate is suspended (see the guard around controls.update()
// in tick()) and this object's position/target fully drive the camera
// instead, whether scrubbed live in Studio or played back from keyframes.
const theatreCameraState = { enabled: false }

const cameraTheatre = theatreSheet.object('Camera', {
    position: types.compound({
        x: types.number(camera.position.x, { range: [-200, 200] }),
        y: types.number(camera.position.y, { range: [-200, 200] }),
        z: types.number(camera.position.z, { range: [-200, 200] })
    }),
    target: types.compound({
        x: types.number(controls.target.x, { range: [-200, 200] }),
        y: types.number(controls.target.y, { range: [-200, 200] }),
        z: types.number(controls.target.z, { range: [-200, 200] })
    })
})

cameraTheatre.onValuesChange((values) =>
{
    if (!theatreCameraState.enabled)
        return

    camera.position.set(values.position.x, values.position.y, values.position.z)
    camera.lookAt(values.target.x, values.target.y, values.target.z)
})

// Shared by the checkbox below and the "Play intro sequence" button
// further down - both need to suspend OrbitControls the same way, since
// it and Theatre can't both own the camera in the same frame. Only flips
// the flag/controls.enabled - does NOT touch camera.position or
// controls.target itself, since "resume" means something different for
// each caller.
const setOrbitControlsSuspended = (suspended) =>
{
    theatreCameraState.enabled = suspended
    controls.enabled = !suspended
}

cameraFolder.add(theatreCameraState, 'enabled').name('Theatre camera').listen()
    .onChange((enabled) =>
    {
        setOrbitControlsSuspended(enabled)
        if (enabled)
            camera.lookAt(cameraTheatre.value.target.x, cameraTheatre.value.target.y, cameraTheatre.value.target.z)
        else
            controls.update() // hand back off from Theatre's last position/target to orbit's own state
    })

/**
 * Marker camera zoom
 * Clicking a marker (see the click handler further up, and
 * openMarkerOverlay) also flies the camera in from wherever it currently
 * is to a close-up view of that marker, easing position and look-at
 * target together on the same timeline as the intro travel
 * (easeInOutCubic, defined below near introState). OrbitControls stays
 * suspended for the whole time a marker is "zoomed in" (through the fly-
 * in AND while the overlay sits open) rather than just during the
 * animation itself - CAMERA_TARGET_FREEDOM's clamp above is centered on
 * CAMERA_BASE_TARGET, nowhere near most markers, so leaving orbit live
 * would have snapped the view straight back the instant the fly-in
 * finished. It's handed back only once flyCameraAwayFromMarker()'s own
 * reverse animation (triggered by closeMarkerOverlay) lands back on
 * exactly the pose the camera was in before the first marker was
 * clicked.
 */
const MARKER_ZOOM_DURATION = 1.2 // seconds
const MARKER_ZOOM_DISTANCE = 14 // how far back from the marker the end-of-zoom camera position sits
const MARKER_ZOOM_MIN_HEIGHT_ABOVE = 4 // keeps the zoomed-in shot from ending up level with or below the badge

const markerCameraState = {
    active: false,
    reversing: false, // true while flying back out (flyCameraAwayFromMarker) rather than in
    zoomedIn: false, // true from the moment a marker's fly-in starts until the fly-out finishes
    elapsed: 0,
    startPosition: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
    startTarget: new THREE.Vector3(),
    endTarget: new THREE.Vector3(),
    startFov: 75,
    endFov: 75,
    // The pose the camera was actually in right before the very first
    // marker click - captured once, not overwritten by a second marker
    // click while already zoomed in, so flying from marker A straight to
    // marker B and then closing the overlay still returns to the real
    // original view rather than marker A's.
    returnPosition: new THREE.Vector3(),
    returnTarget: new THREE.Vector3(),
    returnFov: 75
}
const markerCameraLookAt = new THREE.Vector3() // reused scratch vector, same reasoning as introLookAt
const MARKER_ZOOM_TURNTABLE_UP = new THREE.Vector3(0, 1, 0) // rotation axis for keeping the turntable spinning while a marker is zoomed in - see the autoRotate branch in tick()

const flyCameraToMarker = (index) =>
{
    const positionOverride = markerCameraOverrides[index]
    const targetOverride = markerTargetOverrides[index]
    const fovOverride = markerFovOverrides[index]
    if (!positionOverride || !targetOverride || !fovOverride)
        return

    if (!markerCameraState.zoomedIn)
    {
        markerCameraState.returnPosition.copy(camera.position)
        markerCameraState.returnTarget.copy(controls.target)
        markerCameraState.returnFov = camera.fov
        setOrbitControlsSuspended(true)
    }
    markerCameraState.zoomedIn = true

    markerCameraState.startPosition.copy(camera.position)
    markerCameraState.startTarget.copy(controls.target)
    markerCameraState.startFov = camera.fov
    // Exact end position/target/fov per marker, tuned by hand via
    // markerCameraFolder's sliders (see markerCameraOverrides/
    // markerTargetOverrides/markerFovOverrides, populated in the house
    // model's load callback) rather than computed from the camera's
    // current approach angle - gives full manual control over exactly
    // what each marker's close-up shot looks like.
    markerCameraState.endPosition.copy(positionOverride)
    markerCameraState.endTarget.copy(targetOverride)
    markerCameraState.endFov = fovOverride.fov

    markerCameraState.active = true
    markerCameraState.reversing = false
    markerCameraState.elapsed = 0
}

const flyCameraAwayFromMarker = () =>
{
    if (!markerCameraState.zoomedIn)
        return

    markerCameraState.startPosition.copy(camera.position)
    markerCameraState.startTarget.copy(controls.target)
    markerCameraState.startFov = camera.fov
    markerCameraState.endPosition.copy(markerCameraState.returnPosition)
    markerCameraState.endTarget.copy(markerCameraState.returnTarget)
    markerCameraState.endFov = markerCameraState.returnFov

    markerCameraState.active = true
    markerCameraState.reversing = true
    markerCameraState.elapsed = 0
}

const updateMarkerCameraAnimation = (deltaTime) =>
{
    if (!markerCameraState.active)
        return

    markerCameraState.elapsed += deltaTime
    const t = Math.min(markerCameraState.elapsed / MARKER_ZOOM_DURATION, 1)
    const eased = easeInOutCubic(t)

    camera.position.lerpVectors(markerCameraState.startPosition, markerCameraState.endPosition, eased)
    markerCameraLookAt.lerpVectors(markerCameraState.startTarget, markerCameraState.endTarget, eased)
    camera.lookAt(markerCameraLookAt)
    controls.target.copy(markerCameraLookAt)
    camera.fov = THREE.MathUtils.lerp(markerCameraState.startFov, markerCameraState.endFov, eased)
    camera.updateProjectionMatrix()

    if (t >= 1)
    {
        markerCameraState.active = false
        if (markerCameraState.reversing)
        {
            markerCameraState.zoomedIn = false
            markerCameraState.reversing = false
            setOrbitControlsSuspended(false)
            controls.update() // hand back off from this manual pose to orbit's own internal state, same as the Theatre-camera toggle does
        }
        else if (pendingMobileMarkerOverlayIndex !== -1)
        {
            // The fly-in (not the fly-out - that branch is handled just
            // above) has just finished on a mobile viewport - only now
            // does the overlay itself actually open (see the click
            // handler, which set this instead of opening it immediately).
            openMarkerOverlay(pendingMobileMarkerOverlayIndex)
            pendingMobileMarkerOverlayIndex = -1
        }
    }
}

// Live GUI preview - "preview in scene" checkbox under each marker's own
// Marker Camera subfolder (see the loop populating markerCameraFolder
// further down). Two-way, unlike a plain live readout: checking it (or
// dragging any of that marker's camera/target/FOV sliders while it's
// checked) snaps the real scene camera straight to those override values
// (applyMarkerOverrideToCamera); AND, since OrbitControls is deliberately
// left enabled and updated every frame during preview (see the
// markerCameraPreviewIndex check around controls.update() in tick())
// rather than suspended like the real click-zoom is, freely orbiting/
// dragging in the scene while previewing feeds back the other way -
// syncMarkerCameraPreviewFromScene(), also called from tick(), copies
// wherever the camera/target actually end up back into that marker's
// override values and refreshes its sliders' displayed numbers. Finding
// a shot by eye and finding it by typing numbers both end up in the same
// place. Only one marker can preview at a time (there's only one scene
// camera) - checking one unchecks whichever other was active, same as a
// radio group, since lil-gui has no native one for booleans.
let markerCameraPreviewIndex = -1
const markerCameraPreviewReturnPosition = new THREE.Vector3()
const markerCameraPreviewReturnTarget = new THREE.Vector3()
let markerCameraPreviewReturnFov = 75
let markerCameraPreviewReturnMinDistance = 60
let markerCameraPreviewReturnMaxDistance = 100
// Relaxed so OrbitControls' own distance clamp (normally 60-100, tuned
// for the wide establishing view) doesn't fight a marker's own much
// closer tuned distance every single frame - restored to whatever the
// real values were the moment preview ends.
const MARKER_CAMERA_PREVIEW_MIN_DISTANCE = 0.1
const MARKER_CAMERA_PREVIEW_MAX_DISTANCE = 1000
const markerCameraPreviewStates = [] // one { preview: bool } per marker, bound to its checkbox
const markerCameraPreviewControllers = [] // matching lil-gui controllers, so turning one off can call .updateDisplay() on the others
const markerCameraValueControllers = [] // one array of [camX, camY, camZ, targetX, targetY, targetZ] lil-gui controllers per marker, refreshed by syncMarkerCameraPreviewFromScene while orbiting

// Snaps the real scene camera straight to markerCameraOverrides[index]/
// markerTargetOverrides[index]/markerFovOverrides[index] and resyncs
// OrbitControls' own internal spherical state to match (controls.update()
// re-derives it fresh from the camera's actual position/target every
// call - the same "hand back off" trick used elsewhere in this file) so
// a mouse-drag orbit immediately afterward continues smoothly from here
// instead of jumping back to wherever orbit's old internal state was.
const applyMarkerOverrideToCamera = (index) =>
{
    const positionOverride = markerCameraOverrides[index]
    const targetOverride = markerTargetOverrides[index]
    const fovOverride = markerFovOverrides[index]
    if (!positionOverride || !targetOverride || !fovOverride)
        return

    camera.position.copy(positionOverride)
    camera.lookAt(targetOverride)
    controls.target.copy(targetOverride)
    camera.fov = fovOverride.fov
    camera.updateProjectionMatrix()
    controls.update()
}

const setMarkerCameraPreview = (index, active) =>
{
    if (active)
    {
        if (markerCameraPreviewIndex !== -1 && markerCameraPreviewIndex !== index)
        {
            markerCameraPreviewStates[markerCameraPreviewIndex].preview = false
            markerCameraPreviewControllers[markerCameraPreviewIndex].updateDisplay()
        }
        if (markerCameraPreviewIndex === -1)
        {
            markerCameraPreviewReturnPosition.copy(camera.position)
            markerCameraPreviewReturnTarget.copy(controls.target)
            markerCameraPreviewReturnFov = camera.fov
            markerCameraPreviewReturnMinDistance = controls.minDistance
            markerCameraPreviewReturnMaxDistance = controls.maxDistance
            controls.minDistance = MARKER_CAMERA_PREVIEW_MIN_DISTANCE
            controls.maxDistance = MARKER_CAMERA_PREVIEW_MAX_DISTANCE
        }
        markerCameraPreviewIndex = index
        applyMarkerOverrideToCamera(index)
    }
    else if (markerCameraPreviewIndex === index)
    {
        markerCameraPreviewIndex = -1
        controls.minDistance = markerCameraPreviewReturnMinDistance
        controls.maxDistance = markerCameraPreviewReturnMaxDistance
        camera.position.copy(markerCameraPreviewReturnPosition)
        controls.target.copy(markerCameraPreviewReturnTarget)
        camera.fov = markerCameraPreviewReturnFov
        camera.updateProjectionMatrix()
        controls.update() // same hand-back-off as the Theatre-camera toggle/flyCameraAwayFromMarker
    }
}

// Called from tick(), after controls.update() has applied whatever
// mouse-drag orbiting just happened - pulls the camera's resulting
// position/target back into the previewed marker's own override values
// and refreshes its sliders' displayed numbers, so navigating by hand in
// the scene is reflected in the GUI just as much as the GUI is reflected
// in the scene.
const syncMarkerCameraPreviewFromScene = () =>
{
    if (markerCameraPreviewIndex === -1)
        return

    const positionOverride = markerCameraOverrides[markerCameraPreviewIndex]
    const targetOverride = markerTargetOverrides[markerCameraPreviewIndex]
    if (!positionOverride || !targetOverride)
        return

    positionOverride.copy(camera.position)
    targetOverride.copy(controls.target)

    const controllers = markerCameraValueControllers[markerCameraPreviewIndex]
    if (controllers)
        controllers.forEach((controller) => controller.updateDisplay())
}

/**
 * Renderer
 */
// Capped lower than devicePixelRatio's actual max (was 2) - on a 2x/retina
// display that's 4x the fragment-shader work (Sky shader, tone mapping,
// shadow sampling, all per-pixel) versus a 1x render; 1.5 keeps most of the
// visible sharpness while meaningfully cutting that per-pixel cost.
const MAX_PIXEL_RATIO = 1.5

// logarithmicDepthBuffer - DISABLED (was true) as an experiment against
// a WebGL context-loss crash reported on a Pixel 10 XL (both this
// renderer and the loading-idol's separate one lost their context at
// the same instant, right as the first full frame was about to render
// - a GPU-process-wide reset, not an isolated bug in one renderer).
// This flag was deliberately added to fix a real, different problem -
// flickery/dithered z-fighting on coplanar geometry (cars on the
// parking lot, compound wall panels), worse the bigger the camera's
// far/near ratio is (currently 1000/0.1 = 10000x) - so turning it off
// is a trade: if the crash goes away, that z-fighting likely needs a
// separate fix (e.g. raising the near plane, or a small per-mesh
// polygonOffset) rather than this blanket renderer-wide one.
const renderer = new THREE.WebGLRenderer({
    canvas: canvas
})
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, MAX_PIXEL_RATIO))
canvas.addEventListener('webglcontextlost', handleWebglContextLost('main'), false)

// Anisotropic filtering needs the renderer to exist first
// (getMaxAnisotropy queries the GPU) - used by the floor/building
// textures further down (material[key].anisotropy there). NOT set on
// planeColorTexture/planeAlphaTexture any more (it was, originally, for
// exactly the reason anisotropic filtering exists at all - a flat
// ground plane viewed at this scene's shallow camera angle) - dropped
// along with generateMipmaps on those two specifically to work around
// a mobile GPU driver mipmap-corruption bug (see their own definition
// further up); anisotropy has no effect without mipmaps; a bit of
// aliasing on the ground at distance is the accepted trade-off.
const maxAnisotropy = renderer.capabilities.getMaxAnisotropy()

// The Sky shader (see Sky.js) outputs raw, very high-range light values and
// relies on `#include <tonemapping_fragment>` to compress them - without a
// tone mapping mode the renderer just hard-clips anything over 1.0 to flat
// white, which is what was reading as a washed-out sky regardless of the
// turbidity/rayleigh/mie values above. This is the same pairing three.js's
// own Sky example uses.
renderer.toneMapping = THREE.ACESFilmicToneMapping
renderer.toneMappingExposure = 1.14
gui.add(renderer, 'toneMappingExposure').min(0.1).max(2).step(0.01).name('exposure')

/**
 * Shadows
 */
//Renderer

renderer.shadowMap.enabled = true
// Switched from VSMShadowMap to PCFSoftShadowMap - VSM needs an extra full
// blur pass over the shadow map (using shadow.radius/blurSamples) on top of
// the depth render itself, which was a meaningful per-frame cost on a
// ~430-mesh building rendered a second time for shadows. PCFSoftShadowMap's
// softness is fixed (baked into its sampling pattern, not tunable) but is
// substantially cheaper - a reasonable trade against that per-frame cost.
renderer.shadowMap.type = THREE.PCFSoftShadowMap

//Cast and receive
directionalLight.castShadow = true

//Mapping
// Bumped to 4096 - after the performance pass shrank the shadow frustum to
// the building's own footprint (see shadowMargin below), 2048 was still
// coarse enough on the building's own facades to show up as visible
// stair-step lines (shadow acne) rather than a clean shadow edge.
directionalLight.shadow.mapSize.width = 4096
directionalLight.shadow.mapSize.height = 4096
directionalLight.shadow.near = 1

// Offsets before the depth comparison - the standard fix for shadow acne
// (self-shadowing striping on surfaces near-parallel to the light, most
// visible here on the building's own sloped roof), which resolution alone
// reduces but doesn't fully eliminate. normalBias is in world units
// (meters, since this scene is real-world scaled) - 0.02 wasn't enough to
// clear the acne on the roof, bumped to 0.1. bias works in shadow-map depth
// space instead, and helps with acne bias alone doesn't fully resolve.
// Raise normalBias further if lines persist; lower it if shadows visibly
// detach from the building instead (peter-panning).
directionalLight.shadow.normalBias = 0.5
directionalLight.shadow.bias = 0

// Live-tunable, since the right value depends on what the shadow acne
// actually looks like in the running scene - raise normalBias if lines are
// still visible, lower it if shadows detach from the building instead.
const shadowBiasFolder = gui.addFolder('Shadow bias').close()
shadowBiasFolder.add(directionalLight.shadow, 'normalBias').min(0).max(0.5).step(0.005).name('normal bias')
shadowBiasFolder.add(directionalLight.shadow, 'bias').min(-0.01).max(0.01).step(0.0001).name('depth bias')

// Initial ground coverage for the shadow camera's ortho frustum, used
// before the floor model has loaded (and as a fallback if it never does).
// Once Floor.glb loads, its real measured size replaces this - see
// updateShadowCameraFrustum's other call site in the floor's load callback.
const GROUND_EXTENT = 60

const updateShadowCameraFrustum = (size) =>
{
    const halfSize = size / 2
    directionalLight.shadow.camera.top = halfSize
    directionalLight.shadow.camera.right = halfSize
    directionalLight.shadow.camera.bottom = -halfSize
    directionalLight.shadow.camera.left = -halfSize
    // Generous margin over the light's own ~15-unit distance from the
    // origin (see updateSky's directionalLight.position) plus the frustum
    // half-size, so the far plane never clips the ground plane itself
    directionalLight.shadow.camera.far = halfSize + 30
    directionalLight.shadow.camera.updateProjectionMatrix()
}
updateShadowCameraFrustum(GROUND_EXTENT)

/**
 * Sky
 */
const sky = new Sky()
// Was 200 - far too small a radius for this scene. The sky dome is real
// depth-tested geometry, not an "always at infinity" backdrop trick - so
// anything in the scene farther than its radius from the origin (several
// Background_Tree_Atlas trees sit right around -196 on one axis, almost
// exactly at the old boundary) ends up BEHIND the dome's surface and gets
// occluded by it in the color pass. That's what was actually causing
// trees to disappear at certain camera angles/heights - the sky dome
// itself was quietly hiding them, and since Sky never participates in
// shadow rendering, their shadows kept rendering normally throughout,
// which is what confirmed this: the mesh was still fully present in the
// scene the whole time. Set comfortably large - big enough to clear every
// tree/prop with margin, but still under the main camera's far plane
// (1000) so it doesn't get clipped there instead.
sky.scale.setScalar(900)
sky.position.set(0, 0, 0)   // keep the sky's centre aligned with the ground (Floor.glb sits at the origin)
scene.add(sky)


// turbidity/rayleigh/mie tuned low for a clear, natural day (the haunted-house
// original used turbidity 10 + mie 0.1, meant for a hazy dusk look - that's
// what was reading as "white washed" for a bright clear morning).
// Sun placed by elevation/azimuth (like three.js's own Sky demo) instead of
// hand-picked XYZ, so "11am" is a readable, tunable angle rather than a guess.
const skyParameters = {
    turbidity: 4,
    rayleigh: 0.106,
    mieCoefficient: 0.0025,
    mieDirectionalG: 0.85,
    elevation: 55, // degrees above the horizon - high late-morning sun, not full overhead noon
    azimuth: 135   // degrees around the horizon - sun toward the south-east, matching a ~11am angle
}

const sunDirection = new THREE.Vector3()

const updateSky = () =>
{
    sky.material.uniforms['turbidity'].value = skyParameters.turbidity
    sky.material.uniforms['rayleigh'].value = skyParameters.rayleigh
    sky.material.uniforms['mieCoefficient'].value = skyParameters.mieCoefficient
    sky.material.uniforms['mieDirectionalG'].value = skyParameters.mieDirectionalG

    const phi = THREE.MathUtils.degToRad(90 - skyParameters.elevation)
    const theta = THREE.MathUtils.degToRad(skyParameters.azimuth)
    sunDirection.setFromSphericalCoords(1, phi, theta)

    sky.material.uniforms['sunPosition'].value.copy(sunDirection)

    // Keep the scene's actual light in sync with where the sun visually sits
    // in the sky, so shadows point the right way instead of being guessed
    // separately. Offset from the light's target (not the world origin) so
    // the sun still comes from the right relative direction once the target
    // is moved onto the actual building/floor - see where target gets
    // repositioned in the floor's load callback above.
    directionalLight.position.copy(directionalLight.target.position)
        .add(sunDirection.clone().multiplyScalar(15))
}
updateSky()

const skyFolder = gui.addFolder('Sky').close()
skyFolder.add(skyParameters, 'turbidity').min(0).max(20).step(0.1).onChange(updateSky)
skyFolder.add(skyParameters, 'rayleigh').min(0).max(4).step(0.001).onChange(updateSky)
skyFolder.add(skyParameters, 'mieCoefficient').min(0).max(0.1).step(0.0001).onChange(updateSky)
skyFolder.add(skyParameters, 'mieDirectionalG').min(0).max(1).step(0.001).onChange(updateSky)
skyFolder.add(skyParameters, 'elevation').min(0).max(90).step(0.1).name('sun elevation').onChange(updateSky)
skyFolder.add(skyParameters, 'azimuth').min(0).max(360).step(0.1).name('sun azimuth').onChange(updateSky)
skyFolder.add(sky.position, 'x').min(-100).max(100).step(0.1).name('position X')
skyFolder.add(sky.position, 'y').min(-100).max(100).step(0.1).name('position Y')
skyFolder.add(sky.position, 'z').min(-100).max(100).step(0.1).name('position Z')

/**
 * Fog
 * Mild linear depth fog so distant geometry - and the horizon line where the
 * ground meets the Sky dome - softens into the sky instead of cutting off
 * sharply. Color/near/far pinned to values tuned live via the Fog GUI
 * folder, matched against the actual sky/ambient blue so it blends with the
 * horizon rather than reading as a separate gray haze.
 *
 * near/far widened (was 60/220) - trees are scattered around at various
 * distances from the orbit target, not all equidistant from it, so as the
 * turntable auto-rotate swings the camera around, individual trees' actual
 * distance from the camera kept crossing the old 220 far distance. Past
 * that, a tree renders almost fully fog-tinted - and since the fog color
 * closely matches the sky, a fog-tinted tree (especially thin foliage,
 * which has less "mass" than a solid object) visually blends into the sky
 * and reads as disappearing, even though it's technically still drawn.
 * Pushed out far enough that this shouldn't happen for any tree within a
 * reasonable distance of the building, while still fading the truly
 * distant "Background_Tree_Atlas" backdrop trees at long range.
 */
const fog = new THREE.Fog('#cddee9', 52, 218)
scene.fog = fog

const fogFolder = gui.addFolder('Fog').close()
fogFolder.addColor({ color: `#${fog.color.getHexString()}` }, 'color')
    .name('color').onChange((hex) => fog.color.set(hex))
fogFolder.add(fog, 'near').min(0).max(300).step(1).name('near')
fogFolder.add(fog, 'far').min(10).max(1000).step(1).name('far')
fogFolder.add({ enabled: true }, 'enabled').name('enabled')
    .onChange((value) => { scene.fog = value ? fog : null })

// Theatre.js fog binding - an "Intro Fog" object with near/far, meant to
// be hand-sequenced in Studio the same way as "Camera" above. No on/off
// toggle needed here (unlike Camera vs. OrbitControls) - nothing else
// competes for fog.near/far every frame, so this can just always apply
// whenever Theatre actually drives a change (scrubbing or playback);
// when idle, the Fog GUI sliders above are free to set it directly.
const introFogTheatre = theatreSheet.object('Intro Fog', {
    near: types.number(fog.near, { range: [0, 300] }),
    far: types.number(fog.far, { range: [10, 1000] })
})
introFogTheatre.onValuesChange((values) =>
{
    fog.near = values.near
    fog.far = values.far
})

/**
 * Theatre intro sequence - preview
 * "Camera" and "Intro Fog" above are meant to be hand-sequenced/keyframed
 * in Studio - see the walkthrough alongside this code (or ask for it
 * again any time). This button just plays whatever's currently authored,
 * without needing a full page reload each time you want to check it:
 * suspends OrbitControls for the duration (same mechanism as the "Theatre
 * camera" toggle), plays the sequence from 0 to INTRO_TRAVEL_DURATION
 * (declared further down, with the rest of the actual intro logic - this
 * button used to have its own separate INTRO_PREVIEW_DURATION constant,
 * which meant the preview and the real intro could silently drift out of
 * sync if only one got updated; sharing the one constant means they can't),
 * then hands control back to OrbitControls at wherever the sequence left
 * the camera.
 */
const introPlayFolder = gui.addFolder('Theatre Intro').close()
introPlayFolder.add({
    play: () =>
    {
        setOrbitControlsSuspended(true)
        theatreSheet.sequence.position = 0
        theatreSheet.sequence.play({ range: [0, INTRO_TRAVEL_DURATION] })
            .then(() => setOrbitControlsSuspended(false))
    }
}, 'play').name('▶ Play intro sequence')

/**
 * Clouds
 * (no ready-made cloud addon in three.js - built from soft billboard sprites,
 * cheap and reads naturally against the Sky)
 */
const cloudCanvas = document.createElement('canvas')
cloudCanvas.width = 128
cloudCanvas.height = 128
const cloudCtx = cloudCanvas.getContext('2d')
const cloudGradient = cloudCtx.createRadialGradient(64, 64, 0, 64, 64, 64)
cloudGradient.addColorStop(0, 'rgba(255,255,255,0.8)')
cloudGradient.addColorStop(0.5, 'rgba(255,255,255,0.35)')
cloudGradient.addColorStop(1, 'rgba(255,255,255,0)')
cloudCtx.fillStyle = cloudGradient
cloudCtx.fillRect(0, 0, 128, 128)
const cloudTexture = new THREE.CanvasTexture(cloudCanvas)
// Mipmaps aren't needed for a small billboard sprite viewed at these
// distances, and generating them from a canvas source with a soft
// alpha gradient is exactly the combination that triggers a known iOS/
// macOS Safari WebGL bug - corrupted lower mip levels show up as
// colored speckling (reported as "red dotted" clouds) that plain
// linear filtering never runs into.
cloudTexture.generateMipmaps = false
cloudTexture.minFilter = THREE.LinearFilter
cloudTexture.magFilter = THREE.LinearFilter

const cloudMaterial = new THREE.SpriteMaterial({
    map: cloudTexture,
    color: '#fffaf0',
    transparent: true,
    opacity: 0.6,
    depthWrite: false
})

const clouds = new THREE.Group()
scene.add(clouds)

// Clouds drift in a straight line across the sky - real wind, one shared
// direction (+X) - each cluster at its own randomized speed, rather than
// orbiting around the building (which read as the sky slowly turning, not
// clouds passing overhead). Wrapped back to the upwind edge once a cluster
// drifts past the far side, so the sky stays populated indefinitely and
// there's always something passing no matter which way the camera orbits.
const cloudClusters = []
const CLOUD_CLUSTER_COUNT = 10
const CLOUD_FIELD_HALF_SIZE = 100 // wind-axis (X) extent before wrapping

for (let i = 0; i < CLOUD_CLUSTER_COUNT; i++)
{
    const cluster = new THREE.Group()
    const puffCount = 4 + Math.floor(Math.random() * 3)

    for (let j = 0; j < puffCount; j++)
    {
        const puff = new THREE.Sprite(cloudMaterial)
        const puffScale = 8 + Math.random() * 8
        puff.scale.set(puffScale, puffScale * 0.55, 1)
        puff.position.set(
            (Math.random() - 0.5) * 12,
            (Math.random() - 0.5) * 2,
            (Math.random() - 0.5) * 12
        )
        cluster.add(puff)
    }

    // Spread across the whole field up front (not bunched at one edge) so
    // it's already populated on load - only the wraparound in tick() needs
    // to reset to the upwind edge specifically
    const x = (Math.random() * 2 - 1) * CLOUD_FIELD_HALF_SIZE
    const z = (Math.random() - 0.5) * 140
    const height = 12 + Math.random() * 10

    cluster.position.set(x, height, z)
    clouds.add(cluster)

    cloudClusters.push({
        group: cluster,
        height,
        // Same direction for every cluster (like real wind) but each at its
        // own pace, so they visibly pass each other rather than moving in
        // lockstep. Fast enough to clearly read as motion within a couple
        // of seconds, slow enough to still feel like drifting cloud, not a
        // sped-up timelapse.
        driftSpeed: 3 + Math.random() * 3,
        bobAmplitude: 0.4 + Math.random() * 0.6,
        bobSpeed: 0.15 + Math.random() * 0.15,
        bobPhase: Math.random() * Math.PI * 2
    })
}

/**
 * Birds
 * No GLB - procedural, the same technique as the clouds above: a
 * canvas-drawn silhouette on a billboard sprite. Two frames (wings up/down)
 * are drawn once and swapped on each bird's own timer for a simple wingbeat
 * cue, cheap enough at this distance/scale that a full animated mesh isn't
 * needed. Unlike the clouds (one shared wind direction), each bird wanders
 * independently - picking a new random point to fly toward every few
 * seconds and gradually steering onto it - so flight paths read as actually
 * random rather than a fixed loop or formation.
 */
const createBirdWingTexture = (wingsUp) =>
{
    const size = 64
    const canvas = document.createElement('canvas')
    canvas.width = size
    canvas.height = size
    const ctx = canvas.getContext('2d')

    // Classic distant-bird cue: a shallow "M"/gull-wing shape, two curved
    // strokes meeting at the body - wingsUp lifts the tips further above
    // the body than the down-flap frame, which is what actually reads as
    // flapping once the two frames alternate.
    const midX = size / 2
    const midY = size / 2
    const wingSpan = size * 0.4
    const wingLift = wingsUp ? size * 0.22 : size * 0.06

    ctx.strokeStyle = 'rgba(35, 35, 35, 0.9)'
    ctx.lineWidth = 3
    ctx.lineCap = 'round'
    ctx.beginPath()
    ctx.moveTo(midX - wingSpan, midY - wingLift)
    ctx.quadraticCurveTo(midX - wingSpan * 0.3, midY + size * 0.05, midX, midY)
    ctx.quadraticCurveTo(midX + wingSpan * 0.3, midY + size * 0.05, midX + wingSpan, midY - wingLift)
    ctx.stroke()

    const texture = new THREE.CanvasTexture(canvas)
    // Same Safari mipmap-corruption risk as the cloud texture above,
    // same fix - not needed at this sprite's size/viewing distance anyway.
    texture.generateMipmaps = false
    texture.minFilter = THREE.LinearFilter
    texture.magFilter = THREE.LinearFilter
    return texture
}

const birdWingUpTexture = createBirdWingTexture(true)
const birdWingDownTexture = createBirdWingTexture(false)

const birds = new THREE.Group()
scene.add(birds)

// A handful of small flocks rather than many independently-wandering
// birds - a shared "leader" per flock wanders the sky slowly and smoothly,
// and the rest trail it in a loose V, which is what actually reads as a
// bird "flying pattern" instead of a scatter of things randomly darting
// around.
const BIRD_FLOCK_COUNT = 2
// Local-space offsets from the leader (a loose V, wingtip birds slightly
// behind/below/either side, one further back on the centerline) - rotated
// onto the leader's own current heading each frame in updateBirds().
const BIRD_FOLLOWER_OFFSETS = [
    new THREE.Vector3(-2.4, -0.3, -2.8),
    new THREE.Vector3(2.4, -0.2, -2.6),
    new THREE.Vector3(0, -0.5, -4.6)
]
const BIRD_FIELD_HALF_SIZE = 70 // wander bounds on X/Z, centered on the origin like the cloud field
const BIRD_HEIGHT_RANGE = [18, 38] // Y range flocks wander within, above the building
const BIRD_SPEED_RANGE = [3, 4.5] // meters/second-ish, model's real-world scale - slow, unhurried gliding
const BIRD_FLAP_INTERVAL_RANGE = [0.16, 0.26] // seconds between wingbeat toggles - randomized per bird so they don't all flap in unison
const BIRD_WANDER_INTERVAL_RANGE = [6, 10] // seconds between a flock picking a new random destination - long, easy sweeps rather than frequent direction changes
const BIRD_TURN_RATE = 0.6 // how fast (per second) a flock's heading can turn toward its new wander target - low, so turns read as a slow, wide bank rather than a snap
const BIRD_FOLLOWER_LAG = 2.5 // how briskly a follower eases toward its formation slot - lower reads as more of an organic trail, not a rigid attachment
const BIRD_BOB_AMPLITUDE = [0.3, 0.6] // gentle per-bird vertical undulation, layered on top of the flock's own flight path
const BIRD_BOB_SPEED = [0.5, 0.9]

const createBird = () =>
{
    const material = new THREE.SpriteMaterial({
        map: birdWingUpTexture,
        transparent: true,
        depthWrite: false
    })
    const bird = new THREE.Sprite(material)
    const scale = 1.2 + Math.random() * 0.6
    bird.scale.set(scale, scale, 1)
    birds.add(bird)

    return {
        sprite: bird,
        flapTimer: Math.random() * BIRD_FLAP_INTERVAL_RANGE[1],
        flapInterval: THREE.MathUtils.lerp(BIRD_FLAP_INTERVAL_RANGE[0], BIRD_FLAP_INTERVAL_RANGE[1], Math.random()),
        wingsUp: true,
        bobAmplitude: THREE.MathUtils.lerp(BIRD_BOB_AMPLITUDE[0], BIRD_BOB_AMPLITUDE[1], Math.random()),
        bobSpeed: THREE.MathUtils.lerp(BIRD_BOB_SPEED[0], BIRD_BOB_SPEED[1], Math.random()),
        bobPhase: Math.random() * Math.PI * 2
    }
}

const birdFlocks = []

for (let f = 0; f < BIRD_FLOCK_COUNT; f++)
{
    const leaderBird = createBird()
    const startPosition = new THREE.Vector3(
        (Math.random() * 2 - 1) * BIRD_FIELD_HALF_SIZE,
        THREE.MathUtils.lerp(BIRD_HEIGHT_RANGE[0], BIRD_HEIGHT_RANGE[1], Math.random()),
        (Math.random() * 2 - 1) * BIRD_FIELD_HALF_SIZE
    )
    leaderBird.sprite.position.copy(startPosition)

    const speed = THREE.MathUtils.lerp(BIRD_SPEED_RANGE[0], BIRD_SPEED_RANGE[1], Math.random())
    const followers = BIRD_FOLLOWER_OFFSETS.map((offset) =>
    {
        const followerBird = createBird()
        followerBird.sprite.position.copy(startPosition).add(offset)
        return { ...followerBird, offset }
    })

    birdFlocks.push({
        leader: leaderBird,
        followers,
        velocity: new THREE.Vector3(Math.random() - 0.5, 0, Math.random() - 0.5).normalize().multiplyScalar(speed),
        wanderTarget: new THREE.Vector3(),
        wanderTimer: Math.random() * BIRD_WANDER_INTERVAL_RANGE[0] // staggered so both flocks don't re-target on the same frame
    })
}

// Reused scratch objects for the per-frame bird update below - avoids
// allocating new Vector3s/Matrix4s for every bird on every frame.
const birdDesiredDirection = new THREE.Vector3()
const birdSteerDirection = new THREE.Vector3()
const birdForward = new THREE.Vector3()
const birdRight = new THREE.Vector3()
const birdUp = new THREE.Vector3(0, 1, 0)
const birdFormationBasis = new THREE.Matrix4()
const birdFollowerTarget = new THREE.Vector3()

const updateBirdWingFlap = (bird, deltaTime) =>
{
    bird.flapTimer -= deltaTime
    if (bird.flapTimer <= 0)
    {
        bird.wingsUp = !bird.wingsUp
        bird.sprite.material.map = bird.wingsUp ? birdWingUpTexture : birdWingDownTexture
        bird.sprite.material.needsUpdate = true
        bird.flapTimer = bird.flapInterval
    }
}

const updateBirds = (deltaTime) =>
{
    for (const flock of birdFlocks)
    {
        // Pick a new random destination within the field bounds every so
        // often, rather than following any fixed path.
        flock.wanderTimer -= deltaTime
        if (flock.wanderTimer <= 0)
        {
            flock.wanderTarget.set(
                (Math.random() * 2 - 1) * BIRD_FIELD_HALF_SIZE,
                THREE.MathUtils.lerp(BIRD_HEIGHT_RANGE[0], BIRD_HEIGHT_RANGE[1], Math.random()),
                (Math.random() * 2 - 1) * BIRD_FIELD_HALF_SIZE
            )
            flock.wanderTimer = THREE.MathUtils.lerp(BIRD_WANDER_INTERVAL_RANGE[0], BIRD_WANDER_INTERVAL_RANGE[1], Math.random())
        }

        // Gradually steer velocity toward the target direction (a slow turn
        // rate, not an instant snap) - reads as a wide, easy bank into a new
        // heading rather than teleporting onto it. Speed itself is
        // preserved, only direction changes.
        const speed = flock.velocity.length()
        birdDesiredDirection.subVectors(flock.wanderTarget, flock.leader.sprite.position).normalize()
        birdSteerDirection.copy(flock.velocity).normalize().lerp(birdDesiredDirection, Math.min(BIRD_TURN_RATE * deltaTime, 1))
        if (birdSteerDirection.lengthSq() > 0)
            flock.velocity.copy(birdSteerDirection.normalize()).multiplyScalar(speed)

        flock.leader.sprite.position.addScaledVector(flock.velocity, deltaTime)
        updateBirdWingFlap(flock.leader, deltaTime)
        flock.leader.bobPhase += deltaTime * flock.leader.bobSpeed
        flock.leader.sprite.position.y += Math.sin(flock.leader.bobPhase) * flock.leader.bobAmplitude * deltaTime

        // Followers ease toward their own formation slot (the leader's
        // position plus their offset, rotated onto the leader's current
        // heading) rather than snapping rigidly onto it - that lag is what
        // reads as an organic trailing flock instead of a fixed rigid
        // shape glued to the leader.
        birdForward.copy(flock.velocity).normalize()
        birdRight.crossVectors(birdUp, birdForward).normalize()
        birdFormationBasis.makeBasis(birdRight, birdUp, birdForward)

        for (const follower of flock.followers)
        {
            birdFollowerTarget.copy(follower.offset).applyMatrix4(birdFormationBasis).add(flock.leader.sprite.position)
            const lagFactor = 1 - Math.exp(-BIRD_FOLLOWER_LAG * deltaTime)
            follower.sprite.position.lerp(birdFollowerTarget, lagFactor)
            updateBirdWingFlap(follower, deltaTime)
            follower.bobPhase += deltaTime * follower.bobSpeed
            follower.sprite.position.y += Math.sin(follower.bobPhase) * follower.bobAmplitude * deltaTime
        }
    }
}

/**
 * Intro camera animation
 * Plays once, right as the loading screen finishes fading out. Opens on an
 * aerial view over the building, "inside" a bank of cloud (dense fog plus
 * a handful of oversized cloud puffs parked right in front of the camera),
 * then eases the camera down to the pinned default view while the fog
 * clears and those puffs drift off and fade - reads as flying out of cloud
 * cover into the establishing shot, rather than a hard cut or a flat
 * screen-wipe.
 *
 * Hybrid: if you've hand-sequenced/keyframed "Camera" in Theatre Studio
 * (right-click each prop in the Details panel - Sequence - keyframe it at
 * 0s and again wherever INTRO_TRAVEL_DURATION sits), beginIntroTravel()
 * below detects that and plays the real Theatre sequence, so the timeline
 * is what actually drives it. If nothing's been authored (or the browser's
 * local Studio storage ever gets cleared again - already happened once),
 * it automatically falls back to this file's own plain eased tween
 * instead - so there's always a working intro, Studio setup or not.
 *
 * Split into a "prepare" step and a "begin" step because of the loading
 * screen's own fade timing: prepareIntroAerialView() runs the instant
 * loading finishes and snaps everything into its starting state with no
 * easing - safe to do since it's still hidden behind the (still opaque)
 * loading screen. beginIntroTravel() doesn't start the actual eased tween
 * until that screen has fully faded out (see loadingManager.onLoad
 * above), so the very first frame the user actually sees is the static
 * aerial/cloudy shot, not something already mid-travel.
 */
const INTRO_TRAVEL_DURATION = 8 // seconds, once the travel actually starts (i.e. after the loading screen has fully faded) - was 4, slowed down for a more cinematic pace
const introClouds = new THREE.Group()
scene.add(introClouds)

const introState = {
    active: false,
    // Whether playback is coming from a real Theatre.js sequence (true) or
    // the hand-rolled fallback tween below (false) - see beginIntroTravel().
    drivenByTheatre: false,
    elapsed: 0,
    startPosition: new THREE.Vector3(),
    endPosition: new THREE.Vector3(),
    startTarget: new THREE.Vector3(),
    endTarget: new THREE.Vector3(),
    startFogNear: fog.near,
    startFogFar: fog.far,
    endFogNear: fog.near,
    endFogFar: fog.far,
    puffs: [] // { sprite, spawnPosition, dispersedPosition, baseOpacity }, populated by prepareIntroAerialView()
}
const introLookAt = new THREE.Vector3() // reused scratch vector for the per-frame lerped look-at target, avoids allocating one every frame

// Ramps controls.autoRotateSpeed back up from 0 once the intro hands off
// to OrbitControls (see finishIntroTravel()/the ramp block in tick()),
// instead of switching it on at full speed instantly.
const AUTO_ROTATE_RAMP_DURATION = 2 // seconds
const autoRotateRamp = { active: false, elapsed: 0, targetSpeed: 0 }

// Quint (a stronger slow-in/slow-out than cubic) held too long at the
// aerial start specifically - back to cubic's gentler ease, which still
// settles smoothly into both ends without lingering as noticeably at the
// top before it starts moving.
const easeInOutCubic = (t) => (t < 0.5 ? 4 * t * t * t : 1 - Math.pow(-2 * t + 2, 3) / 2)

const prepareIntroAerialView = () =>
{
    // Aerial start pose: straight above the building's actual center, high
    // enough to comfortably fit its footprint in view, with a small
    // horizontal offset and a slight tilt (rather than a perfectly
    // vertical look-down) so it reads as a cinematic aerial shot instead
    // of a flat orthographic snapshot.
    // Was 1.6x / 40 minimum - high/wide enough that the frame's edges (a
    // 75-degree FOV, viewed at an angle rather than straight down) could
    // extend past the actual rendered ground/floor extent, showing empty
    // black space in a corner instead of scene content. Brought in closer
    // so the aerial frame stays within what's actually there to see.
    const aerialHeight = Math.max(introBuildingFootprint * 1.1, 28)
    introState.startPosition.set(
        buildingCenter.x + introBuildingFootprint * 0.15,
        buildingCenter.y + aerialHeight,
        buildingCenter.z + introBuildingFootprint * 0.35
    )
    introState.startTarget.copy(buildingCenter)

    // End pose is wherever the camera/controls are actually pinned to
    // right now (the values set earlier in the Camera section) - read
    // live rather than duplicating those numbers, so re-tuning them there
    // doesn't also require updating this.
    introState.endPosition.copy(camera.position)
    introState.endTarget.copy(controls.target)

    // Fog starts closed-in (like flying inside cloud) and opens back out
    // to whatever near/far are actually set to right now - captured live
    // so this respects any tuning done via the Fog GUI folder instead of
    // assuming its defaults.
    introState.startFogNear = 0
    introState.startFogFar = Math.max(introBuildingFootprint * 0.3, 6)
    introState.endFogNear = fog.near
    introState.endFogFar = fog.far

    camera.position.copy(introState.startPosition)
    camera.lookAt(introState.startTarget)
    fog.near = introState.startFogNear
    fog.far = introState.startFogFar
    setOrbitControlsSuspended(true)

    // A handful of oversized puffs scattered close around the starting
    // camera position, biased toward the view direction (between the
    // camera and the building) so they actually occlude the opening shot -
    // sprites always face the camera regardless of exact position, so
    // "close and roughly in front" is enough to read as "inside cloud".
    // Each gets its own cloned material (not the shared cloudMaterial used
    // by the ambient cloudClusters) so fading one doesn't fade them all.
    // Positions are precomputed start/end pairs (rather than an accumulated
    // per-frame drift) so they can be placed with a plain lerp each frame.
    introClouds.clear()
    introState.puffs = []
    const INTRO_PUFF_COUNT = 7
    const viewDirection = introState.startTarget.clone().sub(introState.startPosition).normalize()

    for (let i = 0; i < INTRO_PUFF_COUNT; i++)
    {
        const puff = new THREE.Sprite(cloudMaterial.clone())
        const puffScale = 30 + Math.random() * 20
        puff.scale.set(puffScale, puffScale * 0.6, 1)

        const forwardDistance = 6 + Math.random() * 20
        const spawnPosition = introState.startPosition.clone()
            .addScaledVector(viewDirection, forwardDistance)
            .add(new THREE.Vector3(
                (Math.random() - 0.5) * 18,
                (Math.random() - 0.5) * 10,
                (Math.random() - 0.5) * 18
            ))
        const driftDirection = new THREE.Vector3(Math.random() - 0.5, Math.random() * 0.4, Math.random() - 0.5).normalize()
        const dispersedPosition = spawnPosition.clone().addScaledVector(driftDirection, 20 + Math.random() * 20)

        const baseOpacity = 0.5 + Math.random() * 0.3
        puff.position.copy(spawnPosition)
        puff.material.opacity = baseOpacity
        introClouds.add(puff)

        introState.puffs.push({ sprite: puff, spawnPosition, dispersedPosition, baseOpacity })
    }
}

const finishIntroTravel = () =>
{
    introState.active = false
    introState.drivenByTheatre = false
    fog.near = introState.endFogNear
    fog.far = introState.endFogFar
    controls.target.copy(introState.endTarget)
    setOrbitControlsSuspended(false)
    // No controls.update() here - tick()'s own trailing call already does
    // this exactly once per frame, now that theatreCameraState.enabled is
    // false. Calling it a second time back-to-back (essentially zero real
    // time apart) let OrbitControls' damping/auto-rotate math produce a
    // tiny inconsistent camera jitter right at this exact frame - and
    // since fog blending is distance-to-camera dependent per-fragment,
    // that's what was actually showing up as a one-frame fog flicker at
    // the end of the intro.

    // The intro's own eased tween decelerates smoothly to a dead stop at
    // its final frame - if autoRotate then kicks in immediately at its
    // full constant speed, motion goes from "smoothly slowing down" to
    // "suddenly moving again" in one frame, which reads as a jerk even
    // though nothing actually jumps position. Ramping the speed in from 0
    // over a couple of seconds (see the ramp block in tick()) instead of
    // switching it on abruptly smooths over exactly that handoff.
    if (controls.autoRotate)
    {
        autoRotateRamp.active = true
        autoRotateRamp.elapsed = 0
        autoRotateRamp.targetSpeed = controls.autoRotateSpeed
        controls.autoRotateSpeed = 0
    }

    for (const puff of introState.puffs)
    {
        introClouds.remove(puff.sprite)
        puff.sprite.material.dispose()
    }
    introState.puffs = []

    // Site header (logo + menu) + hero text only reveal once the whole
    // intro camera sequence has actually finished - not before, and not
    // just once the loading screen itself faded (which is well before
    // this, right as the intro travel is only just starting).
    siteHeader.classList.add('site-header--visible')
    hero.classList.add('hero--visible')
    markerDial.classList.add('marker-dial--visible')

    // Building markers fade/pop in on the same cue - see their opacity-0/
    // scale-0 starting state in the house model's load callback, and the
    // elastic-ease growth itself in tick(). Recorded via timer.getElapsed()
    // (not a local variable in tick()) since this runs from outside it.
    markersRevealTriggered = true
    markersRevealStartTime = timer.getElapsed()
}

const beginIntroTravel = () =>
{
    introState.active = true
    introState.elapsed = 0

    // If "Camera" AND "Intro Fog" have actually been hand-sequenced/
    // keyframed in Theatre Studio (see those objects and the "Theatre
    // Intro" preview button further up), trust that authored timeline and
    // play the real Theatre sequence instead of this file's built-in
    // tween below. Every relevant leaf prop is checked, not just one - a
    // one-off/partial experiment in Studio (e.g. sequencing just Camera's
    // position.x at some point without finishing the rest, particularly
    // "Intro Fog") used to be enough to flip this on, which meant fog
    // never actually got driven at all (frozen at its "closed in" start
    // value - "fog is staying") and the camera could jerk at the handoff
    // since the rest of its data was never authored either. This is why
    // the hand-rolled tween still exists at all rather than being
    // replaced outright: Theatre's keyframes only ever live in the
    // browser's local Studio storage, not in this file, and can be lost
    // (already happened once) - so there's always a guaranteed fallback.
    const sequencedProps = [
        cameraTheatre.props.position.x, cameraTheatre.props.position.y, cameraTheatre.props.position.z,
        cameraTheatre.props.target.x, cameraTheatre.props.target.y, cameraTheatre.props.target.z,
        introFogTheatre.props.near, introFogTheatre.props.far
    ]
    const hasAuthoredSequence = sequencedProps.every((prop) => theatreSheet.sequence.__experimental_getKeyframes(prop).length > 0)
    introState.drivenByTheatre = hasAuthoredSequence

    if (hasAuthoredSequence)
    {
        setOrbitControlsSuspended(true)
        theatreSheet.sequence.position = 0
        theatreSheet.sequence.play({ range: [0, INTRO_TRAVEL_DURATION] }).then(finishIntroTravel)
    }
}

const updateIntroAnimation = (deltaTime) =>
{
    if (!introState.active)
        return

    introState.elapsed += deltaTime
    const t = Math.min(introState.elapsed / INTRO_TRAVEL_DURATION, 1)
    const eased = easeInOutCubic(t)

    // The cloud puffs always animate on this same eased timeline
    // regardless of what's driving the camera/fog below - Theatre only
    // knows about the "Camera"/"Intro Fog" props, not these puffs, so
    // they stay on the hand-rolled schedule either way. Keeping
    // INTRO_TRAVEL_DURATION matched to wherever your authored keyframes
    // actually end (same constant the preview button uses) is what keeps
    // this in sync with a Theatre-driven run too.
    for (const puff of introState.puffs)
    {
        puff.sprite.position.lerpVectors(puff.spawnPosition, puff.dispersedPosition, eased)
        puff.sprite.material.opacity = puff.baseOpacity * (1 - eased)
    }

    // When Theatre is driving (see beginIntroTravel), it owns
    // camera/fog and its own sequence.play() promise calls
    // finishIntroTravel() when done - this file's own tween and its
    // t>=1 check are skipped entirely in that case.
    if (introState.drivenByTheatre)
        return

    camera.position.lerpVectors(introState.startPosition, introState.endPosition, eased)
    introLookAt.lerpVectors(introState.startTarget, introState.endTarget, eased)
    camera.lookAt(introLookAt)

    fog.near = THREE.MathUtils.lerp(introState.startFogNear, introState.endFogNear, eased)
    fog.far = THREE.MathUtils.lerp(introState.startFogFar, introState.endFogFar, eased)

    if (t >= 1)
        finishIntroTravel()
}

// Reused scratch objects for the per-frame vehicle update below - avoids
// allocating a new Vector3/Matrix4/Quaternion for every car on every frame.
const vehicleTangent = new THREE.Vector3()
const vehicleRight = new THREE.Vector3()
const vehicleUp = new THREE.Vector3(0, 1, 0)
const vehicleLookMatrix = new THREE.Matrix4()
const vehicleFacingQuaternion = new THREE.Quaternion()

// Sideways offset from the road's own centerline, so opposite-direction
// traffic ends up on opposite physical sides of the road instead of both
// hugging the centerline and colliding head-on - these paths are single
// centerlines, not separate per-lane curves. In the same local-space units
// as the path geometry itself (pre real-world scaling); tune by feel.
const VEHICLE_LANE_OFFSET = 0.06 // was 0.15, reduced 60%

// This car pack's own modeled "forward" axis doesn't line up with -Z (the
// axis the lookAt matrix below assumes as forward) - reported as driving
// squarely sideways, a 90-degree heading error - so this nudges the final
// compass heading by that much, applied last/in world space so it can't
// disturb the car's own baked-in tilt (baseQuaternion) or the lookAt
// pitch/roll. Flip the sign (or try Math.PI for a full 180) if it's ever
// still wrong after this.
const VEHICLE_FORWARD_YAW_CORRECTION = -Math.PI / 2 // was +90 degrees, which read as sideways in the other direction (facing squarely backwards) - flipped
const vehicleYawCorrection = new THREE.Quaternion().setFromAxisAngle(vehicleUp, VEHICLE_FORWARD_YAW_CORRECTION)

// Minimum following distance (same units as the path geometry itself,
// pre real-world scaling) kept between two vehicles sharing the same road
// AND currently travelling the same direction - what was letting a faster
// car drive straight through a slower one ahead of it, since each vehicle
// only ever tracked its own position along its own curve with no
// awareness of any other vehicle at all. Opposite-direction traffic on the
// same road doesn't need this - VEHICLE_LANE_OFFSET above already keeps
// it on a separate side, so it was never at risk of this specific overlap.
//
// One car length - measured directly against the GLB (checked each of the
// 5 car meshes' own bounding box against their authored node scale, in
// this same houseModel-local space the path curves live in: they range
// ~0.0997-0.1055, so 0.1 is a representative "1 car length" across all of
// them, not a guess). Was 0.35 (a full comfortable cruising distance,
// several car-lengths) - shrunk down, since at 0.35 a faster car caught
// behind a slower one got glued at that exact gap indefinitely (there's no
// passing/lane-changing here, so once caught it can't get free again) -
// identical relative spacing at every moment read as an artificial,
// uniform parade. At one car length, it only kicks in right at near-
// contact, keeping speeds/spacing looking as randomly varied as before
// everywhere except the instant two vehicles would actually clip into
// each other.
const VEHICLE_MIN_GAP = 0.5 // was 0.1 (~1 car length) - widened to ~5 car lengths
const vehicleLanes = new Map() // curve -> Map(direction -> vehicle[]), rebuilt fresh each frame

// How much faster (multiplier on its own base speed) a leader accelerates
// once something's tailgating it, and how briskly it eases toward that -
// in EITHER direction, ramping up when it starts being tailgated just as
// gradually as it eases back down to normal (1x) afterward, rather than
// snapping straight to the target the instant it's needed (which read as
// a sudden nitro-boost jolt) - so the leader shares in fixing the gap
// instead of the follower being the only side that ever reacts to it.
const VEHICLE_SPEED_BOOST = 5
const VEHICLE_BOOST_EASE_RATE = 1.2 // per second - same rate ramping up or down

const resolveVehicleOvertaking = () =>
{
    // Default target is "no boost" - anything still too close sets its
    // own target back up to VEHICLE_SPEED_BOOST in the pass below; this
    // only decides WHAT each vehicle should be easing toward this frame,
    // not its actual current speedBoost value yet (that's eased toward
    // this target in one shared step at the very end, so ramping up and
    // recovering back down go through the exact same smoothing).
    for (const vehicle of vehicles)
        vehicle.speedBoostTarget = 1

    vehicleLanes.clear()
    for (const vehicle of vehicles)
    {
        let byDirection = vehicleLanes.get(vehicle.curve)
        if (!byDirection)
        {
            byDirection = new Map()
            vehicleLanes.set(vehicle.curve, byDirection)
        }
        let group = byDirection.get(vehicle.direction)
        if (!group)
        {
            group = []
            byDirection.set(vehicle.direction, group)
        }
        group.push(vehicle)
    }

    for (const byDirection of vehicleLanes.values())
    {
        for (const group of byDirection.values())
        {
            if (group.length < 2)
                continue

            const curveLength = group[0].curve.getLength()
            const minGapT = curveLength > 0 ? VEHICLE_MIN_GAP / curveLength : 0

            // "Forward" - t itself for a vehicle currently driving toward
            // t=1, negated for one currently driving toward t=0 - so
            // whichever way this particular group is travelling, a larger
            // forward value always means "further along in that direction",
            // letting every vehicle in the group be ordered leader-to-
            // follower on one consistent scale regardless of which way
            // that actually is.
            group.sort((a, b) => (b.t * b.direction) - (a.t * a.direction))

            for (let i = 1; i < group.length; i++)
            {
                const ahead = group[i - 1]
                const behind = group[i]
                const maxForward = (ahead.t * ahead.direction) - minGapT
                const behindForward = behind.t * behind.direction
                if (behindForward > maxForward)
                {
                    // Hard clamp is still the guarantee against visual
                    // overlap THIS exact frame - the leader's own boost
                    // (below) only changes how fast it moves starting next
                    // frame (and ramps in gradually even then, see the
                    // easing pass below), so both need to happen together:
                    // the clamp keeps them from touching right now, the
                    // boost is what actually pulls them apart afterward.
                    behind.t = THREE.MathUtils.clamp(maxForward * behind.direction, 0, 1)
                    ahead.speedBoostTarget = Math.max(ahead.speedBoostTarget, VEHICLE_SPEED_BOOST)
                }
            }
        }
    }
}

// Finds where two road paths actually cross, directly off their own raw
// vertices (curve.rawPoints, attached where each curve is built above) -
// checked (via a standalone script against this exact GLB) that where two
// road paths genuinely meet, they share a near-exact coincident vertex
// right at that point: RoadPath01<->02 came out ~0.04 apart, RoadPath01<->
// 03 ~0.02 apart, while RoadPath02<->03 (which don't actually meet at all)
// came out ~5.8 apart - a huge, unambiguous gap between "real junction"
// and "just two separate roads", and one exact-vertex search finds it
// precisely instead of only approximately, the way sampling a handful of
// points along the smoothed curve and checking closest approach would.
// Cheap enough to check exhaustively (a handful of paths, a few hundred
// points each) since this only ever runs once, right after the road
// curves are built, not per frame. JUNCTION_DISTANCE is how close that
// closest raw-vertex pair needs to be to actually count as a real
// crossing.
const findRoadJunctions = (curves) =>
{
    const JUNCTION_DISTANCE = 0.25
    const junctions = []

    for (let i = 0; i < curves.length; i++)
    {
        for (let j = i + 1; j < curves.length; j++)
        {
            const curveA = curves[i]
            const curveB = curves[j]
            const pointsA = curveA.rawPoints
            const pointsB = curveB.rawPoints
            if (!pointsA || !pointsB)
                continue

            let closestDistSq = Infinity
            let closestIndexA = 0
            let closestIndexB = 0

            for (let a = 0; a < pointsA.length; a++)
            {
                for (let b = 0; b < pointsB.length; b++)
                {
                    const distSq = pointsA[a].distanceToSquared(pointsB[b])
                    if (distSq < closestDistSq)
                    {
                        closestDistSq = distSq
                        closestIndexA = a
                        closestIndexB = b
                    }
                }
            }

            if (Math.sqrt(closestDistSq) < JUNCTION_DISTANCE)
            {
                // CatmullRomCurve3 with the 'catmullrom' curveType (as
                // built above) parameterizes uniformly, so vertex index i
                // maps directly to t = i / (count - 1) - no separate
                // lookup/refinement needed to go from "which raw vertex"
                // to "which t value along the driving curve".
                const tA = pointsA.length > 1 ? closestIndexA / (pointsA.length - 1) : 0
                const tB = pointsB.length > 1 ? closestIndexB / (pointsB.length - 1) : 0
                junctions.push({ curveA, tA, curveB, tB, occupant: null })
            }
        }
    }

    return junctions
}

// Minimum real-world-ish distance (same local-space units as everything
// else here) from a junction point at which a vehicle counts as
// "approaching/in" it - converted to each curve's own t-space per
// junction below, since the same physical distance maps to a different
// t-range depending on that curve's own length. Must stay bigger than
// VEHICLE_JUNCTION_STOP_OFFSET below (with margin) - otherwise a vehicle
// would never actually be detected as "nearby" while still short of its
// own stop line, and the whole yield check below would never fire.
const VEHICLE_YIELD_ZONE = 0.5 // kept comfortably bigger than VEHICLE_JUNCTION_STOP_OFFSET below (0.3), with margin
// Near-stop, not a hard 0 - keeps the same speed = base * speedBoost math
// well-behaved (and reads slightly more alive than a perfectly frozen car)
// while still clearly reading as "waiting".
const VEHICLE_YIELD_TARGET_SPEED = 0.05
// How far short of the actual junction point a yielding vehicle should
// stop - without this, "within the zone" was symmetric around the exact
// crossing point, so a car could end up slowing to a near-stop only once
// it was already ON TOP of (or past) that point, reading as stalled in
// the middle of the intersection rather than waiting cleanly before it.
const VEHICLE_JUNCTION_STOP_OFFSET = 0.3 // settled here, between the too-close 0.12 and the too-far 0.7

// Two different roads crossing each other in the model (found once at
// startup - see findRoadJunctions()) but with each car only ever aware of
// its own position along its OWN curve, nothing stopped two vehicles from
// two different roads driving straight through each other at a junction
// point exactly where those curves happen to cross. Resolved the same way
// real traffic without signals actually works at an unmarked crossing:
// whoever's already there gets to go, everyone else arriving waits their
// turn - decided randomly each time a junction goes from empty to
// contested, not by some fixed rule that would always resolve the same
// way.
const resolveIntersectionYielding = () =>
{
    for (const junction of vehicleJunctions)
    {
        const zoneRadiusA = junction.curveA.getLength() > 0 ? VEHICLE_YIELD_ZONE / junction.curveA.getLength() : 0
        const zoneRadiusB = junction.curveB.getLength() > 0 ? VEHICLE_YIELD_ZONE / junction.curveB.getLength() : 0

        const nearby = vehicles.filter((vehicle) =>
            (vehicle.curve === junction.curveA && Math.abs(vehicle.t - junction.tA) < zoneRadiusA) ||
            (vehicle.curve === junction.curveB && Math.abs(vehicle.t - junction.tB) < zoneRadiusB)
        )

        // Release the junction once its current occupant has actually
        // driven clear of it (not before - it keeps the right of way for
        // as long as it's still physically in the zone).
        if (junction.occupant && !nearby.includes(junction.occupant))
            junction.occupant = null

        if (nearby.length === 0)
            continue

        // Nothing currently holds the junction - claim it. With only one
        // vehicle present this is a no-op in practice (nothing left to
        // yield to below); with two or more arriving at once, this is the
        // random pick that decides who goes first.
        if (!junction.occupant)
            junction.occupant = nearby[Math.floor(Math.random() * nearby.length)]

        const stopOffsetA = junction.curveA.getLength() > 0 ? VEHICLE_JUNCTION_STOP_OFFSET / junction.curveA.getLength() : 0
        const stopOffsetB = junction.curveB.getLength() > 0 ? VEHICLE_JUNCTION_STOP_OFFSET / junction.curveB.getLength() : 0

        for (const vehicle of nearby)
        {
            if (vehicle === junction.occupant)
                continue

            // "Forward" - t itself if heading toward t=1, negated if
            // heading toward t=0 (same trick used for lane-following
            // elsewhere) - lets "the stop line's own position" be one
            // consistent number regardless of which way this vehicle
            // happens to be driving.
            const junctionT = vehicle.curve === junction.curveA ? junction.tA : junction.tB
            const stopOffsetT = vehicle.curve === junction.curveA ? stopOffsetA : stopOffsetB
            const stopLineForward = junctionT * vehicle.direction - stopOffsetT
            const vehicleForward = vehicle.t * vehicle.direction

            if (vehicleForward > stopLineForward)
            {
                // A hard position clamp, not just a speed reduction - the
                // previous version only slowed the vehicle down (toward,
                // never quite reaching, zero) without ever stopping it from
                // still physically advancing every single frame, so given
                // enough time it could still creep straight through the
                // stop line and into the crossing while the actual
                // occupant was still there. Clamping t directly guarantees
                // it can't, for as long as it isn't the occupant - the
                // speed target below is just what makes the approach and
                // hold at that line actually look like slowing down and
                // waiting, not a visual teleport back to the same spot.
                vehicle.t = THREE.MathUtils.clamp(stopLineForward * vehicle.direction, 0, 1)
                vehicle.speedBoostTarget = Math.min(vehicle.speedBoostTarget, VEHICLE_YIELD_TARGET_SPEED)
            }
        }
    }
}

const updateVehicles = (deltaTime) =>
{
    for (const vehicle of vehicles)
    {
        const curveLength = vehicle.curve.getLength()
        if (curveLength === 0)
            continue

        // Advance t by (speed / curveLength) * direction each frame - scaled
        // by deltaTime so real-world speed (meters/second) stays constant
        // regardless of frame rate or each path's own length. speedBoost
        // (normally 1, temporarily higher - see resolveVehicleOvertaking)
        // is what lets a leader accelerate away from a tailgating follower.
        vehicle.t += (vehicle.speed * vehicle.speedBoost / curveLength) * vehicle.direction * deltaTime

        // Bounce back-and-forth at both ends instead of looping/snapping -
        // "randomly to and fro" driving, as asked for, not a one-way loop.
        if (vehicle.t >= 1)
        {
            vehicle.t = 1
            vehicle.direction = -1
        }
        else if (vehicle.t <= 0)
        {
            vehicle.t = 0
            vehicle.direction = 1
        }
    }

    // Resolved AFTER every vehicle's own t/direction is finalized for this
    // frame (bounces included) but BEFORE any of it is actually applied to
    // positions/orientations below - so a vehicle clamped back here for
    // following too closely still gets a fully correct position/facing on
    // this same frame, not a one-frame-stale one. Intersection-yielding
    // runs second so it can override (via Math.min) a same-lane boost
    // target with a near-stop instead - waiting at a junction takes
    // priority over accelerating away from a tailgater.
    resolveVehicleOvertaking()
    resolveIntersectionYielding()

    // One shared easing step toward whatever target this frame settled on
    // for each vehicle - identical smoothing whether that target just rose
    // (ramping a same-lane boost up, or recovering from a junction stop)
    // or fell (easing into a junction yield, or recovering a boost back
    // down to normal).
    for (const vehicle of vehicles)
        vehicle.speedBoost += (vehicle.speedBoostTarget - vehicle.speedBoost) * Math.min(VEHICLE_BOOST_EASE_RATE * deltaTime, 1)

    for (const vehicle of vehicles)
    {
        // Lane offset uses the road's own tangent (not yet flipped by
        // travel direction) as the reference, so "which side" stays tied to
        // a fixed physical side of the road rather than flipping depending
        // on which way a given car happens to be driving - direction (+1/-1)
        // then puts opposite-direction traffic on opposite sides of it.
        // Cross product order (up, tangent rather than tangent, up) is
        // swapped from the first pass specifically to keep-left/RHD - each
        // direction now sits on the side that convention actually drives on.
        vehicle.curve.getTangentAt(vehicle.t, vehicleTangent)
        vehicleRight.crossVectors(vehicleUp, vehicleTangent).normalize()
        vehicle.object.position
            .copy(vehicle.curve.getPointAt(vehicle.t))
            .addScaledVector(vehicleRight, VEHICLE_LANE_OFFSET * vehicle.direction)

        // Face the direction of travel: the same tangent, reversed when
        // driving backward - built as a lookAt-style rotation, then
        // combined with the car's own baked-in corrective orientation
        // (baseQuaternion) so this doesn't fight the model's authored
        // "lying flat" transform.
        vehicleTangent.multiplyScalar(vehicle.direction).normalize()
        if (vehicleTangent.lengthSq() > 0)
        {
            vehicleLookMatrix.lookAt(new THREE.Vector3(), vehicleTangent, vehicleUp)
            vehicleFacingQuaternion.setFromRotationMatrix(vehicleLookMatrix)
            vehicle.object.quaternion
                .copy(vehicleFacingQuaternion)
                .multiply(vehicle.baseQuaternion)
                .premultiply(vehicleYawCorrection)
        }
    }
}

/**
 * People
 * Procedural low-poly figures (see createPersonMesh) for the students
 * (walking the road curves, set up alongside Vehicles above) and the
 * kids playing (set up right after that) - no rigged/animated character
 * model exists anywhere in this project, so walking/playing motion here
 * is all procedural too: a sine-wave leg/arm swing plus a small bob,
 * driven by actual distance moved each frame (advancePersonWalkCycle)
 * rather than elapsed time alone, so a faster-moving figure visibly
 * takes faster steps instead of just sliding along with its legs
 * swinging at a fixed, unrelated rate.
 */
const PERSON_SHIRT_COLORS = ['#e4572e', '#17bebb', '#ffc914', '#2e86ab', '#8b5fbf', '#f25f5c']
const PERSON_SKIN_COLORS = ['#f0c6a0', '#d8a278', '#a9704f', '#6b4530']
const PERSON_HAIR_COLORS = ['#2b2118', '#4a3222', '#1a1a1a', '#6b4226']
const randomFrom = (list) => list[Math.floor(Math.random() * list.length)]

const createPersonMesh = (height) =>
{
    const group = new THREE.Group()

    const skinMaterial = new THREE.MeshStandardMaterial({ color: randomFrom(PERSON_SKIN_COLORS), roughness: 0.8 })
    const shirtMaterial = new THREE.MeshStandardMaterial({ color: randomFrom(PERSON_SHIRT_COLORS), roughness: 0.8 })
    const pantsMaterial = new THREE.MeshStandardMaterial({ color: '#2f2f3a', roughness: 0.8 })
    const hairMaterial = new THREE.MeshStandardMaterial({ color: randomFrom(PERSON_HAIR_COLORS), roughness: 0.9 })

    // Proportions as fractions of the one height passed in, so a
    // shorter "kid" and a taller "student" (see where this is called)
    // both come out with the same basic build, just scaled.
    const legLength = height * 0.45
    const torsoLength = height * 0.32
    const headRadius = height * 0.11
    const armLength = height * 0.38
    const limbRadius = height * 0.045

    // Legs/arms are each their own pivot Group positioned at the actual
    // hip/shoulder height, with the limb's own mesh hung further down
    // inside it (negative local y) - rotating the PIVOT swings the limb
    // like a hinge; rotating the mesh itself would orbit it around the
    // wrong point entirely.
    const legGeometry = new THREE.CapsuleGeometry(limbRadius, Math.max(legLength - limbRadius * 2, 0.01), 4, 8)
    const makeLimb = (geometry, material, pivotY, side, sideOffset, length) =>
    {
        const pivot = new THREE.Group()
        pivot.position.set(side * sideOffset, pivotY, 0)
        const mesh = new THREE.Mesh(geometry, material)
        mesh.position.y = -length / 2
        mesh.castShadow = true
        pivot.add(mesh)
        group.add(pivot)
        return pivot
    }
    const leftLeg = makeLimb(legGeometry, pantsMaterial, legLength, -1, headRadius * 0.9, legLength)
    const rightLeg = makeLimb(legGeometry, pantsMaterial, legLength, 1, headRadius * 0.9, legLength)

    const torso = new THREE.Mesh(
        new THREE.CapsuleGeometry(height * 0.16, Math.max(torsoLength - height * 0.32, 0.01), 4, 8),
        shirtMaterial
    )
    torso.position.y = legLength + torsoLength / 2
    torso.castShadow = true
    group.add(torso)

    const armGeometry = new THREE.CapsuleGeometry(limbRadius * 0.9, Math.max(armLength - limbRadius * 1.8, 0.01), 4, 8)
    const shoulderY = legLength + torsoLength - height * 0.05
    const leftArm = makeLimb(armGeometry, skinMaterial, shoulderY, -1, height * 0.18, armLength)
    const rightArm = makeLimb(armGeometry, skinMaterial, shoulderY, 1, height * 0.18, armLength)

    const headY = legLength + torsoLength + headRadius
    const head = new THREE.Mesh(new THREE.SphereGeometry(headRadius, 10, 8), skinMaterial)
    head.position.y = headY
    head.castShadow = true
    group.add(head)
    // A simple "hair cap" - the same sphere, slightly larger and only
    // the top portion (thetaLength), rather than a separate hairstyle
    // mesh - reads fine at this figure's size/viewing distance.
    const hairCap = new THREE.Mesh(
        new THREE.SphereGeometry(headRadius * 1.02, 10, 8, 0, Math.PI * 2, 0, Math.PI * 0.55),
        hairMaterial
    )
    hairCap.position.y = headY
    group.add(hairCap)

    group.userData.legs = [leftLeg, rightLeg]
    group.userData.arms = [leftArm, rightArm]
    group.userData.height = height
    group.userData.walkPhase = Math.random() * Math.PI * 2 // desynced so a whole group doesn't step in lockstep
    group.userData.bobOffset = 0
    return group
}

// Real animated replacement for createPersonMesh above, built once
// Person.glb has loaded (see personGltfPromise/personTemplate) - falls
// back to the procedural figure above (via createPersonOrFallbackMesh,
// further down) if the model failed to load, same defensive pattern as
// the floor/KPA promises elsewhere in this file.
const createAnimatedPersonMesh = (height) =>
{
    const group = new THREE.Group()

    const clone = cloneSkeleton(personTemplate)
    // The template's single material is shared by every clone by default
    // (cloneSkeleton retargets bones/skeletons, not materials) - cloned
    // per instance here so each figure's random tint doesn't repaint
    // every other one too.
    clone.traverse((child) =>
    {
        if (child.isMesh)
        {
            child.material = child.material.clone()
            child.material.color.set(randomFrom(PERSON_SHIRT_COLORS))
            child.castShadow = true
            child.receiveShadow = true
        }
    })

    // personNaturalHeight is the template's own rest-pose height in its
    // raw, unscaled units (see where it's measured, right after the
    // template loads) - scaling the clone (not the outer group) up/down
    // to the requested height keeps the group itself at a plain identity
    // scale, matching what the rest of this file already expects from
    // createPersonMesh above (nothing out there ever touches .scale on
    // a student/kid, only .position/.quaternion).
    clone.scale.setScalar(height / personNaturalHeight)
    group.add(clone)

    const mixer = new THREE.AnimationMixer(clone)
    const action = mixer.clipAction(personAnimationClip)
    action.play()

    group.userData.mixer = mixer
    group.userData.height = height
    group.userData.walkPhase = Math.random() * Math.PI * 2
    group.userData.bobOffset = 0
    // Desynced the same way walkPhase is above (each figure starts at a
    // random point in its own gait) - otherwise every clone of the same
    // clip would step in perfect lockstep.
    mixer.setTime(Math.random() * personAnimationClip.duration)
    return group
}

// Only the loading path needs to know whether Person.glb actually made
// it in - everywhere else (the student/kid creation loops, and
// advancePersonWalkCycle below) just calls this and gets back whichever
// kind of figure is available.
const createPersonOrFallbackMesh = (height) => personTemplate ? createAnimatedPersonMesh(height) : createPersonMesh(height)

const WALK_STEP_RATE = 3.2 // radians of walk-phase advanced per meter actually travelled - the "stride frequency"
// Seconds of the walk-cycle clip advanced per meter actually travelled -
// the GLB-animation equivalent of WALK_STEP_RATE above. The clip is a
// 1.25s in-place loop; a relaxed walking pace covers roughly 1.3m per
// full left-right stride, so ~1.25/1.3 seconds of clip per meter reads
// about right without needing to measure actual foot-sliding.
const PERSON_WALK_SECONDS_PER_METER = 0.96
const WALK_SWING_ANGLE = 0.55 // max leg/arm swing either way, radians

const advancePersonWalkCycle = (person, distanceDelta) =>
{
    // bobOffset is still driven by this same walkPhase either way - it's
    // the external up/down bounce updateStudents/updateKidsPlaying add
    // on top of the curve/wander position below, independent of whichever
    // figure (procedural or GLB) is actually doing the stepping.
    person.userData.walkPhase += distanceDelta * WALK_STEP_RATE
    person.userData.bobOffset = Math.abs(Math.sin(person.userData.walkPhase)) * person.userData.height * 0.03

    if (person.userData.mixer)
    {
        person.userData.mixer.update(distanceDelta * PERSON_WALK_SECONDS_PER_METER)
        return
    }

    const swing = Math.sin(person.userData.walkPhase) * WALK_SWING_ANGLE
    const [leftLeg, rightLeg] = person.userData.legs
    const [leftArm, rightArm] = person.userData.arms
    leftLeg.rotation.x = swing
    rightLeg.rotation.x = -swing
    // Arms swing opposite their same-side leg, same as a real walk.
    leftArm.rotation.x = -swing
    rightArm.rotation.x = swing
}

const studentTangent = new THREE.Vector3()
const studentRight = new THREE.Vector3()
const studentUp = new THREE.Vector3(0, 1, 0)
const studentLookMatrix = new THREE.Matrix4()
const PEDESTRIAN_LANE_OFFSET = 0.14 // further from the road's centerline than VEHICLE_LANE_OFFSET (0.06) - the shoulder, not the lane itself

const updateStudents = (deltaTime) =>
{
    for (const student of students)
    {
        const curveLength = student.curve.getLength()
        if (curveLength === 0)
            continue

        const previousT = student.t
        student.t += (student.speed / curveLength) * student.direction * deltaTime
        if (student.t >= 1)
        {
            student.t = 1
            student.direction = -1
        }
        else if (student.t <= 0)
        {
            student.t = 0
            student.direction = 1
        }

        advancePersonWalkCycle(student.object, Math.abs(student.t - previousT) * curveLength)

        student.curve.getTangentAt(student.t, studentTangent)
        studentRight.crossVectors(studentUp, studentTangent).normalize()
        student.object.position
            .copy(student.curve.getPointAt(student.t))
            .addScaledVector(studentRight, PEDESTRIAN_LANE_OFFSET * student.side)
        student.object.position.y += student.object.userData.bobOffset

        studentTangent.multiplyScalar(student.direction).normalize()
        if (studentTangent.lengthSq() > 0)
        {
            studentLookMatrix.lookAt(new THREE.Vector3(), studentTangent, studentUp)
            student.object.quaternion.setFromRotationMatrix(studentLookMatrix)
        }
    }
}

const kidUp = new THREE.Vector3(0, 1, 0)
const kidLookMatrix = new THREE.Matrix4()
const kidMoveDirection = new THREE.Vector3()

const updateKidsPlaying = (deltaTime) =>
{
    for (const kid of kidsPlaying)
    {
        kid.userData.wanderOffset += deltaTime * kid.userData.wanderSpeed
        // Two sine waves at slightly different rates/axes rather than one
        // shared angle - a plain circle reads as too mechanical for
        // "playing"; this still loops but never quite retraces itself.
        const wanderX = Math.cos(kid.userData.wanderOffset) * kid.userData.wanderRadius
        const wanderZ = Math.sin(kid.userData.wanderOffset * 1.3) * kid.userData.wanderRadius
        const targetX = kid.userData.homeX + wanderX
        const targetZ = kid.userData.homeZ + wanderZ

        const dx = targetX - kid.position.x
        const dz = targetZ - kid.position.z
        const distance = Math.sqrt(dx * dx + dz * dz)

        if (distance > 0.001)
        {
            kidMoveDirection.set(dx, 0, dz).normalize()
            kidLookMatrix.lookAt(new THREE.Vector3(), kidMoveDirection, kidUp)
            kid.quaternion.setFromRotationMatrix(kidLookMatrix)
        }

        kid.position.x = targetX
        kid.position.z = targetZ

        advancePersonWalkCycle(kid, distance)
        // A touch more energetic than the walking students' own bob -
        // kids playing reads better a little bouncier.
        kid.position.y = kid.userData.bobOffset * 1.6
    }
}

/**
 * Animate
 */
const timer = new Timer()

const tick = () =>
{
    // Timer
    timer.update()
    const elapsedTime = timer.getElapsed()
    const deltaTime = timer.getDelta()

    // Drift each cloud cluster in a straight line across the sky (own
    // speed, frame-rate independent via deltaTime) plus a gentle vertical
    // bob - reads as clouds actually passing overhead, visible from any
    // orbit angle since it crosses the whole field rather than circling
    // around the building
    for (const cloud of cloudClusters)
    {
        cloud.group.position.x += cloud.driftSpeed * deltaTime
        // Wrap back to the upwind edge once it's crossed the far side, so
        // the sky stays populated instead of eventually drifting empty
        if (cloud.group.position.x > CLOUD_FIELD_HALF_SIZE)
            cloud.group.position.x -= CLOUD_FIELD_HALF_SIZE * 2

        const bobY = Math.sin(elapsedTime * cloud.bobSpeed + cloud.bobPhase) * cloud.bobAmplitude
        cloud.group.position.y = cloud.height + bobY
    }

    // Building markers pop up out of the ground together once the intro
    // finishes (markersRevealTriggered, set in finishIntroTravel()), each
    // line growing from the floor (localFloorY - fixed, at the plane) up
    // toward its resting height and its badge riding the tip - the two
    // stay welded together as one unit throughout, rather than the line
    // just appearing and the label fading in separately on its own timer.
    // A time-based progress (not the damped asymptotic lerp used
    // elsewhere in this file) is what allows the "little elastic"
    // overshoot-then-settle - an ever-approaching lerp never overshoots
    // its target by definition. Each marker's own revealDelay (rolled
    // once, randomly, at creation) staggers it from the others so the 12
    // pop up in no particular order, rather than a visible 1-2-3
    // sequence; revealDone skips a marker's math entirely once its own
    // animation has settled, rather than recomputing an unchanging
    // result forever.
    if (markersRevealTriggered)
    {
        for (const ref of markerFadeRefs)
        {
            if (ref.revealDone)
                continue

            const t = Math.min(Math.max(elapsedTime - markersRevealStartTime - ref.revealDelay, 0) / MARKER_REVEAL_DURATION, 1)
            const easedT = easeOutBack(t)
            // Line grows from the floor (localFloorY, fixed) up toward
            // topY - eased, so it overshoots past topY then settles back,
            // like the whole pin springing up out of the ground. Opacity
            // is tied to this exact same eased value (clamped, since
            // easedT briefly exceeds 1 during the overshoot and opacity
            // can't) rather than fading in on its own separate, faster
            // timer - that mismatch was what read as the badge "already
            // there, dropping in from above" instead of visibly climbing
            // up out of the ground with the line.
            const opacity = Math.min(Math.max(easedT, 0), 1)
            const currentTopY = THREE.MathUtils.lerp(ref.localFloorY, ref.topY, easedT)
            ref.line.geometry.setFromPoints([new THREE.Vector3(0, ref.localFloorY, 0), new THREE.Vector3(0, currentTopY, 0)])
            ref.line.material.opacity = opacity * 0.85

            // Badge rides the growing line's tip and scales up with it -
            // welded to the same eased value, not animated separately.
            ref.label.position.y = currentTopY
            ref.label.scale.set(ref.labelTargetScale.x * easedT, ref.labelTargetScale.y * easedT, 1)
            ref.label.material.opacity = opacity

            if (t >= 1)
                ref.revealDone = true
        }
    }

    // Marker hover/click - raycasts against every settled badge (still-
    // revealing ones are skipped: they're mid pop-in and their own
    // animation above already owns their scale for now) and eases a
    // slight scale-bump in/out on whichever one the pointer is over,
    // rather than snapping. hoveredMarkerIndex is what the click
    // listener (see its own addEventListener above) actually reads -
    // this is the only place that gets updated, once per frame.
    {
        // While the circle-nav dial is expanded, the pointer is busy
        // dragging the ring - markers stay visible and keep easing their
        // scale back to normal below, but nothing re-raycasts them, so a
        // marker that happened to be hovered right as the dial opened
        // settles back to its normal texture/scale instead of sticking.
        if (!markerInteractionSuspended)
        {
            markerRaycaster.setFromCamera(markerPointerNDC, camera)
            const revealedLabels = markerFadeRefs.filter((ref) => ref.revealDone).map((ref) => ref.label)
            const hit = markerRaycaster.intersectObjects(revealedLabels, false)[0]
            hoveredMarkerIndex = hit ? markerFadeRefs.findIndex((ref) => ref.label === hit.object) : -1
            canvas.style.cursor = hoveredMarkerIndex !== -1 ? 'pointer' : 'default'
        }

        // Swaps the badge's pre-baked texture (see markerLabels'
        // normalTexture/hoverTexture, MARKER_HOVER_BADGE_COLOR) rather
        // than redrawing the canvas every frame - only touches the two
        // markers actually involved in the change, not all 12, and only
        // on an actual change rather than every frame regardless.
        if (hoveredMarkerIndex !== previousHoveredMarkerIndex)
        {
            if (previousHoveredMarkerIndex !== -1 && markerLabels[previousHoveredMarkerIndex])
                markerLabels[previousHoveredMarkerIndex].sprite.material.map = markerLabels[previousHoveredMarkerIndex].normalTexture
            if (hoveredMarkerIndex !== -1 && markerLabels[hoveredMarkerIndex])
                markerLabels[hoveredMarkerIndex].sprite.material.map = markerLabels[hoveredMarkerIndex].hoverTexture
            previousHoveredMarkerIndex = hoveredMarkerIndex
        }

        const hoverLerpFactor = 1 - Math.exp(-8 * deltaTime)
        markerFadeRefs.forEach((ref, i) =>
        {
            if (!ref.revealDone)
                return
            const hoverScale = i === hoveredMarkerIndex ? 1.15 : 1
            ref.label.scale.set(
                THREE.MathUtils.lerp(ref.label.scale.x, ref.labelTargetScale.x * hoverScale, hoverLerpFactor),
                THREE.MathUtils.lerp(ref.label.scale.y, ref.labelTargetScale.y * hoverScale, hoverLerpFactor),
                1
            )
        })
    }

    // Intro camera animation - no-ops once it's finished (introState.active
    // is false), so this is cheap to call unconditionally every frame.
    updateIntroAnimation(deltaTime)

    // Marker zoom-in/out camera animation - no-ops once settled
    // (markerCameraState.active is false), same reasoning as above.
    updateMarkerCameraAnimation(deltaTime)

    // Cars driving back and forth along the road-path curves (no-ops
    // harmlessly if the house model hasn't loaded yet / had no usable
    // paths - vehicles stays empty in that case)
    updateVehicles(deltaTime)
    updateStudents(deltaTime)
    updateKidsPlaying(deltaTime)

    // Birds wandering the sky - independent of the house model, so this
    // is live from the very first frame rather than waiting on load
    updateBirds(deltaTime)

    // Ramp autoRotateSpeed back up from 0 after the intro hands off (see
    // finishIntroTravel()) instead of letting it snap straight to full
    // speed - smooths over what otherwise read as a jerk right as the
    // intro's own eased tween finished decelerating to a stop.
    if (autoRotateRamp.active)
    {
        autoRotateRamp.elapsed += deltaTime
        const rampT = Math.min(autoRotateRamp.elapsed / AUTO_ROTATE_RAMP_DURATION, 1)
        controls.autoRotateSpeed = autoRotateRamp.targetSpeed * rampT
        if (rampT >= 1)
            autoRotateRamp.active = false
    }

    // Update controls - skipped while something else owns the camera
    // directly (the "Theatre camera" toggle, or the intro animation above -
    // both flip the same theatreCameraState.enabled flag via
    // setOrbitControlsSuspended), since OrbitControls.update() would
    // otherwise reassert its own stored position/target over whatever just
    // set the camera, undoing it every frame. A marker GUI preview
    // (markerCameraPreviewIndex !== -1) deliberately does NOT flip that
    // flag - orbit stays live and updated during preview specifically so
    // dragging the mouse to explore a marker's shot is possible at all -
    // but the base-target clamp and min-height floor just below are
    // still skipped while previewing, since both assume the normal wide
    // establishing view (near CAMERA_BASE_TARGET, above CAMERA_MIN_Y),
    // not an arbitrary close-up a marker's own values were tuned to.
    if (!theatreCameraState.enabled)
    {
        controls.update()

        if (markerCameraPreviewIndex === -1)
        {
            // Keep the orbit target from drifting far from its pinned point
            // while panning - a little give (CAMERA_TARGET_FREEDOM) rather than
            // a hard lock. Shifts the camera by the same correction as the
            // target so the view's distance/angle isn't disturbed by the
            // clamp itself - only the drift beyond the allowed range is undone.
            const clampedTargetX = THREE.MathUtils.clamp(controls.target.x, CAMERA_BASE_TARGET.x - CAMERA_TARGET_FREEDOM, CAMERA_BASE_TARGET.x + CAMERA_TARGET_FREEDOM)
            const clampedTargetY = THREE.MathUtils.clamp(controls.target.y, CAMERA_BASE_TARGET.y - CAMERA_TARGET_FREEDOM, CAMERA_BASE_TARGET.y + CAMERA_TARGET_FREEDOM)
            const clampedTargetZ = THREE.MathUtils.clamp(controls.target.z, CAMERA_BASE_TARGET.z - CAMERA_TARGET_FREEDOM, CAMERA_BASE_TARGET.z + CAMERA_TARGET_FREEDOM)
            const targetDriftX = clampedTargetX - controls.target.x
            const targetDriftY = clampedTargetY - controls.target.y
            const targetDriftZ = clampedTargetZ - controls.target.z
            if (targetDriftX !== 0 || targetDriftY !== 0 || targetDriftZ !== 0)
            {
                controls.target.set(clampedTargetX, clampedTargetY, clampedTargetZ)
                camera.position.x += targetDriftX
                camera.position.y += targetDriftY
                camera.position.z += targetDriftZ
            }

            // Floor the camera's height while the user orbits, so dragging down
            // can't reach the low, underneath-looking angles that break the
            // scene's framing. Clamped directly on position (not via
            // minPolarAngle) since that's the one thing that stays constant
            // regardless of zoom/target - a polar-angle limit alone would let
            // the camera dip lower than this once zoomed in closer.
            if (camera.position.y < CAMERA_MIN_Y)
                camera.position.y = CAMERA_MIN_Y
        }
        else
        {
            // Pulls whatever the camera/target just ended up at (via the
            // controls.update() above, if the mouse was used to orbit)
            // back into this marker's own override values and refreshes
            // its GUI sliders to match - see syncMarkerCameraPreviewFromScene.
            syncMarkerCameraPreviewFromScene()
        }
    }
    else if (markerCameraState.zoomedIn && controls.autoRotate)
    {
        // The turntable shouldn't go still just because a marker is
        // zoomed in/locked (see flyCameraToMarker - it suspends
        // OrbitControls for the whole time a marker is zoomedIn, fly-in,
        // held, AND fly-out, which is exactly when the block above is
        // skipped and controls.update() never runs). Reproduces
        // OrbitControls' own no-deltaTime autoRotate formula
        // (_twoPI / 60 / 60 * autoRotateSpeed per call - see
        // _getAutoRotationAngle in OrbitControls.js) by hand, rotating
        // the camera around whatever controls.target currently is
        // (kept live by updateMarkerCameraAnimation even mid-flight) -
        // so the ambient spin keeps going through the fly-in, the held
        // close-up, and the fly-out alike, not just the normal wide view.
        const autoRotateAngle = (2 * Math.PI) / 60 / 60 * controls.autoRotateSpeed
        const offset = camera.position.clone().sub(controls.target)
        offset.applyAxisAngle(MARKER_ZOOM_TURNTABLE_UP, -autoRotateAngle)
        camera.position.copy(controls.target).add(offset)
        camera.lookAt(controls.target)
    }

    // Render
    renderer.render(scene, camera)

    // Call tick again on the next frame
    window.requestAnimationFrame(tick)
}

tick()
